import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzColorPickerModule } from 'ng-zorro-antd/color-picker';
import { NzFormDirective } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, firstValueFrom, forkJoin, lastValueFrom, of } from 'rxjs';
import { catchError, debounceTime, finalize, takeUntil } from 'rxjs/operators';
import QRCode from 'qrcode';
import { AppMode } from '../models/app-mode.model';
import { Company } from '../company/company.models';
import { DOMAIN } from '../constants/app.constants';
import {
  DEFAULT_QR_POSTER_TEXTS,
  PosterTextField,
  PosterTextSet,
  QrPosterTexts,
  QR_POSTER_TEXT_LIMITS,
  normalizePosterTexts,
  selectPosterTextSet,
} from '../models/qr-poster-texts.model';
import {
  DEFAULT_QR_POSTER_PAGE,
  QrPosterBlock,
  QrPosterRenderContext,
  QrPosterTextFit,
  QrPosterTemplateState,
  QrPosterSystemTemplateOption,
  QrPosterSystemTemplateVariant,
  SavedQrPosterTemplate,
  QrPosterTemplate,
  normalizeQrPosterTextFit,
} from '../models/qr-poster-template.model';
import {
  QrPosterIconAssets,
  QrPosterRenderDescriptor,
  QrPosterRenderDescriptorResponse,
} from '../models/qr-poster-render-descriptor.model';
import {
  QR_POSTER_ICON_DEFINITIONS,
  QrPosterColorizedIconAssetName,
  getQrPosterIconDefinition,
  getQrPosterPreviewIconType,
} from '../models/qr-poster-icons.model';
import { QR_POSTER_SYSTEM_TEMPLATES_FALLBACK, applyVariantColorsToBlocks, getSystemTemplateVariant } from '../models/qr-poster-system-templates';
import { AppModeService } from '../services/app-mode.service';
import { AuthService } from '../services/auth.service';
import { CompanyService } from '../services/company.service';
import { GermanPhoneNormalizerService } from '../services/german.phone.normalizer.service';
import { PropertyService } from '../services/property.service';
import { QrPosterDocumentService } from '../services/qr-poster-document.service';
import { QrPosterRenderAssetLoader } from '../services/qr-poster-render-assets.service';
import {
  QrPosterPdfPreviewRender,
  QrPosterPdfPreviewService,
} from '../services/qr-poster-pdf-preview.service';
import { QrPosterVariableGroup, QrPosterVariableResolverService } from '../services/qr-poster-variable-resolver.service';
import { SignOrderService } from '../services/sign-order.service';
import { SignOrderNewComponent } from '../sign-orders/sign-order-new.component';
import { SignOrderListComponent } from '../sign-orders/sign-order-list.component';
import { SignOrderDetailComponent } from '../sign-orders/sign-order-detail.component';
import { isForbiddenError } from '../utils/http-error.utils';

interface FieldConfig {
  labelKey: string;
  descriptionKey: string;
  type: 'text' | 'textarea';
  rows?: number;
  modes?: AppMode[];
}

interface PosterTemplateFormValue {
  de: {
    title: string;
    subtitle: string;
    intro: string;
    addressHeader: string;
    companyHeader: string;
    cta: string;
    welcomeHeadline: string;
    welcomeSubheading: string;
    wifiSnippet: string;
    houseRulesTeaser: string;
  };
  en: {
    title: string;
    subtitle: string;
    intro: string;
    addressHeader: string;
    companyHeader: string;
    cta: string;
    welcomeHeadline: string;
    welcomeSubheading: string;
    wifiSnippet: string;
    houseRulesTeaser: string;
  };
}

interface HistoryEntry {
  template: QrPosterTemplate;
  selectedBlockIds: string[];
}

interface SimplePosterDescriptorCacheEntry {
  key: string;
  response: QrPosterRenderDescriptorResponse;
}

interface SimplePosterDescriptorRequest {
  key: string;
  promise: Promise<QrPosterRenderDescriptorResponse>;
}

interface QrPosterDesignDiffSummary {
  blockCountBefore: number;
  blockCountAfter: number;
  addedBlockIds: string[];
  removedBlockIds: string[];
  changedBlocks: Array<{
    id: string;
    type: string;
    changedFields: string[];
  }>;
  changedTemplateFields: string[];
}

interface QrPosterHistoryAuditEvent {
  seq?: number;
  ts?: string;
  type: 'action' | 'history-attempt' | 'history-push' | 'history-skip' | 'interaction-begin' | 'interaction-end' | 'undo' | 'redo' | 'baseline-reset' | 'selection' | 'warning';
  actionId?: string;
  reason?: string;
  source?: string;
  callSite?: string;
  blockId?: string;
  blockIds?: string[];
  blockType?: string;
  beforeFingerprint?: string;
  afterFingerprint?: string;
  candidateFingerprint?: string;
  topUndoFingerprint?: string;
  lastHistoryFingerprint?: string;
  undoLength?: number;
  redoLength?: number;
  changed?: boolean;
  pushed?: boolean;
  skipped?: boolean;
  skipReason?: string;
  diff?: QrPosterDesignDiffSummary;
  extra?: Record<string, unknown>;
}

interface GuideLine {
  orientation: 'vertical' | 'horizontal';
  positionMm: number;
}

interface EditorTemplateOption {
  id: string;
  group: 'user' | 'system';
}

type SignOrderRouteNotice = 'permission-denied' | 'no-properties' | 'unavailable';

type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se';

interface EyeDropperResult {
  sRGBHex: string;
}

interface EyeDropperApi {
  open(): Promise<EyeDropperResult>;
}

interface EyeDropperWindow extends Window {
  EyeDropper?: new () => EyeDropperApi;
}

@Component({
  selector: 'app-qr-poster-template',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzTabsModule,
    NzFormDirective,
    NzInputDirective,
    NzInputNumberModule,
    NzButtonModule,
    NzCardModule,
    NzColorPickerModule,
    NzIconModule,
    NzModalModule,
    NzToolTipModule,
    NzSpinModule,
    NzAlertComponent,
    NzSwitchModule,
    NzSelectModule,
    DragDropModule,
    TranslateModule,
    SignOrderNewComponent,
    SignOrderListComponent,
    SignOrderDetailComponent,
  ],
  providers: [NzModalService],
  templateUrl: './qr-poster-template.component.html',
  styleUrls: ['./qr-poster-template.component.less'],
})
export class QrPosterTemplateComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly QR_POSTER_IMAGE_MAX_SIZE_MB = 5;
  private static readonly QR_POSTER_IMAGE_MAX_SIZE_BYTES = QrPosterTemplateComponent.QR_POSTER_IMAGE_MAX_SIZE_MB * 1024 * 1024;
  private static readonly QR_POSTER_IMAGE_ALLOWED_TYPES = new Set(['image/png', 'image/jpeg']);
  readonly iconOptions = QR_POSTER_ICON_DEFINITIONS.filter((icon) => icon.key !== 'caret-down');
  readonly languages = ['de', 'en'];
  readonly builderModes: Array<'simple' | 'advanced' | 'sign-order'> = ['simple', 'advanced'];
  readonly pageWidthMm = DEFAULT_QR_POSTER_PAGE.widthMm;
  readonly pageHeightMm = DEFAULT_QR_POSTER_PAGE.heightMm;
  readonly safeMarginMm = 9;
  readonly basePreviewScale = 3.15;
  readonly minPreviewScale = 2.2;
  readonly maxPreviewScale = 8;
  private readonly debugPreviewTextLayout = false;
  readonly zoomOptions: Array<'fit' | '75' | '100' | '125' | '150' | '175' | '200' | '250'> = ['fit', '75', '100', '125', '150', '175', '200', '250'];
  readonly fieldOrder: PosterTextField[] = [
    'title',
    'subtitle',
    'intro',
    'addressHeader',
    'companyHeader',
    'welcomeHeadline',
    'welcomeSubheading',
    'checkInInfo',
    'wifiSnippet',
    'houseRulesTeaser',
    'cta',
  ];
  readonly fieldConfig: Record<PosterTextField, FieldConfig> = {
    title: { labelKey: 'QR_POSTER_TEMPLATE.FIELDS.TITLE.LABEL', descriptionKey: 'QR_POSTER_TEMPLATE.FIELDS.TITLE.HELP', type: 'text', modes: ['emergency'] },
    subtitle: { labelKey: 'QR_POSTER_TEMPLATE.FIELDS.SUBTITLE.LABEL', descriptionKey: 'QR_POSTER_TEMPLATE.FIELDS.SUBTITLE.HELP', type: 'text', modes: ['emergency'] },
    intro: { labelKey: 'QR_POSTER_TEMPLATE.FIELDS.INTRO.LABEL', descriptionKey: 'QR_POSTER_TEMPLATE.FIELDS.INTRO.HELP', type: 'textarea', rows: 4, modes: ['emergency'] },
    addressHeader: { labelKey: 'QR_POSTER_TEMPLATE.FIELDS.ADDRESS_HEADER.LABEL', descriptionKey: 'QR_POSTER_TEMPLATE.FIELDS.ADDRESS_HEADER.HELP', type: 'text', modes: ['emergency'] },
    companyHeader: { labelKey: 'QR_POSTER_TEMPLATE.FIELDS.COMPANY_HEADER.LABEL', descriptionKey: 'QR_POSTER_TEMPLATE.FIELDS.COMPANY_HEADER.HELP', type: 'text', modes: ['emergency'] },
    cta: { labelKey: 'QR_POSTER_TEMPLATE.FIELDS.CTA.LABEL', descriptionKey: 'QR_POSTER_TEMPLATE.FIELDS.CTA.HELP', type: 'text', modes: ['emergency', 'hospitality'] },
    welcomeHeadline: { labelKey: 'QR_POSTER_TEMPLATE.FIELDS.WELCOME_HEADLINE.LABEL', descriptionKey: 'QR_POSTER_TEMPLATE.FIELDS.WELCOME_HEADLINE.HELP', type: 'text', modes: ['hospitality'] },
    welcomeSubheading: { labelKey: 'QR_POSTER_TEMPLATE.FIELDS.WELCOME_SUBHEADING.LABEL', descriptionKey: 'QR_POSTER_TEMPLATE.FIELDS.WELCOME_SUBHEADING.HELP', type: 'textarea', rows: 3, modes: ['hospitality'] },
    checkInInfo: { labelKey: 'QR_POSTER_TEMPLATE.FIELDS.CHECK_IN_INFO.LABEL', descriptionKey: 'QR_POSTER_TEMPLATE.FIELDS.CHECK_IN_INFO.HELP', type: 'text', modes: ['hospitality'] },
    wifiSnippet: { labelKey: 'QR_POSTER_TEMPLATE.FIELDS.WIFI_SNIPPET.LABEL', descriptionKey: 'QR_POSTER_TEMPLATE.FIELDS.WIFI_SNIPPET.HELP', type: 'textarea', rows: 3, modes: ['hospitality'] },
    houseRulesTeaser: { labelKey: 'QR_POSTER_TEMPLATE.FIELDS.HOUSE_RULES_TEASER.LABEL', descriptionKey: 'QR_POSTER_TEMPLATE.FIELDS.HOUSE_RULES_TEASER.HELP', type: 'textarea', rows: 3, modes: ['hospitality'] },
  };
  readonly limits = QR_POSTER_TEXT_LIMITS;
  readonly blockTypeOptions: Array<{ key: QrPosterBlock['type']; labelKey: string; icon: string }> = [
    { key: 'text', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ADD_TEXT', icon: 'edit' },
    { key: 'image', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ADD_IMAGE', icon: 'picture' },
    { key: 'qrCode', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ADD_QR', icon: 'qrcode' },
    { key: 'icon', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ADD_ICON', icon: 'star' },
    { key: 'line', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ADD_LINE', icon: 'minus' },
    { key: 'rectangle', labelKey: 'QR_POSTER_TEMPLATE.BUILDER.ADD_RECTANGLE', icon: 'border' },
  ];

  form!: FormGroup;
  company: Company | null = null;
  posterTexts: QrPosterTexts = normalizePosterTexts(DEFAULT_QR_POSTER_TEXTS);
  appMode: AppMode;

  isLoading = true;
  isSaving = false;
  isGeneratingPdf = false;
  isSavingTemplate = false;
  isSimplePosterPreviewLoading = false;
  isSimplePosterPreviewReady = false;

  builderMode: 'simple' | 'advanced' | 'sign-order' = 'simple';
  systemTemplates: QrPosterSystemTemplateOption[] = [];
  private systemTemplateCatalogueUnavailable = false;
  private pendingSystemTemplateCatalogue: QrPosterSystemTemplateOption[] | null = null;
  private appliedSystemTemplateCatalogue: QrPosterSystemTemplateOption[] | null = null;
  private isCompanyStateReadyForSystemTemplates = false;
  userTemplates: SavedQrPosterTemplate[] = [];
  activeTemplateId: string | null = null;
  savedActiveTemplateFromBackend: QrPosterTemplate | null = null;
  selectedSystemTemplateId = 'system:scanafy-classic';
  selectedVariantId: string | null = null;
  workingTemplate: QrPosterTemplate | null = null;
  savedTemplate: QrPosterTemplate | null = null;
  loadedTemplateBaseline: QrPosterTemplate | null = null;
  selectedBlockId: string | null = null;
  selectedBlockIds: string[] = [];
  zoomMode: 'fit' | '75' | '100' | '125' | '150' | '175' | '200' | '250' = 'fit';
  stageWidthPx = 760;
  stageHeightPx = 900;
  workspaceHeightPx = 760;
  variableGroupsCache: QrPosterVariableGroup[] = [];
  variableTooltipTitles: Record<string, string> = {};
  hasUnsavedAdvancedChangesFlag = false;
  activeTemplateStatusLabelText = '';
  advancedStatusLabelText = '';
  guideLines: GuideLine[] = [];
  marqueeSelectionRect: { leftPx: number; topPx: number; widthPx: number; heightPx: number } | null = null;
  qrOverlapWarningBlockIds: string[] = [];

  previewPropertyAddress = '';
  previewPropertyId = '';
  previewCompanyName = '';
  previewCompanyPhone = '';
  previewCompanyWebsite = '';
  previewCompanyAddress = '';
  previewCompanyDisplayEmail = '';
  previewLogoUrl = 'assets/scanafy_company_logo_croped.png';
  previewQrValue = DOMAIN;
  previewQrDataUrl = '';
  hasProperties = false;
  themeColor = '#2b2b2d';
  layerModalVisible = false;
  selectedImageFileName = '';
  showSignOrderCta = false;
  signOrderUnavailable = false;
  catalogReason: string | null = null;
  canShowSignOrderTab = false;
  signOrderRouteNotice: SignOrderRouteNotice | null = null;
  signOrderCtaAttentionActive = false;
  signOrderSubMode: 'new' | 'history' | 'detail' = 'new';
  selectedOrderPublicId: string | null = null;
  selectedDraftPublicId: string | null = null;
  freshSignOrderSession = 0;
  private signOrderPublicIdFromQuery: string | null = null;
  private signOrderDraftPublicIdFromQuery: string | null = null;
  private signOrderModeNewFromQuery = false;
  private signOrderAvailabilityResolved = false;
  private signOrderAvailabilityFailed = false;
  private signOrderAvailabilityRequestStarted = false;
  private signOrderPropertiesResolved = false;
  private signOrderRouteRejectionHandled = false;
  private signOrderCtaAttentionStarted = false;
  private criticalPageLoadFailed = false;
  private routeRequestedFreshSignOrderActive = false;

  activeTabIndex = 0;
  editingBlockId: string | null = null;
  inlineEditDraft = '';
  pendingTemplateNameDraft = '';
  pendingTemplateSaveMode: 'save' | 'save-and-activate' = 'save';
  templateNameValidationError = '';
  pendingRenameTemplate: SavedQrPosterTemplate | null = null;
  pendingRenameNameDraft = '';
  renameNameValidationError = '';
  private iconPickerModalRef: NzModalRef | null = null;
  private renameModalRef: NzModalRef | null = null;
  private lastSavedFormValue: PosterTemplateFormValue | null = null;
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  private readonly historyLimit = 50;
  private readonly debugQrPosterHistory = false;
  private readonly debugQrPosterHistoryAudit = false;
  private lastHistoryFingerprint = '';
  private isRestoringHistory = false;
  private isInitializingBuilderHistory = false;
  private qrPosterHistoryAuditTrace: QrPosterHistoryAuditEvent[] = [];
  private readonly qrPosterHistoryAuditMaxEvents = 1000;
  private qrPosterHistoryAuditSeq = 0;
  private currentHistoryActionId: string | undefined;
  private destroy$ = new Subject<void>();
  private readonly simplePosterPreviewRefresh$ = new Subject<number>();
  private simplePosterPreviewRender: QrPosterPdfPreviewRender | null = null;
  private simplePosterPreviewRenderRequest = 0;
  private simplePosterDescriptorCache: SimplePosterDescriptorCacheEntry | null = null;
  private simplePosterDescriptorRequest: SimplePosterDescriptorRequest | null = null;
  private simplePosterAssetContextKey = '';
  private simplePosterLogoPromise: Promise<Blob | null> | null = null;
  private readonly simplePosterIconPromises = new Map<string, Promise<QrPosterIconAssets>>();
  private readonly simplePosterTemplateAssetPromises = new Map<string, Promise<Blob | null>>();
  private dragState: {
    pointerId: number;
    blockIds: string[];
    startClientX: number;
    startClientY: number;
    hasMoved: boolean;
    checkpointPushed: boolean;
    startPositions: Record<string, { x: number; y: number }>;
    startBounds: { x: number; y: number; width: number; height: number };
    startRotatedBounds: { left: number; top: number; right: number; bottom: number; width: number; height: number };
  } | null = null;
  private resizeState: {
    pointerId: number;
    blockId: string;
    handle: ResizeHandle;
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    startRotation: number;
    checkpointPushed: boolean;
  } | null = null;
  private rotateState: {
    pointerId: number;
    blockId: string;
    centerClientX: number;
    centerClientY: number;
    startPointerAngle: number;
    startRotation: number;
    checkpointPushed: boolean;
  } | null = null;
  private marqueeState: {
    pointerId: number;
    startPageX: number;
    startPageY: number;
  } | null = null;
  private suppressSelectionClick = false;
  private clipboardBlocks: QrPosterBlock[] = [];
  private interactionCheckpointTemplate: QrPosterTemplate | null = null;
  private interactionBaselineFingerprint = '';
  private interactionCheckpointReason: 'drag' | 'resize' | 'rotate' | null = null;
  private interactionCheckpointBlockIds: string[] = [];
  private lastPreMutationPush: { fingerprint: string; reason: string; time: number } | null = null;
  private activeContentEditSession: { blockId: string; field: string; reason: string; baselineFingerprint: string } | null = null;
  private activeStyleEditSession: { blockId: string; field: string; reason: string; source: string; baselineFingerprint: string } | null = null;
  private styleEditSessionClearTimer: ReturnType<typeof setTimeout> | null = null;
  private imageAssetPreviewCache = new Map<string, string>();
  private readonly boundWindowKeydownHandler = (event: KeyboardEvent) => this.onWindowKeydown(event);

  @ViewChild('canvasStage') private canvasStageRef?: ElementRef<HTMLDivElement>;
  @ViewChild('posterPage') private posterPageRef?: ElementRef<HTMLDivElement>;
  @ViewChild('simplePosterPreviewCanvas') private simplePosterPreviewCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('simplePosterPreviewRenderCanvas') private simplePosterPreviewRenderCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('iconPickerContent', { static: true }) private iconPickerContentRef?: TemplateRef<void>;
  @ViewChild('templateNameModalContent', { static: true }) private templateNameModalContentRef?: TemplateRef<void>;
  @ViewChild('renameTemplateModalContent', { static: true }) private renameTemplateModalContentRef?: TemplateRef<void>;
  @ViewChild('activeTemplateDeleteContent', { static: true }) private activeTemplateDeleteContentRef?: TemplateRef<void>;

  constructor(
    private readonly fb: FormBuilder,
    private readonly companyService: CompanyService,
    private readonly propertyService: PropertyService,
    private readonly message: NzMessageService,
    private readonly translate: TranslateService,
    private readonly dePhone: GermanPhoneNormalizerService,
    private readonly appModeService: AppModeService,
    private readonly qrPosterDocumentService: QrPosterDocumentService,
    private readonly qrPosterPdfPreviewService: QrPosterPdfPreviewService,
    private readonly variableResolver: QrPosterVariableResolverService,
    private readonly modal: NzModalService,
    private readonly authService: AuthService,
    private readonly signOrderService: SignOrderService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.appMode = this.appModeService.getAppMode();
    this.variableGroupsCache = this.variableResolver.getVariableGroups(this.appMode, this.previewContext);
  }

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const wasRouteRequestedFreshSignOrder = this.signOrderModeNewFromQuery;
        this.signOrderPublicIdFromQuery = params.get('signOrderPublicId')?.trim() || null;
        this.signOrderDraftPublicIdFromQuery = params.get('signOrderDraftPublicId')?.trim() || null;
        this.signOrderModeNewFromQuery = params.get('signOrderMode') === 'new';
        if (this.signOrderModeNewFromQuery && !wasRouteRequestedFreshSignOrder) {
          this.signOrderRouteNotice = null;
          this.signOrderRouteRejectionHandled = false;
        }
        this.applySignOrderRouteState(wasRouteRequestedFreshSignOrder);
      });
    this.simplePosterPreviewRefresh$
      .pipe(debounceTime(250), takeUntil(this.destroy$))
      .subscribe((requestId) => {
        void this.renderSimplePosterPreview(requestId);
      });
    this.initialize();
  }

  ngAfterViewInit(): void {
    this.updateWorkspaceHeight();
    this.updateStageDimensions();
    window.addEventListener('keydown', this.boundWindowKeydownHandler, { passive: false });
    (window as any).__qrPosterHistoryTrace = () => this.qrPosterHistoryAuditTrace;
    (window as any).__copyQrPosterHistoryTrace = () => {
      const text = JSON.stringify(this.qrPosterHistoryAuditTrace, null, 2);
      navigator.clipboard?.writeText(text).catch(() => {
        console.log(text);
      });
      return text;
    };
    (window as any).__clearQrPosterHistoryTrace = () => {
      this.qrPosterHistoryAuditTrace.length = 0;
    };
  }

  ngOnDestroy(): void {
    this.iconPickerModalRef?.destroy();
    window.removeEventListener('keydown', this.boundWindowKeydownHandler);
    this.destroy$.next();
    this.destroy$.complete();
    this.finishContentEditSession('destroy');
    this.finishStyleEditSession('destroy');
    if (this.styleEditSessionClearTimer) {
      clearTimeout(this.styleEditSessionClearTimer);
    }
    this.simplePosterPreviewRenderRequest += 1;
    this.clearSimplePosterPreview();
    this.clearSimplePosterPreviewSession();
  }

  get activeLanguage(): string {
    return this.languages[this.activeTabIndex] || 'de';
  }

  get visibleFields(): PosterTextField[] {
    return this.fieldOrder.filter((field) => this.isFieldVisible(field));
  }

  get selectedBlock(): QrPosterBlock | null {
    if (!this.workingTemplate || !this.selectedBlockIds.length) {
      return null;
    }
    const block = this.workingTemplate.blocks.find((block) => block.id === (this.selectedBlockId || this.selectedBlockIds[0])) || null;
    if (block?.type === 'footer' && !this.showFooterInPreview) {
      return null;
    }
    return block;
  }

  get selectedBlocks(): QrPosterBlock[] {
    if (!this.workingTemplate || !this.selectedBlockIds.length) {
      return [];
    }
    return this.workingTemplate.blocks.filter((block) => this.selectedBlockIds.includes(block.id) && (block.type !== 'footer' || this.showFooterInPreview));
  }

  get isMultiSelection(): boolean {
    return this.selectedBlockIds.length > 1;
  }

  get multiSelectionLabel(): string {
    return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.MULTI_SELECTED', { count: this.selectedBlockIds.length });
  }

  get variableGroups() {
    return this.variableGroupsCache;
  }

  get editorTemplateOptions(): EditorTemplateOption[] {
    return [
      ...this.userTemplates.map((template) => ({ id: `user:${template.id}`, group: 'user' as const })),
      ...this.systemTemplates.map((template) => ({ id: `system:${template.id}`, group: 'system' as const })),
    ];
  }

  get selectedTemplateName(): string {
    return this.getTemplateName(this.selectedSystemTemplateId);
  }

  get hasUnsavedAdvancedChanges(): boolean {
    return this.hasUnsavedAdvancedChangesFlag;
  }

  get hasFooterTextContrastViolation(): boolean {
    return (this.workingTemplate?.blocks || []).some((block) => this.isFooterTextContrastWarningVisible(block));
  }

  get activeTemplateStatusLabel(): string {
    return this.activeTemplateStatusLabelText;
  }

  get advancedStatusLabel(): string {
    return this.advancedStatusLabelText;
  }

  get isDraggingBlock(): boolean {
    return !!this.dragState;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  get hasQrOverlapWarning(): boolean {
    return this.qrOverlapWarningBlockIds.length > 0;
  }

  get isBusinessPlan(): boolean {
    return this.company?.plan === 'business';
  }

  get canBringSelectedBlockForward(): boolean {
    if (!this.selectedBlockIds.length || !this.workingTemplate) {
      return false;
    }
    if (this.selectedBlocks.some((block) => block.type === 'footer')) {
      return false;
    }
    const ordered = this.getOrderedBlocks();
    const indexes = this.selectedBlockIds
      .map((id) => ordered.findIndex((block) => block.id === id))
      .filter((index) => index >= 0);
    return indexes.length > 0 && Math.max(...indexes) < ordered.length - 1;
  }

  get canSendSelectedBlockBackward(): boolean {
    if (!this.selectedBlockIds.length || !this.workingTemplate) {
      return false;
    }
    if (this.selectedBlocks.some((block) => block.type === 'footer')) {
      return false;
    }
    const ordered = this.getOrderedBlocks();
    const indexes = this.selectedBlockIds
      .map((id) => ordered.findIndex((block) => block.id === id))
      .filter((index) => index >= 0);
    return indexes.length > 0 && Math.min(...indexes) > 0;
  }

  get selectedBlockLayerText(): string {
    if (!this.selectedBlock || !this.workingTemplate) {
      return '0 / 0';
    }
    const ordered = this.getOrderedBlocks();
    const currentIndex = ordered.findIndex((block) => block.id === this.selectedBlock?.id);
    return currentIndex === -1 ? '0 / 0' : `${ordered.length - currentIndex} / ${ordered.length}`;
  }

  get showBorderColorVisibilityHint(): boolean {
    const block = this.selectedBlock;
    if (!this.blockSupportsBorderControls(block)) {
      return false;
    }

    const hasBorderColor = !!block?.style?.borderColor;
    const borderWidth = Number(block?.style?.borderWidth || 0);
    return hasBorderColor && borderWidth <= 0;
  }

  get canDeleteSelectedBlock(): boolean {
    if (!this.selectedBlocks.length) {
      return false;
    }
    return this.selectedBlocks.some((block) => !block.locked && (this.isBusinessPlan || block.type !== 'footer'));
  }

  get canvasWidthPx(): number {
    return this.pageWidthMm * this.canvasScalePxPerMm;
  }

  get canvasHeightPx(): number {
    return this.pageHeightMm * this.canvasScalePxPerMm;
  }

  get canvasScalePxPerMm(): number {
    const fitScale = Math.min(
      this.stageWidthPx / this.pageWidthMm,
      this.stageHeightPx / this.pageHeightMm
    );
    const clampedFitScale = this.clampMm(fitScale, this.minPreviewScale, this.maxPreviewScale);

    if (this.zoomMode === 'fit') {
      return clampedFitScale;
    }

    const multiplier = this.zoomMode === '75'
      ? 0.75
      : this.zoomMode === '125'
        ? 1.25
        : this.zoomMode === '150'
          ? 1.5
          : this.zoomMode === '175'
            ? 1.75
            : this.zoomMode === '200'
              ? 2
              : this.zoomMode === '250'
                ? 2.5
                : 1;

    return this.clampMm(clampedFitScale * multiplier, this.minPreviewScale, this.maxPreviewScale);
  }

  get zoomDisplayLabel(): string {
    return this.zoomMode === 'fit' ? 'Fit' : `${this.zoomMode}%`;
  }

  get canZoomOut(): boolean {
    return this.zoomMode !== this.zoomOptions[1];
  }

  get canZoomIn(): boolean {
    return this.zoomMode !== this.zoomOptions[this.zoomOptions.length - 1];
  }

  get showFooterInPreview(): boolean {
    return !!this.workingTemplate && (this.workingTemplate.branding?.showScanafyFooter || !this.isBusinessPlan);
  }

  getQrPreviewSize(block: QrPosterBlock): number {
    return Math.max(32, Math.max(block.width, block.height) * this.canvasScalePxPerMm - 10);
  }

  getBlockIconType(block: QrPosterBlock): string | null {
    if (!block.content?.icon) {
      return null;
    }
    return getQrPosterPreviewIconType(block.content.icon);
  }

  getIconTextRowStyles(block: QrPosterBlock, scalePxPerMm: number): Record<string, string> {
    return {
      'justify-content': block.style?.textAlign === 'right'
        ? 'flex-end'
        : block.style?.textAlign === 'center'
          ? 'center'
          : 'flex-start',
      gap: `${this.getIconTextGapMm(block) * scalePxPerMm}px`,
    };
  }

  getIconMultilineTextLines(block: QrPosterBlock): string[] {
    const text = this.resolvePreviewText(block);
    const textFit = normalizeQrPosterTextFit(block.style?.textFit, 'wrap');
    if (textFit === 'singleLineShrink') {
      return [text];
    }
    const layout = this.getPreviewTextLayout(block, this.canvasScalePxPerMm);
    const scalePxPerMm = this.canvasScalePxPerMm;
    const iconSizeMm = Math.min(4.2, Math.max(block.height - 1, 1.5));
    const gapMm = Math.max(0.5, Math.min(4, Number(block.style?.gap || 3)));
    const horizontalPaddingPx = Math.min(0.8, Math.max(0.2, block.width * 0.025)) * scalePxPerMm;
    const availableWidthPx = Math.max((block.width * scalePxPerMm) - (horizontalPaddingPx * 2), 1);
    const safetyPaddingPx = 1 * scalePxPerMm;
    const textMaxWidthPx = Math.max((block.width * 0.35) * scalePxPerMm, availableWidthPx - ((iconSizeMm + gapMm) * scalePxPerMm) - safetyPaddingPx);
    return this.splitPreviewTextLines(text, textMaxWidthPx, layout.fontSizePx, block.style?.fontWeight || 'normal');
  }

  getIconMultilineTextStyles(block: QrPosterBlock): Record<string, string> {
    const layout = this.getPreviewTextLayout(block, this.canvasScalePxPerMm);
    return {
      display: 'block',
      width: '100%',
      textAlign: block.style?.textAlign || 'center',
      whiteSpace: layout.textFit === 'singleLineShrink' ? 'nowrap' : 'pre-wrap',
      overflowWrap: layout.textFit === 'singleLineShrink' ? 'normal' : 'anywhere',
      wordBreak: layout.textFit === 'singleLineShrink' ? 'normal' : 'break-word',
      lineHeight: `${layout.lineHeightPx}px`,
    };
  }

  getIconMultilineTextIconStyles(block: QrPosterBlock): Record<string, string> {
    const scalePxPerMm = this.canvasScalePxPerMm;
    const layout = this.getPreviewTextLayout(block, scalePxPerMm);
    const fontSizePx = layout.fontSizePx;
    const fontWeight = block.style?.fontWeight || 'normal';
    const gapMm = Math.max(0.5, Math.min(4, Number(block.style?.gap || 3)));
    const iconSizeMm = Math.min(4.2, Math.max(block.height - 1, 1.5));
    // Re-split lines at the same font size and textMaxWidth used by the layout
    const horizontalPaddingPx = Math.min(0.8, Math.max(0.2, block.width * 0.025)) * scalePxPerMm;
    const availableWidthPx = Math.max((block.width * scalePxPerMm) - (horizontalPaddingPx * 2), 1);
    const safetyPaddingPx = 1 * scalePxPerMm;
    const textMaxWidthPx = Math.max((block.width * 0.35) * scalePxPerMm, availableWidthPx - ((iconSizeMm + gapMm) * scalePxPerMm) - safetyPaddingPx);
    const lines = this.splitPreviewTextLines(this.resolvePreviewText(block), textMaxWidthPx, fontSizePx, fontWeight);
    const firstLineWidth = this.measurePreviewTextWidthPx(lines[0] || '', fontSizePx, fontWeight);
    const blockWidthPx = block.width * scalePxPerMm;
    const centerX = blockWidthPx / 2;
    const firstLineLeftX = centerX - firstLineWidth / 2;
    const gapPx = gapMm * scalePxPerMm;
    const iconSizePx = iconSizeMm * scalePxPerMm;
    let iconLeftPx = firstLineLeftX - gapPx - iconSizePx;
    // Clamp only to page safe margin (block-local: icon may extend slightly outside block)
    const blockLeftPx = block.x * scalePxPerMm;
    const pageSafeLeftPx = 5 * scalePxPerMm;
    const blockLocalSafeLeft = pageSafeLeftPx - blockLeftPx;
    if (iconLeftPx < blockLocalSafeLeft) {
      iconLeftPx = blockLocalSafeLeft;
    }
    return {
      position: 'absolute',
      left: `${iconLeftPx}px`,
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: `${iconSizePx * 0.75}px`,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    };
  }

  getIconOptionLabel(key: string | undefined): string {
    const option = this.iconOptions.find((entry) => entry.key === key);
    return option?.labelKey ? this.translate.instant(option.labelKey) : (key || '');
  }

  getPreviewIconSizePx(block: QrPosterBlock): number {
    return Math.max(14, Math.min(block.width, block.height) * this.canvasScalePxPerMm - 6);
  }

  get selectedBlockRotation(): number {
    return this.normalizeRotation(this.selectedBlock?.rotation || 0);
  }

  get selectedBlockDisplayName(): string {
    return this.selectedBlock ? this.getSelectedBlockDisplayName(this.selectedBlock) : '';
  }

  get selectedBlockSecondaryLabel(): string {
    return this.selectedBlock ? this.getSelectedBlockSecondaryLabel(this.selectedBlock) : '';
  }

  getImageObjectFit(block: QrPosterBlock): 'contain' | 'cover' | 'fill' {
    if (block.type === 'companyLogo') {
      return 'contain';
    }
    const fit = block.content?.fit || 'cover';
    return fit === 'stretch' ? 'fill' : fit;
  }

  getImageBlockSource(block: QrPosterBlock): string {
    return block.content?.imageUrl || '';
  }

  getImageUploadButtonLabel(block: QrPosterBlock | null): string {
    if (!block || block.type !== 'image') {
      return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.UPLOAD_IMAGE');
    }
    return (block.content?.imageUrl || block.content?.imageAssetKey)
      ? this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.REPLACE_IMAGE')
      : this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.UPLOAD_IMAGE');
  }

  getImageStatusLabel(block: QrPosterBlock | null): string {
    if (!block || block.type !== 'image') {
      return '';
    }
    return (block.content?.imageUrl || block.content?.imageAssetKey)
      ? this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.IMAGE_STATUS_UPLOADED')
      : this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.IMAGE_STATUS_EMPTY');
  }

  triggerImageFilePicker(input: HTMLInputElement): void {
    input.click();
  }

  get previewContext(): QrPosterRenderContext {
    return {
      companyName: this.previewCompanyName,
      companyPhone: this.previewCompanyPhone,
      companyWebsite: this.previewCompanyWebsite,
      companyAddress: this.previewCompanyAddress,
      companyDisplayEmail: this.previewCompanyDisplayEmail,
      companyLogoBase64: this.previewLogoUrl,
      propertyAddress: this.previewPropertyAddress,
      qrTargetUrl: this.previewQrValue,
      qrDataUrl: this.previewQrDataUrl,
      themeColor: this.themeColor,
      appMode: this.appMode,
      posterTexts: selectPosterTextSet(this.posterTexts, this.activeLanguage),
      icons: {
        environment: '',
        home: '',
        phone: '',
        global: '',
        warning: '',
        fire: '',
        security: '',
        tool: '',
        thunderbolt: '',
        'customer-service': '',
        alert: '',
        info: '',
        safety: '',
        'security-scan': '',
      },
    };
  }

  getVariablePreviewValue(variableToken: string): string {
    return this.variableTooltipTitles[variableToken]
      || this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.VARIABLE_NO_VALUE');
  }

  shouldRenderAdvancedTemplateBlock(block: QrPosterBlock): boolean {
    return this.variableResolver.shouldRenderTemplateBlock(
      block,
      this.previewContext,
      this.workingTemplate?.blocks || [],
    );
  }

  get isSaveDisabled(): boolean {
    return !this.form || this.form.invalid || this.isSaving || this.areFormValuesEqual(this.getCurrentFormValue(), this.lastSavedFormValue);
  }

  get isResetDisabled(): boolean {
    return !this.form || this.isLoading || this.isSaving || this.areFormValuesEqual(this.getCurrentFormValue(), this.defaultFormValue);
  }

  get isAdvancedSaveDisabled(): boolean {
    if (!this.workingTemplate || this.isSavingTemplate) {
      return true;
    }
    return !this.hasUnsavedAdvancedChanges;
  }

  isFieldVisible(field: PosterTextField): boolean {
    const config = this.fieldConfig[field];
    return !config?.modes || config.modes.includes(this.appMode);
  }

  getControl(lang: string, field: PosterTextField): FormControl {
    return this.form.get(`${lang}.${field}`) as FormControl;
  }

  getInputId(lang: string, field: PosterTextField): string {
    return `${lang}-${field}`;
  }

  getCharacterCount(lang: string, field: PosterTextField): number {
    return (this.getControl(lang, field)?.value || '').length;
  }

  exceedsSoftLimit(lang: string, field: PosterTextField): boolean {
    return this.getCharacterCount(lang, field) > this.limits[field].soft;
  }

  isMediaBlock(block: QrPosterBlock): boolean {
    return ['image', 'companyLogo', 'qrCode', 'icon'].includes(block.type);
  }

  onLanguageChange(index: number | { index: number } | Event): void {
    const nextIndex = typeof index === 'number' ? index : typeof (index as { index?: number })?.index === 'number' ? (index as { index: number }).index : this.activeTabIndex;
    this.activeTabIndex = Number.isFinite(nextIndex) ? nextIndex : this.activeTabIndex;
    this.refreshAdvancedUiCache();
    if (this.builderMode === 'simple') {
      this.invalidateSimplePosterDescriptor({ refreshLogo: true });
      this.queueSimplePosterPreview();
    }
  }

  onBuilderModeChange(mode: 'simple' | 'advanced' | 'sign-order'): void {
    if (mode !== 'sign-order' && this.signOrderModeNewFromQuery) {
      this.clearSignOrderModeQuery();
    }
    if (this.builderMode === mode) return;
    this.builderMode = mode;
    if (mode === 'sign-order') {
      this.signOrderSubMode = 'new';
    }
    if (mode === 'simple') {
      this.queueSimplePosterPreview();
    } else {
      this.simplePosterPreviewRenderRequest += 1;
      this.clearSimplePosterPreview();
      this.clearSimplePosterPreviewSession();
    }
    if (mode === 'advanced') {
      setTimeout(() => {
        this.updateWorkspaceHeight();
        this.updateStageDimensions();
      }, 0);
    }
  }

  startNewSignOrder(): void {
    this.freshSignOrderSession += 1;
    this.routeRequestedFreshSignOrderActive = false;
    this.signOrderRouteNotice = null;
    this.onBuilderModeChange('sign-order');
    this.signOrderSubMode = 'new';
    this.selectedOrderPublicId = null;
    this.selectedDraftPublicId = null;

    if (!this.signOrderPublicIdFromQuery && !this.signOrderDraftPublicIdFromQuery && !this.signOrderModeNewFromQuery) return;

    this.signOrderPublicIdFromQuery = null;
    this.signOrderDraftPublicIdFromQuery = null;
    this.signOrderModeNewFromQuery = false;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { signOrderPublicId: null, signOrderDraftPublicId: null, signOrderMode: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onSignOrderSubNavigation(destination: 'new' | 'history'): void {
    if (this.signOrderSubMode === 'detail') {
      this.leaveSignOrderDetail(destination);
      return;
    }
    this.signOrderSubMode = destination;
    if (destination === 'history' && this.signOrderModeNewFromQuery) {
      this.clearSignOrderModeQuery();
    }
    if (destination === 'history' && this.signOrderDraftPublicIdFromQuery) {
      this.selectedDraftPublicId = null;
      this.signOrderDraftPublicIdFromQuery = null;
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { signOrderDraftPublicId: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  onSignOrderDetailBack(): void {
    this.leaveSignOrderDetail('history');
  }

  openSignOrderDetail(publicId: string | null | undefined): void {
    if (typeof publicId !== 'string') return;
    const normalizedPublicId = publicId.trim();
    if (!normalizedPublicId || normalizedPublicId === this.signOrderPublicIdFromQuery) return;

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { signOrderPublicId: normalizedPublicId, signOrderDraftPublicId: null, signOrderMode: null },
      queryParamsHandling: 'merge',
    });
  }

  leaveSignOrderDetail(destination: 'new' | 'history'): void {
    this.signOrderSubMode = destination;
    this.selectedOrderPublicId = null;
    if (!this.signOrderPublicIdFromQuery) return;

    this.signOrderPublicIdFromQuery = null;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { signOrderPublicId: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private openSignOrderDetailFromQuery(): void {
    if (!this.signOrderPublicIdFromQuery) return;
    this.onBuilderModeChange('sign-order');
    this.signOrderSubMode = 'detail';
    this.selectedOrderPublicId = this.signOrderPublicIdFromQuery;
    this.selectedDraftPublicId = null;
  }

  openSignOrderDraft(publicId: string | null | undefined): void {
    if (typeof publicId !== 'string') return;
    const normalizedPublicId = publicId.trim();
    if (!normalizedPublicId || normalizedPublicId === this.signOrderDraftPublicIdFromQuery) return;

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { signOrderDraftPublicId: normalizedPublicId, signOrderPublicId: null, signOrderMode: null },
      queryParamsHandling: 'merge',
    });
  }

  onDraftSaved(publicId: string): void {
    if (!publicId) return;
    this.selectedDraftPublicId = publicId;
    this.signOrderDraftPublicIdFromQuery = publicId;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { signOrderDraftPublicId: publicId, signOrderPublicId: null, signOrderMode: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onDraftConsumed(): void {
    this.selectedDraftPublicId = null;
    this.signOrderDraftPublicIdFromQuery = null;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { signOrderDraftPublicId: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onDraftDeleted(): void {
    this.selectedDraftPublicId = null;
    this.signOrderDraftPublicIdFromQuery = null;
    this.signOrderSubMode = 'history';
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { signOrderDraftPublicId: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private openSignOrderDraftFromQuery(): void {
    if (!this.signOrderDraftPublicIdFromQuery) return;
    this.onBuilderModeChange('sign-order');
    this.signOrderSubMode = 'new';
    this.selectedOrderPublicId = null;
    this.selectedDraftPublicId = this.signOrderDraftPublicIdFromQuery;
  }

  private applySignOrderRouteState(wasRouteRequestedFreshSignOrder = false): void {
    if (this.isLoading) return;

    if (this.signOrderPublicIdFromQuery) {
      this.routeRequestedFreshSignOrderActive = false;
      this.signOrderRouteNotice = null;
      this.openSignOrderDetailFromQuery();
      return;
    }
    if (this.signOrderDraftPublicIdFromQuery) {
      this.routeRequestedFreshSignOrderActive = false;
      this.signOrderRouteNotice = null;
      this.openSignOrderDraftFromQuery();
      return;
    }
    if (this.signOrderModeNewFromQuery) {
      this.resolveFreshSignOrderRouteRequest();
      return;
    }
    if (wasRouteRequestedFreshSignOrder && this.routeRequestedFreshSignOrderActive) {
      this.routeRequestedFreshSignOrderActive = false;
      this.onBuilderModeChange(this.savedActiveTemplateFromBackend ? 'advanced' : 'simple');
      return;
    }
    if (this.builderMode === 'sign-order' && this.signOrderSubMode === 'detail') {
      this.signOrderSubMode = 'history';
      this.selectedOrderPublicId = null;
    } else if (this.builderMode === 'sign-order'
      && this.signOrderSubMode === 'new'
      && this.selectedDraftPublicId) {
      this.selectedDraftPublicId = null;
      this.signOrderDraftPublicIdFromQuery = null;
      this.signOrderSubMode = 'history';
    }
  }

  private resolveFreshSignOrderRouteRequest(): void {
    if (this.criticalPageLoadFailed) {
      this.rejectFreshSignOrderRouteRequest(null);
      return;
    }
    if (!this.canShowSignOrderTab) {
      this.rejectFreshSignOrderRouteRequest('permission-denied');
      return;
    }
    if (!this.signOrderPropertiesResolved || !this.signOrderAvailabilityResolved) return;
    if (!this.hasProperties) {
      this.rejectFreshSignOrderRouteRequest('no-properties');
      return;
    }
    if (this.signOrderUnavailable || this.signOrderAvailabilityFailed || !this.showSignOrderCta) {
      this.rejectFreshSignOrderRouteRequest('unavailable');
      return;
    }
    this.activateFreshSignOrderFromRoute();
  }

  private activateFreshSignOrderFromRoute(): void {
    if (!this.canShowSignOrderTab
      || !this.signOrderPropertiesResolved
      || !this.signOrderAvailabilityResolved
      || !this.showSignOrderCta
      || !this.hasProperties) return;
    if (this.routeRequestedFreshSignOrderActive
      && this.builderMode === 'sign-order'
      && this.signOrderSubMode === 'new'
      && !this.selectedOrderPublicId
      && !this.selectedDraftPublicId) {
      return;
    }

    this.routeRequestedFreshSignOrderActive = true;
    this.freshSignOrderSession += 1;
    this.onBuilderModeChange('sign-order');
    this.signOrderSubMode = 'new';
    this.selectedOrderPublicId = null;
    this.selectedDraftPublicId = null;
    this.signOrderRouteNotice = null;
  }

  private rejectFreshSignOrderRouteRequest(notice: SignOrderRouteNotice | null): void {
    if (!this.signOrderModeNewFromQuery || this.signOrderRouteRejectionHandled) return;
    this.signOrderRouteRejectionHandled = true;
    this.routeRequestedFreshSignOrderActive = false;
    this.signOrderRouteNotice = notice;
    this.signOrderModeNewFromQuery = false;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { signOrderMode: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  dismissSignOrderRouteNotice(): void {
    this.signOrderRouteNotice = null;
  }

  goToPropertiesForSignOrder(): void {
    this.signOrderRouteNotice = null;
    void this.router.navigate(['/properties']);
  }

  get shouldRenderSignOrderTab(): boolean {
    return this.canShowSignOrderTab
      && (!this.signOrderAvailabilityResolved || this.showSignOrderCta);
  }

  get isSignOrderCtaDisabled(): boolean {
    return !this.signOrderAvailabilityResolved
      || !this.showSignOrderCta
      || !this.signOrderPropertiesResolved
      || !this.hasProperties;
  }

  get signOrderCtaTooltipKey(): string | null {
    if (!this.signOrderPropertiesResolved) return null;
    if (!this.hasProperties) return 'QR_POSTER_TEMPLATE.SIGN_ORDER_CTA_DISABLED';
    if (!this.isSignOrderCtaDisabled) return 'QR_POSTER_TEMPLATE.ORDER_SIGNS_PROMO';
    return null;
  }

  get signOrderRouteNoticeKey(): string | null {
    switch (this.signOrderRouteNotice) {
      case 'permission-denied':
        return 'QR_POSTER_TEMPLATE.SIGN_ORDER_PERMISSION_DENIED';
      case 'no-properties':
        return 'QR_POSTER_TEMPLATE.SIGN_ORDER_CTA_DISABLED';
      case 'unavailable':
        return 'QR_POSTER_TEMPLATE.SIGN_ORDER_UNAVAILABLE';
      default:
        return null;
    }
  }

  private clearSignOrderModeQuery(): void {
    this.routeRequestedFreshSignOrderActive = false;
    this.signOrderModeNewFromQuery = false;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { signOrderMode: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  async generateTestPdf(): Promise<void> {
    if (this.isGeneratingPdf) {
      return;
    }
    if (!(await this.confirmQrOverlapIfNeeded())) {
      return;
    }

    this.isGeneratingPdf = true;
    try {
      if (this.builderMode === 'advanced' && this.workingTemplate) {
        const renderTemplate = this.cloneTemplate(this.getLocalTemplateForPlan(this.workingTemplate));
        renderTemplate.blocks = (renderTemplate.blocks || [])
          .map((block) => this.clampBlock({
            ...block,
            content: block.content || {},
            style: block.style || {},
          }))
          .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
        const isSystemTemplateSelection = this.selectedSystemTemplateId.startsWith('system:');
        const useCanonicalDefault = this.systemTemplateCatalogueUnavailable
          && isSystemTemplateSelection
          && this.selectedSystemTemplateId === 'system:scanafy-classic'
          && renderTemplate.selectedTemplateId === 'scanafy-classic'
          && !this.hasUnsavedAdvancedChangesFlag;
        if (this.systemTemplateCatalogueUnavailable
          && isSystemTemplateSelection
          && !useCanonicalDefault) {
          throw new Error('The system template catalogue is unavailable');
        }
        const language = this.activeLanguage === 'en' ? 'en' : 'de';
        const descriptorResponse = this.previewPropertyId
          ? await lastValueFrom(this.propertyService.getQrPosterRenderDescriptor(
            this.previewPropertyId,
            language,
            useCanonicalDefault,
          ))
          : this.createEditorPreviewDescriptorResponse(
            await lastValueFrom(this.propertyService.getQrPosterEditorPreviewDescriptor(language)),
            useCanonicalDefault ? undefined : renderTemplate,
          );
        const editorPreviewResponse: QrPosterRenderDescriptorResponse = {
          ...descriptorResponse,
          descriptor: {
            ...descriptorResponse.descriptor,
            branding: {
              ...descriptorResponse.descriptor.branding,
              themeColor: this.themeColor,
            },
          },
        };

        await this.qrPosterDocumentService.download(
          editorPreviewResponse,
          `qr-poster-preview_${this.formatQrPosterFilenameTimestamp(new Date())}.pdf`,
          {
            templateOverride: useCanonicalDefault ? undefined : renderTemplate,
            editorDraftFooterEnabled: useCanonicalDefault
              ? undefined
              : this.resolveEditorDraftFooterEnabled(renderTemplate),
            renderSource: 'advanced-builder-test-pdf',
          },
        );
      } else {
        const language = this.activeLanguage === 'en' ? 'en' : 'de';
        const descriptorResponse = await this.getSimpleSettingsDescriptorResponse(language);

        await this.qrPosterDocumentService.download(
          descriptorResponse,
          `qr-poster-preview_${this.formatQrPosterFilenameTimestamp(new Date())}.pdf`,
          {
            posterTextsOverride: selectPosterTextSet(this.posterTexts, language),
            renderSource: 'simple-settings-test-pdf',
          },
        );
      }

      this.message.success(this.translate.instant('QR_POSTER_TEMPLATE.TEST_PDF_SUCCESS'));
    } catch (error) {
      console.error('Failed to generate test PDF', error);
      this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.TEST_PDF_ERROR'));
    } finally {
      this.isGeneratingPdf = false;
    }
  }

  private createEditorPreviewDescriptorResponse(
    response: QrPosterRenderDescriptorResponse,
    renderTemplate?: QrPosterTemplate,
  ): QrPosterRenderDescriptorResponse {
    const template = renderTemplate || response.descriptor.template;

    return {
      ...response,
      descriptor: {
        ...response.descriptor,
        template,
        posterTexts: selectPosterTextSet(this.posterTexts, response.descriptor.renderLanguage),
        branding: {
          ...response.descriptor.branding,
          companyName: this.previewCompanyName,
          companyDisplayEmail: this.previewCompanyDisplayEmail,
          companyPhone: this.previewCompanyPhone,
          companyWebsite: this.previewCompanyWebsite,
          companyAddress: this.previewCompanyAddress,
          themeColor: this.themeColor,
          footerEnabled: renderTemplate
            ? this.resolveEditorDraftFooterEnabled(renderTemplate)
            : response.descriptor.branding.footerEnabled,
        },
        property: {
          ...response.descriptor.property,
          propertyAddress: this.previewPropertyAddress,
        },
        qrDestination: this.previewQrValue || response.descriptor.qrDestination,
        templateImageAssetKeys: this.getTemplateImageAssetKeys(template),
      },
    };
  }

  private async getSimpleSettingsDescriptorResponse(
    language: 'de' | 'en',
  ): Promise<QrPosterRenderDescriptorResponse> {
    const key = this.getSimplePosterDescriptorKey(language);
    if (this.simplePosterDescriptorCache?.key === key) {
      return this.simplePosterDescriptorCache.response;
    }

    if (this.simplePosterDescriptorRequest?.key === key) {
      return this.simplePosterDescriptorRequest.promise;
    }

    let requestPromise: Promise<QrPosterRenderDescriptorResponse>;
    const descriptorRequest = this.previewPropertyId
      ? lastValueFrom(this.propertyService.getQrPosterRenderDescriptor(
        this.previewPropertyId,
        language,
        true,
      ))
      : lastValueFrom(this.propertyService.getQrPosterEditorPreviewDescriptor(language))
        .then((response) => this.createEditorPreviewDescriptorResponse(response));

    requestPromise = descriptorRequest
      .then((response) => {
        if (this.simplePosterDescriptorRequest?.promise === requestPromise) {
          this.simplePosterDescriptorCache = { key, response };
          this.simplePosterDescriptorRequest = null;
        }
        return response;
      })
      .catch((error) => {
        if (this.simplePosterDescriptorRequest?.promise === requestPromise) {
          this.simplePosterDescriptorRequest = null;
        }
        throw error;
      });

    this.simplePosterDescriptorRequest = { key, promise: requestPromise };
    return requestPromise;
  }

  private queueSimplePosterPreview(immediate = false): void {
    if (!this.form) {
      return;
    }

    const requestId = ++this.simplePosterPreviewRenderRequest;
    if (this.isSimplePosterPreviewLoading) {
      this.simplePosterPreviewRender?.dispose();
      this.simplePosterPreviewRender = null;
    }
    if (!this.isSimplePosterPreviewReady) {
      this.isSimplePosterPreviewLoading = true;
    }
    if (immediate) {
      void this.renderSimplePosterPreview(requestId);
      return;
    }
    this.simplePosterPreviewRefresh$.next(requestId);
  }

  private async renderSimplePosterPreview(requestId: number): Promise<void> {
    if (!this.isActiveSimplePosterPreviewRequest(requestId) || !this.form) {
      return;
    }

    const hadVisiblePreview = this.isSimplePosterPreviewReady;
    let previewRender: QrPosterPdfPreviewRender | null = null;
    this.isSimplePosterPreviewLoading = true;

    try {
      const language = this.activeLanguage === 'en' ? 'en' : 'de';
      void this.qrPosterPdfPreviewService.prepareWorker().catch(() => undefined);
      const descriptorResponse = await this.getSimpleSettingsDescriptorResponse(language);
      if (!this.isActiveSimplePosterPreviewRequest(requestId)) {
        return;
      }

      const pdfBlob = await this.qrPosterDocumentService.createPdfBlob(descriptorResponse, {
        posterTextsOverride: selectPosterTextSet(this.posterTexts, language),
        renderSource: 'simple-settings-canvas-preview',
        assetLoader: this.getSimplePosterAssetLoader(descriptorResponse.descriptor),
      });
      if (!this.isActiveSimplePosterPreviewRequest(requestId)) {
        return;
      }

      this.cdr.detectChanges();
      if (!this.isActiveSimplePosterPreviewRequest(requestId)) {
        return;
      }
      const renderCanvas = this.simplePosterPreviewRenderCanvas?.nativeElement;
      if (!renderCanvas) {
        throw new Error('Unable to initialize the simple QR poster preview');
      }

      this.simplePosterPreviewRender?.dispose();
      this.simplePosterPreviewRender = null;
      previewRender = this.qrPosterPdfPreviewService.createFirstPageRender(pdfBlob, renderCanvas);
      this.simplePosterPreviewRender = previewRender;
      await previewRender.completed;

      if (!this.isActiveSimplePosterPreviewRequest(requestId)) {
        previewRender.dispose();
        return;
      }

      this.copySimplePosterPreviewCanvas(renderCanvas);
      if (!this.isActiveSimplePosterPreviewRequest(requestId)) {
        return;
      }
      this.isSimplePosterPreviewReady = true;
    } catch {
      if (!this.isActiveSimplePosterPreviewRequest(requestId)) {
        return;
      }

      if (this.simplePosterPreviewRender === previewRender) {
        previewRender?.dispose();
        this.simplePosterPreviewRender = null;
      }
      if (!hadVisiblePreview) {
        this.clearSimplePosterPreviewCanvas();
        this.isSimplePosterPreviewReady = false;
      }
      this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.TEST_PDF_ERROR'));
    } finally {
      if (this.isActiveSimplePosterPreviewRequest(requestId)) {
        this.isSimplePosterPreviewLoading = false;
      }
    }
  }

  private isActiveSimplePosterPreviewRequest(requestId: number): boolean {
    return requestId === this.simplePosterPreviewRenderRequest
      && this.builderMode === 'simple';
  }

  private copySimplePosterPreviewCanvas(source: HTMLCanvasElement): void {
    const target = this.simplePosterPreviewCanvas?.nativeElement;
    if (!target || source.width <= 0 || source.height <= 0) {
      throw new Error('Unable to display the simple QR poster preview');
    }

    const context = target.getContext('2d', { alpha: false });
    if (!context) {
      throw new Error('Unable to display the simple QR poster preview');
    }

    target.width = source.width;
    target.height = source.height;
    context.drawImage(source, 0, 0);
  }

  private getSimplePosterDescriptorKey(language: 'de' | 'en'): string {
    return [
      this.previewPropertyId || 'editor-preview',
      language,
      'default',
    ].join('|');
  }

  private getSimplePosterAssetLoader(descriptor: QrPosterRenderDescriptor): QrPosterRenderAssetLoader {
    this.ensureSimplePosterAssetContext(descriptor);

    return {
      loadLogo: (nextDescriptor) => this.loadSimplePosterLogo(nextDescriptor),
      loadTemplateImage: (nextDescriptor, assetKey) => this.loadSimplePosterTemplateAsset(nextDescriptor, assetKey),
      loadIcons: (nextDescriptor, requiredIconNames) => this.loadSimplePosterIcons(nextDescriptor, requiredIconNames),
    };
  }

  private ensureSimplePosterAssetContext(descriptor: QrPosterRenderDescriptor): void {
    const contextKey = [
      descriptor.property.propertyId,
      descriptor.templateSource,
      descriptor.template.selectedTemplateId,
      descriptor.branding.themeColor,
      [...descriptor.templateImageAssetKeys].sort().join(','),
    ].join('|');

    if (contextKey !== this.simplePosterAssetContextKey) {
      this.clearSimplePosterAssetCache();
      this.simplePosterAssetContextKey = contextKey;
    }
  }

  private loadSimplePosterLogo(descriptor: QrPosterRenderDescriptor): Promise<Blob | null> {
    this.ensureSimplePosterAssetContext(descriptor);
    if (this.simplePosterLogoPromise) {
      return this.simplePosterLogoPromise;
    }

    const request = descriptor.property.propertyId === 'editor-preview'
      ? firstValueFrom(this.companyService.getCompanyLogoBlob())
      : lastValueFrom(this.propertyService.getQrPosterLogoBlob(descriptor.property.propertyId));
    let promise: Promise<Blob | null>;
    promise = request.catch((error) => {
      if (this.simplePosterLogoPromise === promise) {
        this.simplePosterLogoPromise = null;
      }
      throw error;
    });
    this.simplePosterLogoPromise = promise;
    return promise;
  }

  private loadSimplePosterIcons(
    descriptor: QrPosterRenderDescriptor,
    requiredIconNames: readonly QrPosterColorizedIconAssetName[],
  ): Promise<QrPosterIconAssets> {
    this.ensureSimplePosterAssetContext(descriptor);
    const themeColor = descriptor.branding.themeColor;
    const iconNames = Array.from(new Set(requiredIconNames)).sort();
    const iconCacheKey = `${themeColor}|${iconNames.join(',')}`;
    const cached = this.simplePosterIconPromises.get(iconCacheKey);
    if (cached) {
      return cached;
    }

    let promise: Promise<QrPosterIconAssets>;
    promise = lastValueFrom(this.propertyService.getColoredIcons(themeColor, iconNames))
      .catch((error) => {
        if (this.simplePosterIconPromises.get(iconCacheKey) === promise) {
          this.simplePosterIconPromises.delete(iconCacheKey);
        }
        throw error;
      });
    this.simplePosterIconPromises.set(iconCacheKey, promise);
    return promise;
  }

  private loadSimplePosterTemplateAsset(
    descriptor: QrPosterRenderDescriptor,
    assetKey: string,
  ): Promise<Blob | null> {
    this.ensureSimplePosterAssetContext(descriptor);
    const cached = this.simplePosterTemplateAssetPromises.get(assetKey);
    if (cached) {
      return cached;
    }

    let promise: Promise<Blob | null>;
    promise = lastValueFrom(this.companyService.getQrPosterAssetBlob(assetKey))
      .catch((error) => {
        if (this.simplePosterTemplateAssetPromises.get(assetKey) === promise) {
          this.simplePosterTemplateAssetPromises.delete(assetKey);
        }
        throw error;
      });
    this.simplePosterTemplateAssetPromises.set(assetKey, promise);
    return promise;
  }

  private invalidateSimplePosterDescriptor(options: { refreshLogo?: boolean } = {}): void {
    this.simplePosterDescriptorCache = null;
    this.simplePosterDescriptorRequest = null;
    if (options.refreshLogo) {
      this.simplePosterLogoPromise = null;
    }
  }

  private clearSimplePosterPreviewSession(): void {
    this.invalidateSimplePosterDescriptor();
    this.clearSimplePosterAssetCache();
  }

  private clearSimplePosterAssetCache(): void {
    this.simplePosterAssetContextKey = '';
    this.simplePosterLogoPromise = null;
    this.simplePosterIconPromises.clear();
    this.simplePosterTemplateAssetPromises.clear();
  }

  private clearSimplePosterPreview(): void {
    this.simplePosterPreviewRender?.dispose();
    this.simplePosterPreviewRender = null;
    this.clearSimplePosterPreviewCanvas();
    const renderCanvas = this.simplePosterPreviewRenderCanvas?.nativeElement;
    if (renderCanvas) {
      renderCanvas.width = 0;
      renderCanvas.height = 0;
    }
    this.isSimplePosterPreviewLoading = false;
    this.isSimplePosterPreviewReady = false;
  }

  private clearSimplePosterPreviewCanvas(): void {
    const canvas = this.simplePosterPreviewCanvas?.nativeElement;
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }

  private resolveEditorDraftFooterEnabled(template: QrPosterTemplate): boolean {
    return !this.isBusinessPlan || template.branding?.showScanafyFooter !== false;
  }

  private getTemplateImageAssetKeys(template: QrPosterTemplate): string[] {
    return [...new Set(template.blocks
      .filter((block) => block.type === 'image')
      .map((block) => block.content?.imageAssetKey || '')
      .filter((assetKey): assetKey is string => !!assetKey))];
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.VALIDATION_ERROR'));
      return;
    }

    this.isSaving = true;
    const payload = normalizePosterTexts(this.form.value as QrPosterTexts);

    this.companyService.updateQrPosterTexts(payload)
      .pipe(finalize(() => { this.isSaving = false; }), takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          this.posterTexts = normalizePosterTexts(updated);
          const savedFormValue = this.buildFormValueFromPosterTexts(this.posterTexts);
          this.lastSavedFormValue = savedFormValue;
          this.form.reset(savedFormValue, { emitEvent: false });
          this.invalidateSimplePosterDescriptor();
          this.refreshAdvancedUiCache();
          this.message.success(this.translate.instant('QR_POSTER_TEMPLATE.SAVE_SUCCESS'));
        },
        error: (err) => {
          console.error('Failed to save poster texts', err);
          if (!isForbiddenError(err)) {
            this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.SAVE_ERROR'));
          }
        },
      });
  }

  async saveAdvancedTemplateChanges(): Promise<void> {
    if (!this.workingTemplate || this.isSavingTemplate) {
      return;
    }
    if (this.hasFooterTextContrastViolation) {
      this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.FOOTER_TEXT_COLOR_WARNING'));
      return;
    }
    if (!(await this.confirmQrOverlapIfNeeded())) {
      return;
    }
    const selectedUserTemplate = this.getSelectedUserTemplate();
    if (selectedUserTemplate) {
      this.persistAdvancedTemplate({
        templateId: selectedUserTemplate.id,
        name: selectedUserTemplate.name,
        setActive: false,
      });
      return;
    }
    this.openTemplateNameModal('save');
  }

  async confirmSaveAdvancedTemplate(): Promise<void> {
    if (!this.workingTemplate || this.isSavingTemplate) {
      return;
    }
    if (this.hasFooterTextContrastViolation) {
      this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.FOOTER_TEXT_COLOR_WARNING'));
      return;
    }
    if (!this.workingTemplate.blocks.some((block) => block.type === 'qrCode')) {
      this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.QR_REQUIRED_FOR_ACTIVE'));
      return;
    }
    if (!(await this.confirmQrOverlapIfNeeded())) {
      return;
    }

    const selectedUserTemplate = this.getSelectedUserTemplate();
    if (selectedUserTemplate && !this.hasUnsavedAdvancedChanges) {
      this.setTemplateAsActive(selectedUserTemplate.id);
      return;
    }

    if (selectedUserTemplate) {
      this.persistAdvancedTemplate({
        templateId: selectedUserTemplate.id,
        name: selectedUserTemplate.name,
        setActive: false,
        onSuccess: (savedTemplate) => this.setTemplateAsActive(savedTemplate.id),
      });
      return;
    }

    this.openTemplateNameModal('save-and-activate');
  }

  resetToDefault(): void {
    this.form?.reset(this.defaultFormValue);
  }

  onSelectSystemTemplate(templateId: string): void {
    if (!templateId || templateId === this.selectedSystemTemplateId) {
      return;
    }

    const applyTemplate = () => {
      this.recordPreMutationHistoryCheckpoint('template-switch');
      if (templateId.startsWith('user:')) {
        this.selectedSystemTemplateId = templateId;
        this.selectedVariantId = null;
        const userTemplate = this.getUserTemplateByOptionId(templateId);
        if (userTemplate) {
          this.applyWorkingTemplate(userTemplate.template, templateId);
        }
        return;
      }
      this.applySystemTemplateSelection(templateId);
    };

    if (!this.requiresTemplateReplacementConfirmation(templateId)) {
      applyTemplate();
      return;
    }

    this.modal.confirm({
      nzTitle: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.REPLACE_TEMPLATE_TITLE'),
      nzContent: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.REPLACE_TEMPLATE_MESSAGE'),
      nzOkText: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.APPLY_TEMPLATE'),
      nzCancelText: this.translate.instant('COMMON.CANCEL'),
      nzOnOk: applyTemplate,
    });
  }

  private applySystemTemplateSelection(templateId: string): void {
    this.selectedSystemTemplateId = templateId;
    const extractedId = this.extractTemplateId(templateId);
    const systemTemplate = this.getSystemTemplate(extractedId)
      ?? QR_POSTER_SYSTEM_TEMPLATES_FALLBACK.find((template) => template.id === extractedId);
    if (!systemTemplate) {
      return;
    }

    this.applyWorkingTemplate(systemTemplate.qrPosterTemplate, templateId);

    const defaultVariant = systemTemplate.variants?.[0];
    if (defaultVariant) {
      this.selectedVariantId = defaultVariant.id;
      this.applyVariantColors(defaultVariant);
      if (this.workingTemplate) {
        this.loadedTemplateBaseline = this.cloneTemplate(this.workingTemplate);
        this.hasUnsavedAdvancedChangesFlag = false;
        this.advancedStatusLabelText = this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.STATUS_SAVED');
      }
    } else {
      this.selectedVariantId = null;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateWorkspaceHeight();
    if (this.builderMode === 'advanced') {
      this.updateStageDimensions();
    }
  }

  setZoom(mode: 'fit' | '75' | '100' | '125' | '150' | '175' | '200' | '250'): void {
    this.zoomMode = mode;
  }

  zoomOut(): void {
    const currentIndex = this.zoomOptions.indexOf(this.zoomMode);
    if (currentIndex > 1) {
      this.zoomMode = this.zoomOptions[currentIndex - 1];
      return;
    }
    if (this.zoomMode === 'fit') {
      this.zoomMode = '75';
    }
  }

  zoomIn(): void {
    const currentIndex = this.zoomOptions.indexOf(this.zoomMode);
    if (this.zoomMode === 'fit') {
      this.zoomMode = '100';
      return;
    }
    if (currentIndex >= 1 && currentIndex < this.zoomOptions.length - 1) {
      this.zoomMode = this.zoomOptions[currentIndex + 1];
    }
  }

  @HostListener('window:pointermove', ['$event'])
  onWindowPointerMove(event: PointerEvent): void {
    if (this.rotateState && event.pointerId === this.rotateState.pointerId) {
      this.applyRotationMovement(event);
      this.refreshQrOverlapWarningState();
      event.preventDefault();
      return;
    }

    if (this.marqueeState && event.pointerId === this.marqueeState.pointerId) {
      const point = this.getPagePointFromClient(event.clientX, event.clientY);
      if (!point) {
        return;
      }
      this.marqueeSelectionRect = this.buildMarqueeRect(
        this.marqueeState.startPageX,
        this.marqueeState.startPageY,
        point.x,
        point.y,
      );
      event.preventDefault();
      return;
    }

    if (this.resizeState && event.pointerId === this.resizeState.pointerId) {
      const block = this.workingTemplate?.blocks.find((item) => item.id === this.resizeState?.blockId);
      if (!block) {
        return;
      }
      this.resizeState.checkpointPushed = true;
      Object.assign(block, this.getResizedBlock(block, event));
      this.refreshQrOverlapWarningState();
      event.preventDefault();
      return;
    }

    if (!this.dragState || event.pointerId !== this.dragState.pointerId) {
      return;
    }

    this.applyDragMovement(event);
    this.refreshQrOverlapWarningState();
    event.preventDefault();
  }

  @HostListener('window:pointerup', ['$event'])
  @HostListener('window:pointercancel', ['$event'])
  onWindowPointerUp(event: PointerEvent): void {
    let hadInteraction = false;
    if (this.rotateState && event.pointerId === this.rotateState.pointerId) {
      this.rotateState = null;
      this.guideLines = [];
      this.suppressSelectionClick = true;
      hadInteraction = true;
      this.refreshAdvancedUiCache();
    }
    if (this.resizeState && event.pointerId === this.resizeState.pointerId) {
      this.resizeState = null;
      this.guideLines = [];
      this.suppressSelectionClick = true;
      hadInteraction = true;
      this.refreshAdvancedUiCache();
    }
    if (this.dragState && event.pointerId === this.dragState.pointerId) {
      if (this.dragState.hasMoved) {
        this.suppressSelectionClick = true;
        hadInteraction = true;
      }
      this.dragState = null;
      this.guideLines = [];
      this.refreshAdvancedUiCache();
    }
    if (hadInteraction) {
      this.tryCommitInteractionCheckpoint();
    } else {
      this.clearInteractionCheckpointState('no-interaction');
    }
    if (this.marqueeState && event.pointerId === this.marqueeState.pointerId) {
      this.finishMarqueeSelection();
    }
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }

  addBlock(type: QrPosterBlock['type']): void {
    if (!this.workingTemplate) {
      return;
    }
    this.recordPreMutationHistoryCheckpoint('add-block');

    const accentColor = this.getAccentColor();
    const maxZ = this.workingTemplate.blocks.reduce((max, b) => Math.max(max, b.zIndex || 0), 0);

    const base: QrPosterBlock = {
      id: `${type}-${Date.now()}`,
      type,
      x: this.safeMarginMm + 4,
      y: this.safeMarginMm + 4 + this.workingTemplate.blocks.length * 5,
      width: type === 'line' ? 60 : type === 'qrCode' ? 35 : type === 'icon' ? 10 : 60,
      height: type === 'line' ? 0.2 : type === 'qrCode' ? 35 : type === 'rectangle' ? 18 : type === 'companyLogo' || type === 'image' ? 18 : type === 'icon' ? 10 : 12,
      zIndex: Math.min(maxZ + 5, 900),
      content: {},
      style: {
        fontSize: type === 'icon' ? 12 : 10,
        textAlign: 'left',
        color: '#111827',
      },
    };

    if (type === 'text') {
      base.content = { text: 'Text' };
    } else if (type === 'image') {
      base.content = { imageUrl: '', imageAssetKey: '', fit: 'cover' };
      base.style = {};
    } else if (type === 'icon') {
      base.content = { icon: 'environment' };
      base.style = { color: accentColor, fontSize: 12, textAlign: 'center' };
    } else if (type === 'rectangle') {
      base.style = { backgroundColor: '#f8fafc', borderColor: accentColor, borderWidth: 0.4, borderRadius: 2, opacity: 1 };
    } else if (type === 'line') {
      base.style = { borderColor: accentColor, borderWidth: 0.2 };
    }

    const clampedBlock = this.clampBlock(base);
    this.workingTemplate.blocks = [...this.workingTemplate.blocks, clampedBlock];
    this.setSelectedBlockIds([clampedBlock.id]);
    this.enforceFooterLocally();
    this.refreshAdvancedUiCache();
  }

  duplicateSelectedBlock(): void {
    if (!this.workingTemplate || !this.selectedBlocks.length) {
      return;
    }
    this.recordPreMutationHistoryCheckpoint('duplicate-block');
    const copies = this.selectedBlocks
      .filter((block) => !block.locked)
      .map((block, index) => {
        const copy = this.cloneBlock(block);
        copy.id = `${copy.id}-copy-${Date.now()}-${index}`;
        copy.x += 4;
        copy.y += 4;
        return this.clampBlock(copy);
      });
    if (!copies.length) {
      return;
    }
    this.workingTemplate.blocks = [...this.workingTemplate.blocks, ...copies];
    this.setSelectedBlockIds(copies.map((block) => block.id));
    this.refreshAdvancedUiCache();
  }

  deleteSelectedBlock(): void {
    if (!this.workingTemplate || !this.selectedBlocks.length || !this.canDeleteSelectedBlock) {
      return;
    }
    this.recordPreMutationHistoryCheckpoint('delete-block');
    const deletableIds = new Set(
      this.selectedBlocks
        .filter((block) => !block.locked && (this.isBusinessPlan || block.type !== 'footer'))
        .map((block) => block.id)
    );
    this.workingTemplate.blocks = this.workingTemplate.blocks.filter((block) => !deletableIds.has(block.id));
    this.setSelectedBlockIds(this.workingTemplate.blocks[0] ? [this.workingTemplate.blocks[0].id] : []);
    if (this.editingBlockId && !this.selectedBlockIds.includes(this.editingBlockId)) {
      this.cancelInlineEdit(false);
    }
    this.enforceFooterLocally();
    this.refreshAdvancedUiCache();
  }

  selectBlock(block: QrPosterBlock, event?: MouseEvent | PointerEvent): void {
    block.content = block.content || {};
    block.style = block.style || {};
    const toggleSelection = !!event && (event.shiftKey || event.ctrlKey || event.metaKey);
    if (!toggleSelection && this.suppressSelectionClick) {
      this.suppressSelectionClick = false;
      return;
    }
    if (toggleSelection) {
      if (this.selectedBlockIds.includes(block.id)) {
        this.setSelectedBlockIds(this.selectedBlockIds.filter((id) => id !== block.id));
      } else {
        this.setSelectedBlockIds([...this.selectedBlockIds, block.id]);
      }
      return;
    }
    this.setSelectedBlockIds([block.id]);
  }

  onBlockClick(block: QrPosterBlock, event: MouseEvent): void {
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.selectBlock(block, event);
  }

  insertVariable(variable: string): void {
    if (!this.selectedBlock || !this.isInlineEditableBlock(this.selectedBlock)) {
      this.addCenteredVariableBlock(variable);
      return;
    }
    this.recordPreMutationHistoryCheckpoint('variable-insert');
    const current = this.getEditableBlockText(this.selectedBlock);
    const next = `${current}${current ? ' ' : ''}${variable}`;
    if (this.editingBlockId === this.selectedBlock.id) {
      this.inlineEditDraft = next;
    }
    this.writeEditableBlockText(this.selectedBlock, next);
    this.refreshAdvancedUiCache();
  }

  onBlockPointerDown(block: QrPosterBlock, event: PointerEvent): void {
    if (
      block.locked
      || event.button !== 0
      || this.editingBlockId === block.id
      || (event.target instanceof HTMLElement && !!event.target.closest('.block-inline-editor'))
      || (event.target instanceof HTMLElement && !!event.target.closest('.rotate-handle'))
    ) {
      return;
    }

    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      this.selectBlock(block, event);
      this.suppressSelectionClick = true;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const priorSelectedBlockIds = [...this.selectedBlockIds];
    const hadBlockSelected = priorSelectedBlockIds.includes(block.id);
    const shouldDragGroup = hadBlockSelected && priorSelectedBlockIds.length > 1;
    if (!shouldDragGroup) {
      this.selectBlock(block, event);
    }
    const dragBlockIds = shouldDragGroup
      ? priorSelectedBlockIds
      : [block.id];
    const startBlocks = (this.workingTemplate?.blocks || []).filter((item) => dragBlockIds.includes(item.id));
    const startBounds = this.getBoundsForBlocks(startBlocks);
    this.interactionCheckpointReason = 'drag';
    this.interactionCheckpointBlockIds = [...dragBlockIds];
    this.saveInteractionCheckpointTemplate();
    this.dragState = {
      pointerId: event.pointerId,
      blockIds: dragBlockIds,
      startClientX: event.clientX,
      startClientY: event.clientY,
      hasMoved: false,
      checkpointPushed: false,
      startPositions: Object.fromEntries(startBlocks.map((item) => [item.id, { x: item.x, y: item.y }])),
      startBounds,
      startRotatedBounds: this.getBoundsForBlocksConsideringRotation(startBlocks),
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
    event.preventDefault();
    event.stopPropagation();
  }

  onResizeHandlePointerDown(block: QrPosterBlock, handle: ResizeHandle, event: PointerEvent): void {
    if (block.locked || event.button !== 0 || this.editingBlockId === block.id) {
      return;
    }
    this.selectBlock(block, event);
    this.interactionCheckpointReason = 'resize';
    this.interactionCheckpointBlockIds = [block.id];
    this.saveInteractionCheckpointTemplate();
    this.resizeState = {
      pointerId: event.pointerId,
      blockId: block.id,
      handle,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: block.x,
      startY: block.y,
      startWidth: block.width,
      startHeight: block.height,
      startRotation: this.normalizeRotation(block.rotation || 0),
      checkpointPushed: false,
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = this.getResizeCursor(handle);
    event.preventDefault();
    event.stopPropagation();
  }

  onRotateHandlePointerDown(block: QrPosterBlock, event: PointerEvent): void {
    if (block.locked || event.button !== 0 || this.editingBlockId === block.id || this.selectedBlockIds.length !== 1) {
      return;
    }
    const center = this.getBlockCenterClientCoordinates(block);
    if (!center) {
      return;
    }
    this.selectBlock(block, event);
    this.interactionCheckpointReason = 'rotate';
    this.interactionCheckpointBlockIds = [block.id];
    this.saveInteractionCheckpointTemplate();
    this.rotateState = {
      pointerId: event.pointerId,
      blockId: block.id,
      centerClientX: center.x,
      centerClientY: center.y,
      startPointerAngle: this.getPointerAngleDegrees(event.clientX, event.clientY, center.x, center.y),
      startRotation: this.normalizeRotation(block.rotation || 0),
      checkpointPushed: false,
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
    event.preventDefault();
    event.stopPropagation();
  }

  onCanvasPointerDown(event: PointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest('.canvas-block') || target?.closest('.resize-handle') || target?.closest('.rotate-handle') || target?.closest('.block-inline-editor')) {
      return;
    }
    const point = this.getPagePointFromClient(event.clientX, event.clientY);
    if (!point) {
      return;
    }
    this.cancelInlineEdit();
    this.setSelectedBlockIds([]);
    this.guideLines = [];
    this.marqueeSelectionRect = { leftPx: point.x, topPx: point.y, widthPx: 0, heightPx: 0 };
    this.marqueeState = {
      pointerId: event.pointerId,
      startPageX: point.x,
      startPageY: point.y,
    };
    document.body.style.userSelect = 'none';
    event.preventDefault();
  }

  onAdvancedLayoutPointerDown(event: PointerEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    if (target.closest('.advanced-canvas-wrap') || target.closest('.advanced-sidebar') || target.closest('.advanced-inspector') || target.closest('.ant-modal') || target.closest('.layer-order-overlay')) {
      return;
    }
    if (target.closest('button, input, textarea, select, summary, label, [contenteditable="true"], .ant-btn, .ant-input, .ant-input-number, .ant-select, .ant-color-picker')) {
      return;
    }
    this.cancelInlineEdit();
    this.setSelectedBlockIds([]);
    this.guideLines = [];
  }

  getCanvasBlockStyles(block: QrPosterBlock): Record<string, string> {
    const translateX = block.x * this.canvasScalePxPerMm;
    const translateY = block.y * this.canvasScalePxPerMm;
    const minDimensionMm = block.type === 'line' || block.type === 'rectangle' ? 0.1 : 1;
    const previewTextLayout = (block.type === 'text' || block.type === 'variableText' || block.type === 'iconMultilineText')
      ? this.getPreviewTextLayout(block, this.canvasScalePxPerMm)
      : null;
    return {
      left: '0px',
      top: '0px',
      width: `${Math.max(block.width, minDimensionMm) * this.canvasScalePxPerMm}px`,
      height: `${Math.max(block.height, minDimensionMm) * this.canvasScalePxPerMm}px`,
      transform: `translate3d(${translateX}px, ${translateY}px, 0) rotate(${this.normalizeRotation(block.rotation || 0)}deg)`,
      transformOrigin: 'center center',
      zIndex: `${block.zIndex || 0}`,
      textAlign: block.style?.textAlign || 'left',
      justifyContent: block.style?.textAlign === 'center' ? 'center' : block.style?.textAlign === 'right' ? 'flex-end' : 'flex-start',
      fontSize: `${previewTextLayout?.fontSizePx || Math.max(10, (block.style?.fontSize || 10) * (this.canvasScalePxPerMm / 3))}px`,
      fontWeight: block.style?.fontWeight || 'normal',
      fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
      color: block.style?.color || '#000000',
      background: block.type === 'line'
        ? (block.style?.borderColor || block.style?.color || '#111827')
        : (block.style?.backgroundColor || 'transparent'),
      borderColor: block.type === 'line' ? 'transparent' : (block.style?.borderColor || 'transparent'),
      borderWidth: `${block.type === 'line' ? 0 : Math.max(0, (block.style?.borderWidth || 0) * this.canvasScalePxPerMm)}px`,
      borderRadius: `${(block.style?.borderRadius || 0) * this.canvasScalePxPerMm}px`,
      opacity: `${block.style?.opacity ?? 1}`,
      lineHeight: block.type === 'emergencyNumbers'
        ? '1.35'
        : (previewTextLayout?.lineHeightPx ? `${previewTextLayout.lineHeightPx}px` : '1.24'),
    };
  }

  getCanvasBlockPosition(block: QrPosterBlock): { x: number; y: number } {
    return {
      x: block.x * this.canvasScalePxPerMm,
      y: block.y * this.canvasScalePxPerMm,
    };
  }

  shouldShowBlockBadge(block: QrPosterBlock): boolean {
    return this.selectedBlockIds.length === 1 && this.selectedBlockId === block.id && !['rectangle', 'footer', 'line'].includes(block.type);
  }

  shouldShowRotateHandle(block: QrPosterBlock): boolean {
    return this.selectedBlockIds.length === 1
      && this.selectedBlockId === block.id
      && !block.locked
      && block.type !== 'footer'
      && !this.isBlockEditing(block);
  }

  getRotationBadgeLabel(block: QrPosterBlock): string {
    return `${Math.round(this.normalizeRotation(block.rotation || 0))}°`;
  }

  isBlockRotating(block: QrPosterBlock): boolean {
    return this.rotateState?.blockId === block.id;
  }

  getBlockBadgeLabel(block: QrPosterBlock): string {
    switch (block.type) {
      case 'variableText':
        return 'Variable';
      case 'companyLogo':
        return 'Logo';
      case 'image':
        return 'Image';
      case 'qrCode':
        return 'QR';
      case 'icon':
        return 'Icon';
      case 'iconText':
      case 'iconMultilineText':
        return 'Icon';
      case 'emergencyNumbers':
        return 'Emergency';
      default:
        return 'Text';
    }
  }

  isTextLikeBlock(block: QrPosterBlock | null): boolean {
    return !!block && ['text', 'variableText', 'iconText', 'iconMultilineText'].includes(block.type);
  }

  isTextBlock(block: QrPosterBlock | null): boolean {
    return !!block && block.type === 'text';
  }

  isIconTextBlock(block: QrPosterBlock | null): boolean {
    return !!block && block.type === 'iconText';
  }

  isIconMultilineTextBlock(block: QrPosterBlock | null): boolean {
    return !!block && block.type === 'iconMultilineText';
  }

  isImageBlock(block: QrPosterBlock | null): boolean {
    return !!block && block.type === 'image';
  }

  isCompanyLogoBlock(block: QrPosterBlock | null): boolean {
    return !!block && block.type === 'companyLogo';
  }

  isQrCodeBlock(block: QrPosterBlock | null): boolean {
    return !!block && block.type === 'qrCode';
  }

  isIconBlock(block: QrPosterBlock | null): boolean {
    return !!block && block.type === 'icon';
  }

  isRectangleBlock(block: QrPosterBlock | null): boolean {
    return !!block && block.type === 'rectangle';
  }

  blockSupportsBorderControls(block: QrPosterBlock | null): boolean {
    return this.isTextBlock(block) || this.isRectangleBlock(block);
  }

  isLineBlock(block: QrPosterBlock | null): boolean {
    return !!block && block.type === 'line';
  }

  isFooterBlock(block: QrPosterBlock | null): boolean {
    return !!block && block.type === 'footer';
  }

  isInlineEditableBlock(block: QrPosterBlock | null): boolean {
    return !!block && ['text', 'variableText'].includes(block.type);
  }

  isBlockContentVariable(block: QrPosterBlock | null): boolean {
    return !!block && block.type === 'variableText';
  }

  isBlockEditing(block: QrPosterBlock): boolean {
    return this.editingBlockId === block.id;
  }

  isSizeEditable(block: QrPosterBlock | null): boolean {
    return !!block && block.type !== 'footer';
  }

  shouldShowResizeHandles(block: QrPosterBlock): boolean {
    return this.selectedBlockIds.length === 1 && this.selectedBlockId === block.id && !block.locked && block.type !== 'footer' && !this.isBlockEditing(block);
  }

  isQrOverlapWarningBlock(block: QrPosterBlock): boolean {
    return this.qrOverlapWarningBlockIds.includes(block.id);
  }

  isContentBlock(block: QrPosterBlock | null, type: QrPosterBlock['type']): boolean {
    return !!block && block.type === type;
  }

  updateSelectedBlockFrame(field: 'x' | 'y' | 'width' | 'height', value: number | null): void {
    if (!this.selectedBlock || value == null) {
      return;
    }
    if (this.roundAudit(this.selectedBlock[field] as number) === this.roundAudit(value)) {
      return;
    }
    this.recordPreMutationHistoryCheckpoint('inspector-frame', 'updateSelectedBlockFrame');
    const next = this.clampBlock({ ...this.selectedBlock, [field]: value });
    Object.assign(this.selectedBlock, next);
    this.refreshAdvancedUiCache();
  }

  updateSelectedBlockRotation(value: number | null): void {
    if (!this.selectedBlock || value == null) {
      return;
    }
    if (this.normalizeRotation(this.selectedBlock.rotation || 0) === this.normalizeRotation(value)) {
      return;
    }
    this.recordPreMutationHistoryCheckpoint('inspector-rotation', 'updateSelectedBlockRotation');
    this.selectedBlock.rotation = this.normalizeRotation(value);
    this.refreshAdvancedUiCache();
  }

  resetSelectedBlockRotation(): void {
    this.updateSelectedBlockRotation(0);
  }

  updateSelectedBlockStyle<T extends keyof NonNullable<QrPosterBlock['style']>>(field: T, value: NonNullable<QrPosterBlock['style']>[T], callerReason?: string): void {
    if (!this.selectedBlock || value == null) {
      return;
    }
    const currentVal = (this.selectedBlock.style || {})[field];
    if (currentVal === value) {
      return;
    }
    const blockId = this.selectedBlock.id;
    const reason = callerReason || 'inspector-style';
    const source = `style-edit-session-start:${callerReason ? 'updateSelectedBlockColor' : 'updateSelectedBlockStyle'}`;
    const session = this.activeStyleEditSession;
    const isNewSession = !session || session.blockId !== blockId || session.field !== field;

    if (isNewSession || !this.undoStack.length) {
      this.recordPreMutationHistoryCheckpoint(reason, source);
      if (this.undoStack.length > 0) {
        this.activeStyleEditSession = {
          blockId,
          field,
          reason,
          source,
          baselineFingerprint: this.lastHistoryFingerprint,
        };
      }
    } else {
      this.auditHistory({
        type: 'history-skip',
        actionId: this.nextHistoryActionId(reason),
        reason,
        source: 'style-edit-session-continue',
        skipped: true,
        skipReason: 'active-style-session',
        undoLength: this.undoStack.length,
        redoLength: this.redoStack.length,
        blockId,
        extra: { styleField: field },
      });
    }

    this.selectedBlock.style = this.normalizeBlockStyle({ ...(this.selectedBlock.style || {}), [field]: value });
    const next = this.clampBlock(this.selectedBlock);
    Object.assign(this.selectedBlock, next);
    this.refreshAdvancedUiCache();

    this.scheduleFinishStyleEditSession();
  }

  updateSelectedBlockContent(field: 'text' | 'variable' | 'imageUrl' | 'imageAssetKey' | 'fit' | 'icon', value: string): void {
    if (!this.selectedBlock) {
      return;
    }
    const currentContent = (this.selectedBlock.content || {})[field];
    if (currentContent === value) {
      return;
    }
    const blockId = this.selectedBlock.id;
    const session = this.activeContentEditSession;
    const isNewSession = !session || session.blockId !== blockId || session.field !== field;

    if (isNewSession || !this.undoStack.length) {
      this.recordPreMutationHistoryCheckpoint('inspector-content', 'content-edit-session-start');
      if (this.undoStack.length > 0) {
        this.activeContentEditSession = {
          blockId,
          field,
          reason: 'inspector-content',
          baselineFingerprint: this.lastHistoryFingerprint,
        };
      }
    }

    this.selectedBlock.content = { ...(this.selectedBlock.content || {}), [field]: value };
    if (field === 'text' || field === 'variable') {
      this.inlineEditDraft = value;
    }
    this.refreshAdvancedUiCache();
  }

  finishContentEditSession(reason: string): void {
    if (this.activeContentEditSession) {
      this.activeContentEditSession = null;
      if (this.debugQrPosterHistory) {
        console.debug('[QR Builder][Content Edit Session] ended', { reason });
      }
    }
  }

  private finishStyleEditSession(reason: string): void {
    if (this.activeStyleEditSession) {
      this.activeStyleEditSession = null;
      if (this.debugQrPosterHistory) {
        console.debug('[QR Builder][Style Edit Session] ended', { reason });
      }
    }
  }

  private scheduleFinishStyleEditSession(): void {
    if (this.styleEditSessionClearTimer) {
      clearTimeout(this.styleEditSessionClearTimer);
    }
    this.styleEditSessionClearTimer = setTimeout(() => {
      this.finishStyleEditSession('idle-timeout');
    }, 500);
  }

  async onImageFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file || !this.selectedBlock || this.selectedBlock.type !== 'image') {
      return;
    }
    const validationMessage = this.validateImageFile(file);
    if (validationMessage) {
      this.message.error(validationMessage);
      if (input) {
        input.value = '';
      }
      return;
    }
    try {
      this.recordPreMutationHistoryCheckpoint('image-upload');
      const localPreview = await this.convertBlobToBase64(file);
      const response = await lastValueFrom(this.companyService.uploadQrPosterTemplateAsset(file));
      if (response.assetKey && localPreview) {
        this.imageAssetPreviewCache.set(response.assetKey, localPreview);
      }
      this.selectedBlock.content = {
        ...(this.selectedBlock.content || {}),
        imageAssetKey: response.assetKey,
        imageUrl: localPreview || '',
        fit: this.selectedBlock.content?.fit || 'cover',
      };
      this.selectedImageFileName = response.fileName || file.name || '';
      this.refreshAdvancedUiCache();
    } catch (error) {
      console.error('Failed to upload QR poster image asset', error);
      this.message.error(this.getImageUploadErrorMessage(error));
    } finally {
      if (input) {
        input.value = '';
      }
    }
  }

  clearSelectedImage(): void {
    if (!this.selectedBlock || this.selectedBlock.type !== 'image') {
      return;
    }
    this.recordPreMutationHistoryCheckpoint('image-remove');
    this.selectedBlock.content = {
      ...(this.selectedBlock.content || {}),
      imageAssetKey: '',
      imageUrl: '',
    };
    this.selectedImageFileName = '';
    this.refreshAdvancedUiCache();
  }

  private validateImageFile(file: File): string | null {
    if (!QrPosterTemplateComponent.QR_POSTER_IMAGE_ALLOWED_TYPES.has(file.type)) {
      return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.IMAGE_UPLOAD_INVALID_TYPE');
    }
    if (file.size > QrPosterTemplateComponent.QR_POSTER_IMAGE_MAX_SIZE_BYTES) {
      return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.IMAGE_UPLOAD_TOO_LARGE', {
        maxSizeMb: QrPosterTemplateComponent.QR_POSTER_IMAGE_MAX_SIZE_MB,
      });
    }
    return null;
  }

  private getImageUploadErrorMessage(error: unknown): string {
    const httpError = error as HttpErrorResponse | undefined;
    if (httpError?.status === 413 || httpError?.error?.code === 'QR_POSTER_IMAGE_TOO_LARGE') {
      return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.IMAGE_UPLOAD_TOO_LARGE', {
        maxSizeMb: httpError?.error?.maxSizeMb || QrPosterTemplateComponent.QR_POSTER_IMAGE_MAX_SIZE_MB,
      });
    }
    if (httpError?.error?.code === 'QR_POSTER_IMAGE_INVALID_TYPE') {
      return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.IMAGE_UPLOAD_INVALID_TYPE');
    }
    return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.IMAGE_UPLOAD_ERROR');
  }

  updateSelectedBlockColor(field: 'color' | 'backgroundColor' | 'borderColor' | 'iconColor', value: string | null): void {
    if (field === 'iconColor') {
      this.updateSelectedBlockStyle('iconColor', value || '');
      return;
    }
    this.updateSelectedBlockStyle(field, (value || '') as NonNullable<QrPosterBlock['style']>[typeof field], 'color-picker');
  }

  private normalizeHexColor(hex: string | null | undefined, fallback = '#000000'): string {
    const value = (hex || '').trim();
    const match = value.match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
    if (!match) {
      return fallback;
    }
    const normalized = match[1].length === 3
      ? match[1].split('').map((part) => part + part).join('')
      : match[1];
    return `#${normalized.toLowerCase()}`;
  }

  private getHexLuminance(hex: string): number {
    const normalized = this.normalizeHexColor(hex);
    const channels = [0, 2, 4].map((offset) => {
      const srgb = parseInt(normalized.substring(offset + 1, offset + 3), 16) / 255;
      return srgb <= 0.03928
        ? srgb / 12.92
        : ((srgb + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  }

  private getContrastRatio(foreground: string, background: string): number {
    const fg = this.getHexLuminance(foreground);
    const bg = this.getHexLuminance(background);
    const lighter = Math.max(fg, bg);
    const darker = Math.min(fg, bg);
    return (lighter + 0.05) / (darker + 0.05);
  }

  contrastTextColor(hexBg: string): string {
    const hex = this.normalizeHexColor(hexBg).replace('#', '');
    if (hex.length < 6) {
      return '#000000';
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5 ? '#ffffff' : '#000000';
  }

  updateFooterBackground(value: string | null): void {
    if (!this.selectedBlock || this.selectedBlock.type !== 'footer') {
      return;
    }
    const bgColor = this.normalizeHexColor(value, '#d9d9d9');
    this.recordPreMutationHistoryCheckpoint('footer-background');
    const textColor = this.contrastTextColor(bgColor);
    this.selectedBlock.style = { ...(this.selectedBlock.style || {}), backgroundColor: bgColor, color: textColor };
    const next = this.clampBlock(this.selectedBlock);
    Object.assign(this.selectedBlock, next);
    this.refreshAdvancedUiCache();
  }

  updateFooterTextColor(value: string | null): void {
    if (!this.selectedBlock || this.selectedBlock.type !== 'footer') {
      return;
    }
    const backgroundColor = this.normalizeHexColor(this.selectedBlock.style?.backgroundColor, '#d9d9d9');
    const textColor = value
      ? this.normalizeHexColor(value, this.contrastTextColor(backgroundColor))
      : this.contrastTextColor(backgroundColor);
    this.recordPreMutationHistoryCheckpoint('footer-text-color');
    this.selectedBlock.style = { ...(this.selectedBlock.style || {}), color: textColor };
    const next = this.clampBlock(this.selectedBlock);
    Object.assign(this.selectedBlock, next);
    this.refreshAdvancedUiCache();
  }

  isFooterTextContrastWarningVisible(block: QrPosterBlock | null): boolean {
    if (!block || block.type !== 'footer') {
      return false;
    }
    const backgroundColor = this.normalizeHexColor(block.style?.backgroundColor, '#d9d9d9');
    const textColor = this.normalizeHexColor(block.style?.color, this.contrastTextColor(backgroundColor));
    return this.getContrastRatio(textColor, backgroundColor) < 4.5;
  }

  async pickColorForSelectedBlock(field: 'color' | 'backgroundColor' | 'borderColor' | 'iconColor'): Promise<void> {
    if (!this.selectedBlock) {
      return;
    }
    const eyeDropperCtor = (window as EyeDropperWindow).EyeDropper;
    if (!eyeDropperCtor) {
      this.message.info(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.EYEDROPPER_UNSUPPORTED'));
      return;
    }
    try {
      const eyeDropper = new eyeDropperCtor();
      const result = await eyeDropper.open();
      if (!result?.sRGBHex) {
        return;
      }
      this.updateSelectedBlockColor(field, result.sRGBHex);
      this.refreshAdvancedUiCache();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      console.error('Failed to pick poster color', error);
      this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.EYEDROPPER_ERROR'));
    }
  }

  startInlineEdit(block: QrPosterBlock, event?: MouseEvent): void {
    if (!this.isInlineEditableBlock(block)) {
      return;
    }
    this.selectBlock(block);
    this.editingBlockId = block.id;
    this.inlineEditDraft = this.getEditableBlockText(block);
    event?.stopPropagation();
    setTimeout(() => {
      const editor = this.canvasStageRef?.nativeElement.querySelector<HTMLTextAreaElement>(`textarea[data-inline-editor="${block.id}"]`);
      editor?.focus();
      editor?.select();
    }, 0);
  }

  updateInlineEditDraft(value: string): void {
    this.inlineEditDraft = value;
  }

  commitInlineEdit(): void {
    if (!this.editingBlockId || !this.workingTemplate) {
      return;
    }
    const block = this.workingTemplate.blocks.find((item) => item.id === this.editingBlockId);
    if (!block) {
      this.cancelInlineEdit(false);
      return;
    }
    if (this.getEditableBlockText(block) !== this.inlineEditDraft) {
      this.recordPreMutationHistoryCheckpoint('inline-edit-commit');
    }
    this.writeEditableBlockText(block, this.inlineEditDraft);
    this.editingBlockId = null;
    this.inlineEditDraft = '';
    this.refreshAdvancedUiCache();
  }

  cancelInlineEdit(commitDraft = true): void {
    if (commitDraft && this.editingBlockId && this.workingTemplate) {
      this.commitInlineEdit();
      return;
    }
    this.editingBlockId = null;
    this.inlineEditDraft = '';
  }

  onInlineEditorKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelInlineEdit(false);
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      this.commitInlineEdit();
    }
  }

  updateEmergencyRow(index: number, field: 'label' | 'value' | 'description', value: string): void {
    if (!this.selectedBlock || this.selectedBlock.type !== 'emergencyNumbers') {
      return;
    }
    const rows = [...(this.selectedBlock.content?.rows || [])];
    rows[index] = { ...(rows[index] || { label: '', value: '' }), [field]: value };
    this.selectedBlock.content = { ...(this.selectedBlock.content || {}), rows };
    this.refreshAdvancedUiCache();
  }

  updateFooterVisibility(removeFooter: boolean): void {
    if (!this.workingTemplate) {
      return;
    }
    this.recordPreMutationHistoryCheckpoint('footer-visibility');
    this.workingTemplate.branding.showScanafyFooter = !removeFooter;
    this.enforceFooterLocally();
    if (removeFooter) {
      this.setSelectedBlockIds(this.selectedBlockIds.filter((id) => {
        const block = this.workingTemplate?.blocks.find((b) => b.id === id);
        return block?.type !== 'footer';
      }));
    }
    this.refreshAdvancedUiCache();
  }

  bringSelectedBlockForward(): void {
    if (!this.selectedBlocks.length || !this.workingTemplate || !this.canBringSelectedBlockForward) {
      return;
    }
    this.recordPreMutationHistoryCheckpoint('layer-forward');
    const ordered = this.getOrderedBlocks();
    for (let index = ordered.length - 2; index >= 0; index -= 1) {
      if (this.selectedBlockIds.includes(ordered[index].id) && !this.selectedBlockIds.includes(ordered[index + 1].id)) {
        const a = ordered[index];
        const b = ordered[index + 1];
        const tmpZ = a.zIndex || 0;
        a.zIndex = b.zIndex || 0;
        b.zIndex = tmpZ;
        break;
      }
    }
    this.refreshAdvancedUiCache();
  }

  openIconPicker(): void {
    if (!this.selectedBlock || !['icon', 'iconText', 'iconMultilineText'].includes(this.selectedBlock.type) || !this.iconPickerContentRef) {
      return;
    }
    this.iconPickerModalRef?.destroy();
    this.iconPickerModalRef = this.modal.create({
      nzTitle: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.CHOOSE_ICON'),
      nzContent: this.iconPickerContentRef,
      nzFooter: [
        {
          label: this.translate.instant('COMMON.CANCEL'),
          onClick: () => this.closeIconPicker(),
        },
      ],
      nzWidth: 560,
      nzMaskClosable: true,
    });
    this.iconPickerModalRef.afterClose.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.iconPickerModalRef = null;
    });
  }

  closeIconPicker(): void {
    this.iconPickerModalRef?.destroy();
    this.iconPickerModalRef = null;
  }

  applyIconSelection(iconKey: string): void {
    if (!this.selectedBlock || !['icon', 'iconText', 'iconMultilineText'].includes(this.selectedBlock.type)) {
      this.closeIconPicker();
      return;
    }
    this.recordPreMutationHistoryCheckpoint('icon-picker');
    this.selectedBlock.content = {
      ...(this.selectedBlock.content || {}),
      icon: iconKey,
    };
    this.refreshAdvancedUiCache();
    this.closeIconPicker();
  }

  sendSelectedBlockBackward(): void {
    if (!this.selectedBlocks.length || !this.workingTemplate || !this.canSendSelectedBlockBackward) {
      return;
    }
    this.recordPreMutationHistoryCheckpoint('layer-backward');
    const ordered = this.getOrderedBlocks();
    for (let index = 1; index < ordered.length; index += 1) {
      if (this.selectedBlockIds.includes(ordered[index].id) && !this.selectedBlockIds.includes(ordered[index - 1].id)) {
        const a = ordered[index];
        const b = ordered[index - 1];
        const tmpZ = a.zIndex || 0;
        a.zIndex = b.zIndex || 0;
        b.zIndex = tmpZ;
        break;
      }
    }
    this.refreshAdvancedUiCache();
  }

  get orderedBlocksForLayerModal(): QrPosterBlock[] {
    if (!this.workingTemplate) {
      return [];
    }
    return this.getOrderedBlocks().filter((block) => block.type !== 'footer').reverse();
  }

  openLayerModal(): void {
    this.layerModalVisible = true;
  }

  closeLayerModal(): void {
    this.layerModalVisible = false;
  }

  onLayerItemSelect(block: QrPosterBlock, event?: Event): void {
    this.selectBlock(block, event instanceof MouseEvent || event instanceof PointerEvent ? event : undefined);
    event?.preventDefault();
    event?.stopPropagation();
  }

  onLayerDrop(event: CdkDragDrop<QrPosterBlock[]>): void {
    if (!this.workingTemplate || event.previousIndex === event.currentIndex) {
      return;
    }
    this.recordPreMutationHistoryCheckpoint('layer-drop');
    const footerBlocks = this.getOrderedBlocks().filter((block) => block.type === 'footer');
    const displayOrdered = [...this.orderedBlocksForLayerModal];
    moveItemInArray(displayOrdered, event.previousIndex, event.currentIndex);
    const ordered = [...displayOrdered.reverse(), ...footerBlocks];
    this.resequenceBlockLayers(ordered);
    this.refreshAdvancedUiCache();
  }

  getLayerBlockLabel(block: QrPosterBlock): string {
    const t = (key: string) => this.translate.instant(`QR_POSTER_TEMPLATE.BUILDER.LAYER_${key}`);

    const iconHumanName = (key: string): string => {
      const def = getQrPosterIconDefinition(key);
      return def ? this.translate.instant(def.labelKey) : (key || t('ICON'));
    };

    const rectDescriptor = (b: QrPosterBlock): string => {
      const y = b.y || 0;
      const h = b.height || 0;
      if (b.id === 'header-bg' || (y < 30 && h > 10)) return t('RECT_HEADER');
      if (b.id.includes('address-box') || b.id.includes('address-card') || b.id.includes('address-section')) return t('RECT_ADDRESS');
      if (b.id.includes('qr-frame') || b.id.includes('qr-box') || b.id.includes('qr-card') || b.id.includes('qr-section')) return t('RECT_QR');
      if (b.id.includes('company-box') || b.id.includes('company-card') || b.id.includes('company-strip')) return t('RECT_COMPANY');
      if (b.id.includes('numbers-table') || b.id.includes('table')) return t('RECT_TABLE');
      if (y > 160) return t('RECT_BOTTOM');
      if (y < 40) return t('RECT_TOP');
      return t('RECTANGLE');
    };

    const resolve = (b: QrPosterBlock): string => {
      const raw = b.content?.text || b.content?.variable || '';
      return this.variableResolver.resolveText(raw, this.previewContext);
    };

    switch (block.type) {
      case 'text':
      case 'variableText': {
        const resolved = resolve(block);
        const preview = resolved.substring(0, 28);
        return preview ? `${t('TEXT')}: "${preview}${preview.length >= 28 ? '\u2026' : ''}"` : t('TEXT_BLOCK');
      }
      case 'iconText':
      case 'iconMultilineText': {
        const iconKey = block.content?.icon || '';
        const label = iconHumanName(iconKey);
        const resolved = resolve(block);
        const preview = resolved.substring(0, 20);
        if (preview) {
          return `${label}: "${preview}${preview.length >= 20 ? '\u2026' : ''}"`;
        }
        return `${label} (${t('ICON_TEXT_NO_TEXT')})`;
      }
      case 'icon': {
        const iconKey = block.content?.icon || '';
        return `${t('ICON')}: ${iconHumanName(iconKey)}`;
      }
      case 'image':
        return t('IMAGE');
      case 'companyLogo':
        return t('COMPANY_LOGO');
      case 'qrCode':
        return t('QR_CODE');
      case 'line':
        return t('LINE');
      case 'rectangle':
        return rectDescriptor(block);
      case 'footer':
        return t('FOOTER');
      case 'emergencyNumbers':
        return t('EMERGENCY_NUMBERS');
      default:
        return t('BLOCK');
    }
  }

  resolvePreviewText(block: QrPosterBlock): string {
    const localized = this.activeLanguage === 'de' ? block.content?.de : block.content?.en;
    const sourceText = localized || block.content?.text || block.content?.variable || '';
    return this.variableResolver.resolveText(sourceText, this.previewContext);
  }

  getCanvasTextStyles(block: QrPosterBlock): Record<string, string> {
    if (block.type !== 'text' && block.type !== 'variableText') {
      return {};
    }
    const layout = this.getPreviewTextLayout(block, this.canvasScalePxPerMm);
    return {
      whiteSpace: layout.textFit === 'singleLineShrink' ? 'nowrap' : 'pre-wrap',
      overflowWrap: layout.textFit === 'singleLineShrink' ? 'normal' : 'anywhere',
      wordBreak: layout.textFit === 'singleLineShrink' ? 'normal' : 'break-word',
      lineHeight: `${layout.lineHeightPx}px`,
      display: 'block',
      width: '100%',
    };
  }

  hasPreviewTextFitWarning(block: QrPosterBlock): boolean {
    return (block.type === 'text' || block.type === 'variableText')
      && this.getPreviewTextLayout(block, this.canvasScalePxPerMm).didClip;
  }

  getInlineEditorBackground(block: QrPosterBlock): string {
    const raw = (block.style?.color || '#000000').replace('#', '');
    const hex = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
    const r = parseInt(hex.substring(0, 2), 16) || 0;
    const g = parseInt(hex.substring(2, 4), 16) || 0;
    const b = parseInt(hex.substring(4, 6), 16) || 0;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.7)';
  }

  isWrapTextFitClipRisk(block: QrPosterBlock | null): boolean {
    if (!block || (block.type !== 'text' && block.type !== 'variableText')) {
      return false;
    }
    const layout = this.getPreviewTextLayout(block, this.canvasScalePxPerMm);
    return layout.textFit === 'wrap' && layout.didClip;
  }

  isSelectedBlockWrapTextFitClipRisk(): boolean {
    return this.isWrapTextFitClipRisk(this.selectedBlock);
  }

  applyShrinkToSelectedBlock(): void {
    if (!this.isSelectedBlockWrapTextFitClipRisk()) {
      return;
    }
    this.updateSelectedBlockStyle('textFit', 'shrink');
  }

  getSelectedTextFitHelpKey(): string {
    const textFit = normalizeQrPosterTextFit(this.selectedBlock?.style?.textFit, 'wrap');
    switch (textFit) {
      case 'shrink':
        return 'QR_POSTER_TEMPLATE.BUILDER.TEXT_FIT_HELP_SHRINK';
      case 'singleLineShrink':
        return 'QR_POSTER_TEMPLATE.BUILDER.TEXT_FIT_HELP_SINGLE_LINE_SHRINK';
      case 'wrap':
      default:
        return 'QR_POSTER_TEMPLATE.BUILDER.TEXT_FIT_HELP_WRAP';
    }
  }

  private getIconTextGapMm(block: QrPosterBlock): number {
    return Math.max(0.5, Math.min(4, Number(block.style?.gap || 1.5)));
  }

  private getPreviewTextOccupiedHeightPx(lineCount: number, fontSizePx: number, lineHeightPx: number): number {
    const baselineOffsetPx = fontSizePx * 0.78;
    const bottomGuardPx = fontSizePx * 0.26;
    return baselineOffsetPx + Math.max(lineCount - 1, 0) * lineHeightPx + bottomGuardPx;
  }

  private getPreviewTextLayout(
    block: QrPosterBlock,
    scalePxPerMm: number,
  ): { fontSizePx: number; lineHeightPx: number; textFit: QrPosterTextFit; didClip: boolean; lineCount: number; maxLines: number } {
    const text = this.resolvePreviewText(block);
    const textFit = normalizeQrPosterTextFit(block.style?.textFit, 'wrap');
    const fontWeight = block.style?.fontWeight || 'normal';
    const baseFontSizePx = Math.max(10, (block.style?.fontSize || 10) * (scalePxPerMm / 3));
    const minFontSizePx = Math.max(5, 6 * (scalePxPerMm / 3));
    const horizontalPaddingPx = Math.min(0.8, Math.max(0.2, block.width * 0.025)) * scalePxPerMm;
    const verticalPaddingPx = Math.min(0.5, Math.max(0.16, block.height * 0.03)) * scalePxPerMm;
    const availableWidthPx = Math.max((block.width * scalePxPerMm) - (horizontalPaddingPx * 2), 1);
    const availableHeightPx = Math.max((block.height * scalePxPerMm) - (verticalPaddingPx * 2), 1);

    let fontSizePx = baseFontSizePx;
    let lines = textFit === 'singleLineShrink'
      ? [text]
      : this.splitPreviewTextLines(text, availableWidthPx, fontSizePx, fontWeight);

    if (textFit === 'singleLineShrink') {
      const singleLineHeightLimit = Math.max(minFontSizePx, Math.min(baseFontSizePx, availableHeightPx / 1.1));
      fontSizePx = singleLineHeightLimit;
      let width = this.measurePreviewTextWidthPx(text, fontSizePx, fontWeight);
      if (width > availableWidthPx) {
        fontSizePx = Math.max(minFontSizePx, Math.min(fontSizePx, fontSizePx * (availableWidthPx / Math.max(width, 0.001))));
        width = this.measurePreviewTextWidthPx(text, fontSizePx, fontWeight);
      }
      while (width > availableWidthPx && fontSizePx > minFontSizePx) {
        fontSizePx = Math.max(minFontSizePx, fontSizePx - 0.5);
        width = this.measurePreviewTextWidthPx(text, fontSizePx, fontWeight);
      }
      return {
        fontSizePx,
        lineHeightPx: Math.max(fontSizePx * 1.12, fontSizePx),
        textFit,
        didClip: false,
        lineCount: 1,
        maxLines: 1,
      };
    }

    let lineHeightPx = Math.max(fontSizePx * 1.12, fontSizePx);
    let totalHeightPx = this.getPreviewTextOccupiedHeightPx(lines.length, fontSizePx, lineHeightPx);

    if (textFit === 'shrink') {
      while (totalHeightPx > availableHeightPx && fontSizePx > minFontSizePx) {
        const shrinkRatio = availableHeightPx / Math.max(totalHeightPx, 0.001);
        const candidateFontSize = Math.max(minFontSizePx, Math.min(fontSizePx - 0.5, fontSizePx * Math.max(0.82, shrinkRatio)));
        if (candidateFontSize >= fontSizePx) {
          break;
        }
        fontSizePx = candidateFontSize;
        lines = this.splitPreviewTextLines(text, availableWidthPx, fontSizePx, fontWeight);
        lineHeightPx = Math.max(fontSizePx * 1.12, fontSizePx);
        totalHeightPx = this.getPreviewTextOccupiedHeightPx(lines.length, fontSizePx, lineHeightPx);
      }
    }

    const usableHeightPx = availableHeightPx - fontSizePx * 0.78 - fontSizePx * 0.26;
    const maxLines = Math.max(1, Math.floor(usableHeightPx / Math.max(lineHeightPx, 0.001)) + 1);
    let didClip = false;
    if (totalHeightPx > availableHeightPx) {
      didClip = lines.length > maxLines;
    }

    if (this.debugPreviewTextLayout && block.id?.includes('address')) {
      console.debug('[QR Builder Preview][TextLayout]', {
        blockId: block.id,
        text: text.substring(0, 80),
        textFit,
        baseFontSizePx,
        fontSizePx,
        lineHeightPx,
        lineCount: lines.length,
        totalHeightPx,
        availableHeightPx,
        availableWidthPx,
        didShrink: fontSizePx < baseFontSizePx - 0.01,
        didClip,
        maxLines,
        scalePxPerMm,
        blockWidth: block.width,
        blockHeight: block.height,
      });
    }

    return {
      fontSizePx,
      lineHeightPx,
      textFit,
      didClip,
      lineCount: lines.length,
      maxLines,
    };
  }

  private splitPreviewTextLines(text: string, maxWidthPx: number, fontSizePx: number, fontWeight: string): string[] {
    const normalizedText = (text || '').replace(/\r/g, '');
    const paragraphs = normalizedText.split('\n');
    const lines: string[] = [];

    for (const paragraph of paragraphs) {
      const words = paragraph.split(/\s+/).filter(Boolean);
      if (!words.length) {
        lines.push('');
        continue;
      }
      let currentLine = words[0];
      for (let index = 1; index < words.length; index += 1) {
        const nextWord = words[index];
        const candidate = `${currentLine} ${nextWord}`.trim();
        if (this.measurePreviewTextWidthPx(candidate, fontSizePx, fontWeight) <= maxWidthPx) {
          currentLine = candidate;
          continue;
        }
        lines.push(currentLine);
        currentLine = nextWord;
      }
      lines.push(currentLine);
    }

    return lines.length ? lines : [''];
  }

  private getIconTextLayout(
    block: QrPosterBlock,
    context: QrPosterRenderContext,
    scalePxPerMm: number,
  ): { iconX: number; iconY: number; iconSizeMm: number; textX: number; textWidthMm: number } {
    const iconKey = (block.content?.icon || '').trim().toLowerCase();
    const hasIcon = !!iconKey;
    const text = this.variableResolver.resolveText(block.content?.text || block.content?.variable || '', context);
    const fontSizePx = Math.max(10, (block.style?.fontSize || 10) * (scalePxPerMm / 3));
    const fontWeight = block.style?.fontWeight || 'normal';
    const textWidthPx = this.measurePreviewTextWidthPx(text, fontSizePx, fontWeight);
    const textWidthMm = textWidthPx / scalePxPerMm;
    const iconSizeMm = hasIcon ? Math.min(4.2, Math.max(block.height - 1, 1.5)) : 0;
    const gapMm = hasIcon ? this.getIconTextGapMm(block) : 0;
    const rowWidthMm = Math.min(block.width, textWidthMm + iconSizeMm + gapMm);
    const rowX = (block.style?.textAlign || 'left') === 'center'
      ? block.x + ((block.width - rowWidthMm) / 2)
      : (block.style?.textAlign || 'left') === 'right'
        ? block.x + block.width - rowWidthMm
        : block.x;
    const effectiveTextWidthMm = Math.max(Math.min(textWidthMm, block.width - iconSizeMm - gapMm), 8);

    return {
      iconX: rowX,
      iconY: block.y + ((block.height - iconSizeMm) / 2),
      iconSizeMm,
      textX: hasIcon ? rowX + iconSizeMm + gapMm : rowX,
      textWidthMm: hasIcon ? effectiveTextWidthMm : Math.max(block.width, 8),
    };
  }

  private measurePreviewTextWidthPx(text: string, fontSizePx: number, fontWeight: string): number {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      return fontSizePx * Math.max(text.length, 1) * 0.55;
    }
    context.font = `${fontWeight} ${fontSizePx}px "Open Sans", Helvetica, Arial, sans-serif`;
    return context.measureText(text || ' ').width;
  }

  private getUniqueBlockId(baseId: string): string {
    if (!this.workingTemplate) {
      return baseId;
    }
    if (!this.workingTemplate.blocks.some((block) => block.id === baseId)) {
      return baseId;
    }
    let suffix = 2;
    while (this.workingTemplate.blocks.some((block) => block.id === `${baseId}-${suffix}`)) {
      suffix += 1;
    }
    return `${baseId}-${suffix}`;
  }

  ungroupSelectedIconTextRow(): void {
    if (!this.selectedBlock || this.selectedBlock.type !== 'iconText' || !this.workingTemplate) {
      return;
    }
    const block = this.selectedBlock;
    if (block.locked) {
      return;
    }

    const layout = this.getIconTextLayout(block, this.previewContext, this.canvasScalePxPerMm);
    const iconIdBase = block.id.endsWith('-row') ? block.id.slice(0, -4) : block.id;
    const iconId = this.getUniqueBlockId(`${iconIdBase}-icon`);
    const textId = this.getUniqueBlockId(`${iconIdBase}-text`);
    const rawText = block.content?.text || block.content?.variable || '';
    const iconBlock: QrPosterBlock = this.clampBlock({
      id: iconId,
      type: 'icon',
      x: layout.iconX,
      y: layout.iconY,
      width: layout.iconSizeMm,
      height: layout.iconSizeMm,
      rotation: block.rotation || 0,
      zIndex: block.zIndex || 0,
      locked: block.locked,
      hidden: block.hidden,
      content: { icon: block.content?.icon || 'environment' },
      style: { color: block.style?.iconColor || block.style?.color || '#111827' },
    });
    const textBlock: QrPosterBlock = this.clampBlock({
      id: textId,
      type: 'text',
      x: layout.textX,
      y: block.y,
      width: layout.textWidthMm,
      height: block.height,
      rotation: block.rotation || 0,
      zIndex: block.zIndex || 0,
      locked: block.locked,
      hidden: block.hidden,
      content: { text: rawText },
      style: {
        fontSize: block.style?.fontSize,
        fontWeight: block.style?.fontWeight,
        textAlign: 'left',
        color: block.style?.color || '#000000',
        backgroundColor: block.style?.backgroundColor,
        borderColor: block.style?.borderColor,
        borderWidth: block.style?.borderWidth,
        borderRadius: block.style?.borderRadius,
        opacity: block.style?.opacity,
      },
    });
    this.recordPreMutationHistoryCheckpoint('ungroup-iconText', 'ungroup-iconText');
    const index = this.workingTemplate.blocks.findIndex((item) => item.id === block.id);
    if (index === -1) {
      return;
    }
    this.workingTemplate.blocks.splice(index, 1, iconBlock, textBlock);
    this.setSelectedBlockIds([iconBlock.id, textBlock.id]);
    this.refreshAdvancedUiCache();
  }

  ungroupSelectedIconMultilineText(): void {
    if (!this.selectedBlock || this.selectedBlock.type !== 'iconMultilineText' || !this.workingTemplate) {
      return;
    }
    const block = this.selectedBlock;
    const scalePxPerMm = this.canvasScalePxPerMm;
    const layout = this.getPreviewTextLayout(block, scalePxPerMm);
    const fontSizePx = layout.fontSizePx;
    const fontWeight = block.style?.fontWeight || 'normal';
    const gapMm = Math.max(0.5, Math.min(4, Number(block.style?.gap || 3)));
    const iconSizeMm = Math.min(4.2, Math.max(block.height - 1, 1.5));
    const horizontalPaddingPx = Math.min(0.8, Math.max(0.2, block.width * 0.025)) * scalePxPerMm;
    const availableWidthPx = Math.max((block.width * scalePxPerMm) - (horizontalPaddingPx * 2), 1);
    const safetyPaddingPx = 1 * scalePxPerMm;
    const textMaxWidthPx = Math.max((block.width * 0.35) * scalePxPerMm, availableWidthPx - ((iconSizeMm + gapMm) * scalePxPerMm) - safetyPaddingPx);
    const lines = this.splitPreviewTextLines(this.resolvePreviewText(block), textMaxWidthPx, fontSizePx, fontWeight);
    const firstLineWidth = this.measurePreviewTextWidthPx(lines[0] || '', fontSizePx, fontWeight);
    const blockCenterX = block.width * scalePxPerMm / 2;
    const firstLineLeftPx = blockCenterX - firstLineWidth / 2;
    const gapPx = gapMm * scalePxPerMm;
    const iconSizePx = iconSizeMm * scalePxPerMm;
    const iconLeftPx = firstLineLeftPx - gapPx - iconSizePx;
    const iconId = this.getUniqueBlockId(`${block.id}-icon`);
    const textId = this.getUniqueBlockId(`${block.id}-text`);
    const rawText = block.content?.text || block.content?.variable || '';
    const iconBlock: QrPosterBlock = this.clampBlock({
      id: iconId,
      type: 'icon',
      x: block.x + iconLeftPx / scalePxPerMm,
      y: block.y,
      width: iconSizeMm,
      height: iconSizeMm,
      rotation: block.rotation || 0,
      zIndex: block.zIndex || 0,
      locked: block.locked,
      hidden: block.hidden,
      content: { icon: block.content?.icon || 'environment' },
      style: { color: block.style?.iconColor || block.style?.color || '#111827' },
    });
    const textBlock: QrPosterBlock = this.clampBlock({
      id: textId,
      type: 'text',
      x: block.x,
      y: block.y,
      width: block.width,
      height: block.height,
      rotation: block.rotation || 0,
      zIndex: block.zIndex || 0,
      locked: block.locked,
      hidden: block.hidden,
      content: { text: rawText },
      style: {
        fontSize: block.style?.fontSize,
        fontWeight: block.style?.fontWeight,
        textAlign: block.style?.textAlign || 'center',
        textFit: block.style?.textFit,
        color: block.style?.color || '#000000',
        backgroundColor: block.style?.backgroundColor,
        borderColor: block.style?.borderColor,
        borderWidth: block.style?.borderWidth,
        borderRadius: block.style?.borderRadius,
        opacity: block.style?.opacity,
      },
    });
    this.recordPreMutationHistoryCheckpoint('ungroup-iconMultilineText', 'ungroup-iconMultilineText');
    const index = this.workingTemplate.blocks.findIndex((item) => item.id === block.id);
    if (index === -1) {
      return;
    }
    this.workingTemplate.blocks.splice(index, 1, iconBlock, textBlock);
    this.setSelectedBlockIds([iconBlock.id, textBlock.id]);
    this.refreshAdvancedUiCache();
  }

  getBlockTypeLabel(type: QrPosterBlock['type']): string {
    switch (type) {
      case 'text':
      case 'variableText':
      case 'iconText':
      case 'iconMultilineText':
        return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.BLOCK_TYPES.TEXT');
      case 'image':
        return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.BLOCK_TYPES.IMAGE');
      case 'companyLogo':
        return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.BLOCK_TYPES.COMPANY_LOGO');
      case 'qrCode':
        return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.BLOCK_TYPES.QR_CODE');
      case 'icon':
        return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.BLOCK_TYPES.ICON');
      case 'line':
        return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.BLOCK_TYPES.LINE');
      case 'rectangle':
        return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.BLOCK_TYPES.RECTANGLE');
      case 'footer':
        return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.BLOCK_TYPES.FOOTER');
      default:
        return type;
    }
  }

  getSelectedBlockDisplayName(block: QrPosterBlock): string {
    if (block.type === 'icon') {
      const iconLabel = this.getIconOptionLabel(block.content?.icon);
      return iconLabel
        ? `${this.getBlockTypeLabel(block.type)}: ${iconLabel}`
        : this.getBlockTypeLabel(block.type);
    }
    return this.getBlockTypeLabel(block.type);
  }

  getSelectedBlockSecondaryLabel(block: QrPosterBlock): string {
    if (block.type === 'image') {
      return this.selectedImageFileName || '';
    }
    return '';
  }

  private applyTemplateState(state: QrPosterTemplateState): void {
    this.clearSimplePosterPreviewSession();
    this.userTemplates = (state.templates || []).map((template) => ({
      ...template,
      template: this.getNormalizedTemplateForSave(this.normalizeRuntimeTemplate(this.cloneTemplate(template.template))),
    }));
    this.activeTemplateId = state.activeTemplateId || null;
    this.savedTemplate = state.activeTemplate ? this.getNormalizedTemplateForSave(this.normalizeRuntimeTemplate(this.cloneTemplate(state.activeTemplate))) : null;
    this.savedActiveTemplateFromBackend = this.savedTemplate ? this.cloneTemplate(this.savedTemplate) : null;

    if (this.savedActiveTemplateFromBackend && this.activeTemplateId) {
      this.selectedSystemTemplateId = `user:${this.activeTemplateId}`;
      this.applyWorkingTemplate(this.savedActiveTemplateFromBackend, this.selectedSystemTemplateId);
      return;
    }

    if (this.userTemplates.length) {
      const firstUserTemplate = this.userTemplates[0];
      this.selectedSystemTemplateId = `user:${firstUserTemplate.id}`;
      this.applyWorkingTemplate(firstUserTemplate.template, this.selectedSystemTemplateId);
      return;
    }

    const fallbackTemplate = this.getSystemTemplate('scanafy-classic')?.qrPosterTemplate || QR_POSTER_SYSTEM_TEMPLATES_FALLBACK[0].qrPosterTemplate;
    this.selectedSystemTemplateId = 'system:scanafy-classic';
    this.applyWorkingTemplate(fallbackTemplate, this.selectedSystemTemplateId);
    const fallbackSystemTemplate = this.getSystemTemplate('scanafy-classic');
    const defaultVariant = fallbackSystemTemplate?.variants?.[0];
    if (defaultVariant) {
      this.selectedVariantId = defaultVariant.id;
      this.applyVariantColors(defaultVariant);
      if (this.workingTemplate) {
        this.loadedTemplateBaseline = this.cloneTemplate(this.workingTemplate);
        this.hasUnsavedAdvancedChangesFlag = false;
        this.advancedStatusLabelText = this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.STATUS_SAVED');
      }
    }
  }

  private extractTemplateId(optionId: string): string {
    return optionId.includes(':') ? optionId.split(':')[1] : optionId;
  }

  private getUserTemplateByOptionId(optionId: string): SavedQrPosterTemplate | undefined {
    const templateId = this.extractTemplateId(optionId);
    return this.userTemplates.find((template) => template.id === templateId);
  }

  private getSelectedUserTemplate(): SavedQrPosterTemplate | undefined {
    return this.selectedSystemTemplateId.startsWith('user:')
      ? this.getUserTemplateByOptionId(this.selectedSystemTemplateId)
      : undefined;
  }

  private getDefaultTemplateNameForSave(): string {
    const baseTemplateName = this.getTemplateName(this.selectedSystemTemplateId).replace(/\s·\s.*$/, '').trim();
    return this.generateUniqueTemplateName(baseTemplateName);
  }

  private generateUniqueTemplateName(baseName: string): string {
    const copyLabel = this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.TEMPLATE_COPY_SUFFIX');
    let candidate = `${baseName} ${copyLabel}`.trim();
    if (!this.isTemplateNameDuplicate(candidate, undefined)) {
      return candidate;
    }
    for (let i = 2; i <= 99; i++) {
      candidate = `${baseName} ${copyLabel} ${i}`.trim();
      if (!this.isTemplateNameDuplicate(candidate, undefined)) {
        return candidate;
      }
    }
    return `${baseName} ${copyLabel} ${Date.now()}`.trim();
  }

  private normalizeTemplateNameForComparison(name: string): string {
    return (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private isTemplateNameDuplicate(name: string, excludeTemplateId?: string): boolean {
    const normalized = this.normalizeTemplateNameForComparison(name);
    if (!normalized) {
      return false;
    }
    return this.userTemplates.some((template) =>
      template.id !== (excludeTemplateId || '')
      && this.normalizeTemplateNameForComparison(template.name) === normalized
    );
  }

  onTemplateNameDraftChange(value: string): void {
    this.pendingTemplateNameDraft = value;
    this.templateNameValidationError = this.validateTemplateName(value, undefined);
  }

  private validateTemplateName(name: string, excludeTemplateId?: string): string {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.NAME_ERROR_REQUIRED');
    }
    if (trimmed.length > 80) {
      return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.NAME_ERROR_TOO_LONG');
    }
    if (this.isTemplateNameDuplicate(trimmed, excludeTemplateId)) {
      return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.NAME_ERROR_DUPLICATE');
    }
    return '';
  }

  private openTemplateNameModal(mode: 'save' | 'save-and-activate'): void {
    if (!this.templateNameModalContentRef) {
      return;
    }
    this.pendingTemplateSaveMode = mode;
    this.pendingTemplateNameDraft = this.getDefaultTemplateNameForSave();
    this.templateNameValidationError = '';
    this.modal.create({
      nzTitle: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.NAME_TEMPLATE_TITLE'),
      nzContent: this.templateNameModalContentRef,
      nzClosable: true,
      nzOkText: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.SAVE_TEMPLATE'),
      nzCancelText: this.translate.instant('COMMON.CANCEL'),
      nzOnOk: () => {
        const validationError = this.validateTemplateName(this.pendingTemplateNameDraft, undefined);
        if (validationError) {
          this.templateNameValidationError = validationError;
          return false;
        }
        this.templateNameValidationError = '';
        this.persistAdvancedTemplate({
          name: this.pendingTemplateNameDraft,
          setActive: false,
          onSuccess: (savedTemplate) => {
            if (mode === 'save-and-activate') {
              this.setTemplateAsActive(savedTemplate.id);
            }
          },
        });
        return true;
      },
      nzOnCancel: () => {
        this.templateNameValidationError = '';
      },
    });
  }

  openRenameTemplateModal(template: SavedQrPosterTemplate): void {
    if (!this.renameTemplateModalContentRef || this.renameModalRef) {
      return;
    }
    this.pendingRenameTemplate = template;
    this.pendingRenameNameDraft = template.name;
    this.renameNameValidationError = '';
    this.renameModalRef = this.modal.create({
      nzTitle: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.RENAME_TEMPLATE_TITLE'),
      nzContent: this.renameTemplateModalContentRef,
      nzClosable: true,
      nzMaskClosable: false,
      nzOkText: this.translate.instant('COMMON.SAVE'),
      nzCancelText: this.translate.instant('COMMON.CANCEL'),
      nzOnOk: () => {
        const validationError = this.validateTemplateName(this.pendingRenameNameDraft, template.id);
        if (validationError) {
          this.renameNameValidationError = validationError;
          return false;
        }
        this.renameNameValidationError = '';
        this.executeRename(template, this.pendingRenameNameDraft.trim());
        return true;
      },
      nzOnCancel: () => {
        this.renameNameValidationError = '';
        this.pendingRenameTemplate = null;
      },
    });
    this.renameModalRef.afterClose.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.renameModalRef = null;
    });
  }

  onRenameNameDraftChange(value: string): void {
    this.pendingRenameNameDraft = value;
    this.renameNameValidationError = this.validateTemplateName(value, this.pendingRenameTemplate?.id);
  }

  private executeRename(template: SavedQrPosterTemplate, newName: string): void {
    this.companyService.saveQrPosterTemplate({
      qrPosterTemplate: template.template,
      templateId: template.id,
      name: newName,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (state) => {
          this.applyTemplateState(state);
          this.message.success(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.RENAME_SUCCESS'));
        },
        error: (error) => {
          console.error('Failed to rename template', error);
          this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.RENAME_ERROR'));
        },
      });
  }

  private persistAdvancedTemplate(options: {
    templateId?: string;
    name?: string;
    setActive?: boolean;
    onSuccess?: (savedTemplate: SavedQrPosterTemplate) => void;
  }): void {
    if (!this.workingTemplate) {
      return;
    }
    this.isSavingTemplate = true;
    const payload = this.getNormalizedTemplateForSave(this.workingTemplate);
    this.companyService.saveQrPosterTemplate({
      qrPosterTemplate: payload,
      templateId: options.templateId,
      name: options.name,
      setActive: options.setActive === true,
    })
      .pipe(finalize(() => { this.isSavingTemplate = false; }), takeUntil(this.destroy$))
      .subscribe({
        next: (state) => {
          this.applyTemplateState(state);
          const savedTemplate = state.savedTemplate || this.userTemplates.find((template) => template.id === this.activeTemplateId) || null;
          if (savedTemplate) {
            this.selectedSystemTemplateId = `user:${savedTemplate.id}`;
            this.applyWorkingTemplate(savedTemplate.template, this.selectedSystemTemplateId);
            options.onSuccess?.(savedTemplate);
          }
          this.refreshAdvancedUiCache();
          this.message.success(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.SAVE_SUCCESS'));
        },
        error: (error) => {
          console.error('Failed to save QR poster template', error);
          const backendError = error?.error as any;
          if (backendError?.code === 'QR_POSTER_TEMPLATE_NAME_EXISTS') {
            this.templateNameValidationError = this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.NAME_ERROR_DUPLICATE');
            this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.NAME_ERROR_DUPLICATE'));
          } else if (backendError?.code === 'QR_POSTER_TEMPLATE_NAME_REQUIRED') {
            this.templateNameValidationError = this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.NAME_ERROR_REQUIRED');
            this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.NAME_ERROR_REQUIRED'));
          } else if (backendError?.code === 'QR_POSTER_TEMPLATE_NAME_TOO_LONG') {
            this.templateNameValidationError = this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.NAME_ERROR_TOO_LONG');
            this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.NAME_ERROR_TOO_LONG'));
          } else {
            this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.SAVE_ERROR'));
          }
        },
      });
  }

  private executeSetActive(templateId: string): void {
    this.isSavingTemplate = true;
    this.companyService.setActiveQrPosterTemplate(templateId)
      .pipe(finalize(() => { this.isSavingTemplate = false; }), takeUntil(this.destroy$))
      .subscribe({
        next: (state) => {
          this.applyTemplateState(state);
          if (state.activeTemplateId) {
            this.selectedSystemTemplateId = `user:${state.activeTemplateId}`;
            const activeEntry = this.userTemplates.find((t) => t.id === state.activeTemplateId);
            if (activeEntry) {
              this.applyWorkingTemplate(activeEntry.template, this.selectedSystemTemplateId);
            }
          }
          this.message.success(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.SET_ACTIVE_SUCCESS'));
        },
        error: (error) => {
          console.error('Failed to set active QR poster template', error);
          this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.SAVE_ERROR'));
        },
      });
  }

  private confirmAndExecuteSetActive(templateId: string): void {
    this.modal.confirm({
      nzTitle: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.SET_ACTIVE_CONFIRM_TITLE'),
      nzContent: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.SET_ACTIVE_CONFIRM_MESSAGE'),
      nzOkText: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.SET_ACTIVE_CONFIRM_OK'),
      nzCancelText: this.translate.instant('COMMON.CANCEL'),
      nzOnOk: () => this.executeSetActive(templateId),
    });
  }

  private setTemplateAsActive(templateId: string): void {
    if (!this.workingTemplate?.blocks.some((block) => block.type === 'qrCode')) {
      this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.QR_REQUIRED_FOR_ACTIVE'));
      return;
    }
    this.confirmAndExecuteSetActive(templateId);
  }

  setTemplateActiveFromList(template: SavedQrPosterTemplate): void {
    if (!template?.id || template.id === this.activeTemplateId || this.isSavingTemplate) {
      return;
    }

    if (!template.template?.blocks?.some((block) => block.type === 'qrCode')) {
      this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.QR_REQUIRED_FOR_ACTIVE'));
      return;
    }

    this.confirmAndExecuteSetActive(template.id);
  }

  duplicateUserTemplate(template: SavedQrPosterTemplate): void {
    if (!template?.template || this.isSavingTemplate) {
      return;
    }

    const runDuplicate = () => {
      const clonedTemplate = this.cloneTemplate(template.template);
      const uniqueName = this.generateUniqueTemplateName(template.name);
      this.applyWorkingTemplate(clonedTemplate);
      this.persistAdvancedTemplate({ name: uniqueName, setActive: false });
    };

    if (this.hasUnsavedAdvancedChanges) {
      this.modal.confirm({
        nzTitle: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.DUPLICATE_UNSAVED_TITLE'),
        nzContent: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.DUPLICATE_UNSAVED_MESSAGE'),
        nzOkText: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.DUPLICATE_UNSAVED_OK'),
        nzCancelText: this.translate.instant('COMMON.CANCEL'),
        nzOnOk: runDuplicate,
      });
      return;
    }

    runDuplicate();
  }

  async onDeleteUserTemplate(template: SavedQrPosterTemplate): Promise<void> {
    if (!template?.id) {
      return;
    }

    let affectedLandingProfileCount = 0;
    try {
      const usage = await firstValueFrom(this.companyService.getQrPosterTemplateLandingProfileUsage(template.id));
      affectedLandingProfileCount = usage.assignedLandingProfileCount || 0;
    } catch {
      this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.DELETE_PROFILE_USAGE_LOAD_ERROR'));
      return;
    }

    const isActive = template.id === this.activeTemplateId;

    if (isActive) {
      this.openActiveDeleteFallbackModal(template, affectedLandingProfileCount);
      return;
    }

    this.openNormalDeleteModal(template, affectedLandingProfileCount);
  }

  private getLandingProfileDeletionImpactMessage(affectedLandingProfileCount: number): string {
    if (!affectedLandingProfileCount) {
      return '';
    }

    return `\n\n${this.translate.instant(
      'QR_POSTER_TEMPLATE.BUILDER.DELETE_PROFILE_ASSIGNMENTS_MESSAGE',
      { count: affectedLandingProfileCount }
    )}`;
  }

  private openNormalDeleteModal(template: SavedQrPosterTemplate, affectedLandingProfileCount: number): void {
    this.modal.confirm({
      nzTitle: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.DELETE_CONFIRM_TITLE'),
      nzContent: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.DELETE_CONFIRM_MESSAGE')
        + this.getLandingProfileDeletionImpactMessage(affectedLandingProfileCount),
      nzOkText: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.DELETE_CONFIRM_OK'),
      nzCancelText: this.translate.instant('COMMON.CANCEL'),
      nzOkDanger: true,
      nzOnOk: () => this.executeDeleteTemplate(template.id),
    });
  }

  replacementActiveTemplateIdDraft = '';
  deletableActiveTemplateOptions: SavedQrPosterTemplate[] = [];

  private openActiveDeleteFallbackModal(template: SavedQrPosterTemplate, affectedLandingProfileCount: number): void {
    this.modal.confirm({
      nzTitle: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.DELETE_ACTIVE_FALLBACK_TITLE'),
      nzContent: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.DELETE_ACTIVE_FALLBACK_MESSAGE')
        + this.getLandingProfileDeletionImpactMessage(affectedLandingProfileCount),
      nzOkText: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.DELETE_ACTIVE_FALLBACK_OK'),
      nzCancelText: this.translate.instant('COMMON.CANCEL'),
      nzWidth: 560,
      nzOkDanger: true,
      nzOnOk: () => this.executeDeleteTemplate(template.id),
    });
  }

  private executeDeleteTemplate(templateId: string, replacementId?: string): void {
    this.isSavingTemplate = true;
    this.companyService.deleteQrPosterSavedTemplate(templateId, replacementId)
      .pipe(finalize(() => { this.isSavingTemplate = false; }), takeUntil(this.destroy$))
      .subscribe({
        next: (state: QrPosterTemplateState) => {
          if (state.templates || state.activeTemplateId !== undefined) {
            this.applyTemplateState(state as QrPosterTemplateState);
            if (!replacementId && state.activeTemplateId == null) {
              this.applySystemTemplateSelection('system:scanafy-classic');
            }
          } else {
            this.userTemplates = this.userTemplates.filter((t) => t.id !== templateId);
            if (this.activeTemplateId === templateId) {
              this.activeTemplateId = replacementId || null;
            }
            if (this.selectedSystemTemplateId === `user:${templateId}`) {
              if (this.activeTemplateId && this.userTemplates.some((t) => t.id === this.activeTemplateId)) {
                this.selectedSystemTemplateId = `user:${this.activeTemplateId}`;
                const activeEntry = this.userTemplates.find((t) => t.id === this.activeTemplateId);
                if (activeEntry) {
                  this.applyWorkingTemplate(activeEntry.template, this.selectedSystemTemplateId);
                }
              } else if (this.userTemplates.length) {
                this.selectedSystemTemplateId = `user:${this.userTemplates[0].id}`;
                this.applyWorkingTemplate(this.userTemplates[0].template, this.selectedSystemTemplateId);
              } else {
                this.applySystemTemplateSelection('system:scanafy-classic');
              }
            }
          }
          this.refreshAdvancedUiCache();
          const clearedLandingProfileCount = state.clearedLandingProfileCount || 0;
          this.message.success(this.translate.instant(
            clearedLandingProfileCount
              ? 'QR_POSTER_TEMPLATE.BUILDER.DELETE_SUCCESS_WITH_PROFILE_CLEANUP'
              : 'QR_POSTER_TEMPLATE.BUILDER.DELETE_SUCCESS',
            { count: clearedLandingProfileCount }
          ));
        },
        error: (error) => {
          console.error('Failed to delete QR poster template', error);
          const backendError = error?.error as any;
          if (backendError?.code === 'QR_POSTER_TEMPLATE_NO_REPLACEMENT' || backendError?.code === 'QR_POSTER_TEMPLATE_REPLACEMENT_REQUIRED') {
            this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.DELETE_ACTIVE_BLOCKED_MESSAGE'));
          } else {
            this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.DELETE_ERROR'));
          }
        },
      });
  }

  trackByField(_: number, field: PosterTextField): PosterTextField {
    return field;
  }

  trackByBlock(_: number, block: QrPosterBlock): string {
    return block.id;
  }

  private initialize(): void {
    this.isLoading = true;
    this.isInitializingBuilderHistory = true;
    this.systemTemplateCatalogueUnavailable = false;
    this.pendingSystemTemplateCatalogue = null;
    this.appliedSystemTemplateCatalogue = null;
    this.isCompanyStateReadyForSystemTemplates = false;
    this.canShowSignOrderTab = this.hasRequiredSignOrderPermissions();
    this.showSignOrderCta = false;
    this.signOrderUnavailable = false;
    this.catalogReason = null;
    this.signOrderAvailabilityResolved = false;
    this.signOrderAvailabilityFailed = false;
    this.signOrderAvailabilityRequestStarted = false;
    this.signOrderPropertiesResolved = false;
    this.signOrderCtaAttentionActive = false;
    this.signOrderCtaAttentionStarted = false;
    this.criticalPageLoadFailed = false;
    this.checkSignOrderAvailability();
    void this.qrPosterPdfPreviewService.preload().catch(() => undefined);
    this.initializeSupportingPageState();
    this.initializeCriticalPageState();
  }

  private initializeCriticalPageState(): void {
    forkJoin({
      company: this.companyService.getCompanyDetails(),
      posterTexts: this.companyService.getQrPosterTexts(),
      properties: this.propertyService.getProperties(),
      templateState: this.companyService.getQrPosterTemplateState(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ company, posterTexts, properties, templateState }) => {
          this.company = company;
          this.themeColor = company.themeColor || this.themeColor;
          this.posterTexts = normalizePosterTexts(posterTexts);
          const companyMode: AppMode = company.appMode === 'hospitality' ? 'hospitality' : 'emergency';
          this.applyAppMode(companyMode);
          this.previewCompanyName = company.name;
          this.previewCompanyPhone = this.formatGermanPhoneNumber(company.phone);
          this.previewCompanyWebsite = this.variableResolver.normalizeCompanyWebsite(company.website);
          this.previewCompanyAddress = company.address || '';
          this.previewCompanyDisplayEmail = company.displayEmail || company.email || '';
          this.hasProperties = Array.isArray(properties) && properties.length > 0;
          this.signOrderPropertiesResolved = true;
          this.startSignOrderCtaAttentionWhenEligible();
          this.previewPropertyId = this.hasProperties && properties[0]?._id ? String(properties[0]._id) : '';
          this.previewPropertyAddress = this.hasProperties ? properties[0].address : this.translate.instant('QR_POSTER_TEMPLATE.PLACEHOLDER_ADDRESS');
          this.previewQrValue = this.hasProperties && properties[0]?._id && properties[0]?.companyId
            ? (properties[0].landingPageUrl ?? `${DOMAIN}/landing-page/${properties[0].companyId}/${properties[0]._id}`)
            : DOMAIN;
          void this.updatePreviewQrDataUrl();

          this.isCompanyStateReadyForSystemTemplates = true;
          this.applyPendingSystemTemplateCatalogue();
          this.applyTemplateState(templateState);
          this.builderMode = this.savedActiveTemplateFromBackend ? 'advanced' : 'simple';
          this.refreshAdvancedUiCache();

          this.resetHistoryBaseline('initial-builder-load');
          this.isInitializingBuilderHistory = false;

          this.buildForm();
          this.loadLogo();
          this.isLoading = false;
          this.applySignOrderRouteState();
          if (this.builderMode === 'simple') {
            this.queueSimplePosterPreview(true);
          }
          setTimeout(() => {
            this.updateWorkspaceHeight();
            this.updateStageDimensions();
          }, 0);
        },
        error: (error) => {
          console.error('Failed to load QR poster page', error);
          this.message.error(this.translate.instant('QR_POSTER_TEMPLATE.LOAD_ERROR'));
          this.isLoading = false;
          this.isInitializingBuilderHistory = false;
          this.criticalPageLoadFailed = true;
          this.applySignOrderRouteState();
        },
      });
  }

  private initializeSupportingPageState(): void {
    this.companyService.getQrPosterSystemTemplates()
      .pipe(
        catchError(() => {
          this.systemTemplateCatalogueUnavailable = true;
          return of(QR_POSTER_SYSTEM_TEMPLATES_FALLBACK);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((systemTemplates) => {
        this.pendingSystemTemplateCatalogue = systemTemplates?.length
          ? systemTemplates
          : QR_POSTER_SYSTEM_TEMPLATES_FALLBACK;
        this.applyPendingSystemTemplateCatalogue();
      });
  }

  private applyPendingSystemTemplateCatalogue(): void {
    if (!this.isCompanyStateReadyForSystemTemplates) {
      return;
    }

    const systemTemplates = this.pendingSystemTemplateCatalogue || QR_POSTER_SYSTEM_TEMPLATES_FALLBACK;
    if (this.appliedSystemTemplateCatalogue === systemTemplates) {
      return;
    }

    this.applySystemTemplateCatalogue(systemTemplates);
    this.appliedSystemTemplateCatalogue = systemTemplates;
  }

  private applySystemTemplateCatalogue(systemTemplates: QrPosterSystemTemplateOption[]): void {
    this.systemTemplates = (systemTemplates?.length ? systemTemplates : QR_POSTER_SYSTEM_TEMPLATES_FALLBACK).map((template) => ({
      ...template,
      qrPosterTemplate: this.getLocalTemplateForPlan(this.resolveSystemTemplateSource(template.id, template.qrPosterTemplate)),
      variants: template.variants?.length ? template.variants : (QR_POSTER_SYSTEM_TEMPLATES_FALLBACK.find((t) => t.id === template.id)?.variants || undefined),
    }));
  }

  private applyAppMode(mode: AppMode): void {
    this.appMode = mode;
    this.refreshAdvancedUiCache();
  }

  private buildForm(): void {
    const normalized = normalizePosterTexts(this.posterTexts);
    const groupConfig: Record<string, FormGroup> = {};

    this.languages.forEach((lang) => {
      const values = normalized[lang] || normalized['de'];
      groupConfig[lang] = this.fb.group({
        title: [values.title, [Validators.required, Validators.maxLength(this.limits.title.hard)]],
        subtitle: [values.subtitle, [Validators.required, Validators.maxLength(this.limits.subtitle.hard)]],
        intro: [values.intro, [Validators.required, Validators.maxLength(this.limits.intro.hard)]],
        addressHeader: [values.addressHeader, [Validators.required, Validators.maxLength(this.limits.addressHeader.hard)]],
        companyHeader: [values.companyHeader, [Validators.required, Validators.maxLength(this.limits.companyHeader.hard)]],
        cta: [values.cta, [Validators.required, Validators.maxLength(this.limits.cta.hard)]],
        welcomeHeadline: [values.welcomeHeadline, [Validators.required, Validators.maxLength(this.limits.welcomeHeadline.hard)]],
        welcomeSubheading: [values.welcomeSubheading, [Validators.required, Validators.maxLength(this.limits.welcomeSubheading.hard)]],
        wifiSnippet: [values.wifiSnippet, [Validators.required, Validators.maxLength(this.limits.wifiSnippet.hard)]],
        houseRulesTeaser: [values.houseRulesTeaser, [Validators.required, Validators.maxLength(this.limits.houseRulesTeaser.hard)]],
      });
    });

    this.form = this.fb.group(groupConfig);
    this.lastSavedFormValue = this.getCurrentFormValue();

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.posterTexts = normalizePosterTexts(value as QrPosterTexts);
      this.refreshVariableTooltipCache();
      if (this.builderMode === 'simple') {
        this.queueSimplePosterPreview();
      }
    });
  }

  private async loadLogo(): Promise<void> {
    this.companyService.getCompanyLogoBlob()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (blob) => {
          if (!blob) {
            return;
          }
          const base64 = await this.convertBlobToBase64(blob);
          if (base64) {
            this.previewLogoUrl = base64;
          }
        },
      });
  }

  private buildFormValueFromPosterTexts(source: QrPosterTexts): PosterTemplateFormValue {
    const normalized = normalizePosterTexts(source);
    return {
      de: {
        title: normalized['de'].title,
        subtitle: normalized['de'].subtitle,
        intro: normalized['de'].intro,
        addressHeader: normalized['de'].addressHeader,
        companyHeader: normalized['de'].companyHeader,
        cta: normalized['de'].cta,
        welcomeHeadline: normalized['de'].welcomeHeadline,
        welcomeSubheading: normalized['de'].welcomeSubheading,
        wifiSnippet: normalized['de'].wifiSnippet,
        houseRulesTeaser: normalized['de'].houseRulesTeaser,
      },
      en: {
        title: normalized['en'].title,
        subtitle: normalized['en'].subtitle,
        intro: normalized['en'].intro,
        addressHeader: normalized['en'].addressHeader,
        companyHeader: normalized['en'].companyHeader,
        cta: normalized['en'].cta,
        welcomeHeadline: normalized['en'].welcomeHeadline,
        welcomeSubheading: normalized['en'].welcomeSubheading,
        wifiSnippet: normalized['en'].wifiSnippet,
        houseRulesTeaser: normalized['en'].houseRulesTeaser,
      },
    };
  }

  private get defaultFormValue(): PosterTemplateFormValue {
    return this.buildFormValueFromPosterTexts(DEFAULT_QR_POSTER_TEXTS);
  }

  private getCurrentFormValue(): PosterTemplateFormValue {
    return this.form.getRawValue() as PosterTemplateFormValue;
  }

  private areFormValuesEqual(a: PosterTemplateFormValue | null | undefined, b: PosterTemplateFormValue | null | undefined): boolean {
    return JSON.stringify(a ?? {}) === JSON.stringify(b ?? {});
  }

  private checkSignOrderAvailability(): void {
    if (this.signOrderAvailabilityRequestStarted) return;
    this.signOrderAvailabilityRequestStarted = true;

    if (!this.canShowSignOrderTab) {
      this.signOrderUnavailable = false;
      this.signOrderAvailabilityResolved = true;
      this.applySignOrderRouteState();
      return;
    }

    this.signOrderService.getCatalog()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (catalog) => {
          this.showSignOrderCta = catalog.available;
          this.signOrderUnavailable = !catalog.available;
          this.signOrderAvailabilityFailed = false;
          this.catalogReason = (catalog as any).reason || null;
          this.signOrderAvailabilityResolved = true;
          this.startSignOrderCtaAttentionWhenEligible();
          this.applySignOrderRouteState();
        },
        error: () => {
          this.showSignOrderCta = false;
          this.signOrderUnavailable = false;
          this.signOrderAvailabilityFailed = true;
          this.catalogReason = null;
          this.signOrderAvailabilityResolved = true;
          this.applySignOrderRouteState();
        },
      });
  }

  private hasRequiredSignOrderPermissions(): boolean {
    return this.authService.hasPermission('company.sign_orders.create')
      && this.authService.hasPermission('company.properties.read');
  }

  private startSignOrderCtaAttentionWhenEligible(): void {
    if (this.signOrderCtaAttentionStarted || this.isSignOrderCtaDisabled) return;
    this.signOrderCtaAttentionStarted = true;
    this.signOrderCtaAttentionActive = true;
  }

  private formatGermanPhoneNumber(phone?: string): string {
    if (!phone) {
      return '';
    }
    try {
      return this.dePhone.normalize(phone).display || phone;
    } catch {
      return phone;
    }
  }

  private async convertBlobToBase64(blob: Blob): Promise<string | null> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  }

  private async readFileAsDataUrl(file: File): Promise<string | null> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  }

  private getSystemTemplate(id: string): QrPosterSystemTemplateOption | undefined {
    return this.systemTemplates.find((template) => template.id === id);
  }

  private async resolveQrPosterAssetDataUrl(assetKey: string): Promise<string | null> {
    const normalizedKey = (assetKey || '').trim();
    if (!normalizedKey) {
      return null;
    }
    const cached = this.imageAssetPreviewCache.get(normalizedKey);
    if (cached) {
      return cached;
    }
    const blob = await lastValueFrom(this.companyService.getQrPosterAssetBlob(normalizedKey));
    if (!blob) {
      return null;
    }
    const base64 = await this.convertBlobToBase64(blob);
    if (base64) {
      this.imageAssetPreviewCache.set(normalizedKey, base64);
    }
    return base64;
  }

  private async resolveTemplateImageAssets(template: QrPosterTemplate | null): Promise<void> {
    if (!template?.blocks?.length) {
      return;
    }
    const imageBlocks = template.blocks.filter((block) => block.type === 'image' && block.content?.imageAssetKey);
    await Promise.all(imageBlocks.map(async (block) => {
      const currentImageUrl = block.content?.imageUrl || '';
      if (currentImageUrl.startsWith('data:image/')) {
        return;
      }
      const resolved = await this.resolveQrPosterAssetDataUrl(block.content?.imageAssetKey || '');
      if (resolved) {
        block.content = {
          ...(block.content || {}),
          imageUrl: resolved,
        };
      }
    }));
  }

  getTemplateName(id: string): string {
    if (id.startsWith('user:')) {
      const userTemplate = this.getUserTemplateByOptionId(id);
      if (!userTemplate) {
        return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.MY_TEMPLATES');
      }
      const baseName = userTemplate.name;
      return userTemplate.id === this.activeTemplateId
        ? `${baseName} · ${this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.ACTIVE_SHORT')}`
        : baseName;
    }
    switch (this.extractTemplateId(id)) {
      case 'scanafy-classic':
        return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.TEMPLATE_SCANAFY_CLASSIC_NAME');
      case 'emergency-notice':
      case 'info-board':
        return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.TEMPLATE_INFO_BOARD_NAME');
      case 'official-notice':
        return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.TEMPLATE_OFFICIAL_NOTICE_NAME');
      case 'scanafy-modern':
        return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.TEMPLATE_SCANAFY_MODERN_NAME');
      default:
        return this.getSystemTemplate(this.extractTemplateId(id))?.name || id;
    }
  }

  private applyVariantColors(variant: QrPosterSystemTemplateVariant): void {
    if (!this.workingTemplate) {
      return;
    }
    this.workingTemplate.blocks = applyVariantColorsToBlocks(this.workingTemplate.blocks, variant);
  }

  onSelectVariant(variantId: string): void {
    if (!this.workingTemplate || variantId === this.selectedVariantId) {
      return;
    }
    const extractedId = this.extractTemplateId(this.selectedSystemTemplateId);
    const variant = getSystemTemplateVariant(extractedId, variantId);
    if (!variant) {
      return;
    }
    this.recordPreMutationHistoryCheckpoint('variant-select');
    this.selectedVariantId = variantId;
    this.applyVariantColors(variant);
    this.themeColor = variant.colors.iconColor || variant.colors.accent || variant.colors.primary || this.themeColor;
    this.hasUnsavedAdvancedChangesFlag = true;
  }

  get selectedTemplateDescription(): string {
    if (this.selectedSystemTemplateId.startsWith('user:')) {
      return this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.MY_TEMPLATE_DESCRIPTION');
    }
    const extractedId = this.extractTemplateId(this.selectedSystemTemplateId);
    const template = this.getSystemTemplate(extractedId);
    return template?.description || this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.SYSTEM_TEMPLATE_DESCRIPTION');
  }

  get selectedTemplateVariants(): QrPosterSystemTemplateVariant[] | null {
    if (this.selectedSystemTemplateId.startsWith('user:')) {
      return null;
    }
    const extractedId = this.extractTemplateId(this.selectedSystemTemplateId);
    const template = this.getSystemTemplate(extractedId);
    return template?.variants || null;
  }

  getVariantDisplayName(variant: QrPosterSystemTemplateVariant): string {
    const key = 'QR_POSTER_TEMPLATE.BUILDER.VARIANT_' + variant.id.toUpperCase().replace(/-/g, '_');
    const translated = this.translate.instant(key);
    return translated !== key ? translated : variant.name;
  }

  private applyWorkingTemplate(template: QrPosterTemplate, optionId?: string): void {
    this.cancelInlineEdit(false);
    this.workingTemplate = this.getNormalizedTemplateForSave(this.getLocalTemplateForPlan(this.cloneTemplate(template)));
    this.workingTemplate.blocks = this.workingTemplate.blocks.map((block) => ({
      ...block,
      content: {
        ...(block.content || {}),
        ...(block.type === 'image' ? { fit: block.content?.fit || 'cover' } : {}),
      },
      style: block.style || {},
    }));
    this.loadedTemplateBaseline = this.cloneTemplate(this.workingTemplate);
    this.setSelectedBlockIds(this.workingTemplate.blocks[0] ? [this.workingTemplate.blocks[0].id] : []);
    this.selectedSystemTemplateId = optionId || this.selectedSystemTemplateId;
    void this.resolveTemplateImageAssets(this.workingTemplate);
    this.refreshAdvancedUiCache();
  }

  private getLocalTemplateForPlan(template: QrPosterTemplate): QrPosterTemplate {
    const next = this.cloneTemplate(template);
    if (!this.isBusinessPlan) {
      next.branding.showScanafyFooter = true;
      if (!next.blocks.some((block) => block.type === 'footer')) {
        next.blocks.push({
          id: 'footer-default',
          type: 'footer',
          x: 0,
          y: 195,
          width: 148,
          height: 15,
          locked: true,
          zIndex: 1000,
        });
      }
    }
    return next;
  }

  private enforceFooterLocally(): void {
    if (!this.workingTemplate) {
      return;
    }
    if (!this.isBusinessPlan) {
      this.workingTemplate.branding.showScanafyFooter = true;
      if (!this.workingTemplate.blocks.some((block) => block.type === 'footer')) {
        this.workingTemplate.blocks = [
          ...this.workingTemplate.blocks,
          {
            id: 'footer-default',
            type: 'footer',
            x: 0,
            y: 195,
            width: 148,
            height: 15,
            locked: true,
            zIndex: 1000,
          },
        ];
      }
    }
  }

  private cloneTemplate(template: QrPosterTemplate): QrPosterTemplate {
    return JSON.parse(JSON.stringify(template));
  }

  private cloneBlock(block: QrPosterBlock): QrPosterBlock {
    return JSON.parse(JSON.stringify(block));
  }

  private clampMm(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, Number.isFinite(value) ? value : min));
  }

  private shouldIgnoreBuilderShortcut(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;
    if (this.editingBlockId) {
      return true;
    }
    if (!target) {
      return false;
    }
    if (target.isContentEditable) {
      return true;
    }
    if (target.closest('input, textarea, select, [contenteditable="true"], .ant-input, .ant-input-number, .ant-select, .ant-color-picker, .template-name-modal')) {
      return true;
    }
    return false;
  }

  onWindowKeydown(event: KeyboardEvent): void {
    if (this.builderMode !== 'advanced' || !this.workingTemplate || this.shouldIgnoreBuilderShortcut(event)) {
      return;
    }
    const key = event.key.toLowerCase();
    if (key === 'delete' || key === 'backspace') {
      this.deleteSelectedBlock();
      event.preventDefault();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && key === 'z') {
      if (event.shiftKey) {
        this.redo();
      } else {
        this.undo();
      }
      event.preventDefault();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && key === 'y') {
      this.redo();
      event.preventDefault();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && key === 'c') {
      this.copySelectedBlocks();
      event.preventDefault();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && key === 'v') {
      this.pasteClipboardBlocks();
      event.preventDefault();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && key === 'x') {
      this.cutSelectedBlocks();
      event.preventDefault();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && key === 'd') {
      this.duplicateSelectedBlock();
      event.preventDefault();
      return;
    }
    if (key === 'escape') {
      if (this.editingBlockId) {
        this.cancelInlineEdit();
      }
    }
  }

  private copySelectedBlocks(): void {
    const blocks = this.selectedBlocks
      .filter((block) => !block.locked && block.type !== 'footer')
      .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    if (!blocks.length) {
      return;
    }
    this.clipboardBlocks = blocks.map((block) => this.cloneBlock(block));
    this.message.success(this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.COPY_SUCCESS', { count: blocks.length }));
  }

  private pasteClipboardBlocks(): void {
    if (!this.workingTemplate || !this.clipboardBlocks.length) {
      return;
    }

    this.recordPreMutationHistoryCheckpoint('paste-blocks');
    const currentMaxZ = this.workingTemplate.blocks.reduce((max, block) => Math.max(max, block.zIndex || 0), 0);
    let copies = this.clipboardBlocks.map((block, index) => {
      const copy = this.cloneBlock(block);
      copy.id = `${copy.id}-paste-${Date.now()}-${index}`;
      copy.x += 4;
      copy.y += 4;
      copy.zIndex = currentMaxZ + index + 1;
      return copy;
    });

    const bounds = this.getBoundsForBlocksConsideringRotation(copies);
    let adjustX = 0;
    let adjustY = 0;
    if (bounds.left < 0) {
      adjustX = -bounds.left;
    } else if (bounds.right > this.pageWidthMm) {
      adjustX = this.pageWidthMm - bounds.right;
    }
    if (bounds.top < 0) {
      adjustY = -bounds.top;
    } else if (bounds.bottom > this.pageHeightMm) {
      adjustY = this.pageHeightMm - bounds.bottom;
    }

    copies = copies.map((block) => this.clampBlock({
      ...block,
      x: block.x + adjustX,
      y: block.y + adjustY,
    }));

    this.workingTemplate.blocks = [...this.workingTemplate.blocks, ...copies];
    this.setSelectedBlockIds(copies.map((block) => block.id));
    this.refreshAdvancedUiCache();
  }

  private cutSelectedBlocks(): void {
    if (!this.workingTemplate || !this.canDeleteSelectedBlock) {
      return;
    }
    const blocks = this.selectedBlocks.filter((block) => !block.locked && (this.isBusinessPlan || block.type !== 'footer'));
    if (!blocks.length) {
      return;
    }
    this.recordPreMutationHistoryCheckpoint('cut-blocks');
    this.clipboardBlocks = blocks.map((block) => this.cloneBlock(block));
    const deletableIds = new Set(blocks.map((block) => block.id));
    this.workingTemplate.blocks = this.workingTemplate.blocks.filter((block) => !deletableIds.has(block.id));
    this.setSelectedBlockIds(this.workingTemplate.blocks[0] ? [this.workingTemplate.blocks[0].id] : []);
    this.enforceFooterLocally();
    this.refreshAdvancedUiCache();
  }

  private updateStageDimensions(): void {
    const element = this.canvasStageRef?.nativeElement;
    if (!element) {
      return;
    }
    const rect = element.getBoundingClientRect();
    const nextStageWidth = Math.max(360, Math.round(rect.width - 24));
    const nextStageHeight = Math.max(520, Math.round(rect.height - 24));
    if (nextStageWidth !== this.stageWidthPx) {
      this.stageWidthPx = nextStageWidth;
    }
    if (nextStageHeight !== this.stageHeightPx) {
      this.stageHeightPx = nextStageHeight;
    }
  }

  private updateWorkspaceHeight(): void {
    const nextWorkspaceHeight = Math.max(640, Math.min(980, window.innerHeight - 180));
    if (nextWorkspaceHeight !== this.workspaceHeightPx) {
      this.workspaceHeightPx = nextWorkspaceHeight;
    }
  }

  private requiresTemplateReplacementConfirmation(templateId: string): boolean {
    if (!this.workingTemplate || templateId === this.selectedSystemTemplateId) {
      return false;
    }
    return this.hasUnsavedAdvancedChanges;
  }

  private addCenteredVariableBlock(variable: string): void {
    if (!this.workingTemplate) {
      return;
    }
    this.recordPreMutationHistoryCheckpoint('variable-insert-centered');
    const block = this.clampBlock({
      id: `text-${Date.now()}`,
      type: 'text',
      x: (this.pageWidthMm - 70) / 2,
      y: (this.pageHeightMm - 12) / 2,
      width: 70,
      height: 12,
      content: { text: variable },
      style: {
        fontSize: 10,
        textAlign: 'center',
        color: '#0f172a',
      },
      zIndex: 100,
    });
    this.workingTemplate.blocks = [...this.workingTemplate.blocks, block];
    this.setSelectedBlockIds([block.id]);
    this.refreshAdvancedUiCache();
  }

  private suppressHistoryUntil = 0;

  undo(): void {
    this.finishContentEditSession('undo');
    this.finishStyleEditSession('undo');
    if (!this.canUndo || !this.workingTemplate) {
      return;
    }
    const currentFp = this.getHistoryDesignFingerprint(this.workingTemplate);

    let previous = this.undoStack.pop();
    while (previous) {
      const targetFp = this.getHistoryDesignFingerprint(previous.template);
      if (targetFp !== currentFp) { break; }
      previous = this.undoStack.pop();
    }
    if (!previous) {
      return;
    }

    this.isRestoringHistory = true;
    this.redoStack.push(this.createHistoryEntry());
    this.lastHistoryFingerprint = this.getHistoryDesignFingerprint(previous.template);
    this.suppressHistoryUntil = Date.now() + 150;

    this.auditHistory({
      type: 'undo',
      actionId: this.nextHistoryActionId('undo'),
      reason: 'undo',
      beforeFingerprint: this.shortFp(currentFp),
      afterFingerprint: this.shortFp(this.lastHistoryFingerprint),
      diff: this.getDesignDiffSummary(this.workingTemplate, previous.template),
      undoLength: this.undoStack.length,
      redoLength: this.redoStack.length,
      blockIds: this.getBlockIdsFromDiff(this.getDesignDiffSummary(this.workingTemplate, previous.template), [...this.selectedBlockIds]),
    });

    this.applyHistoryEntry(previous);
    this.isRestoringHistory = false;
  }

  redo(): void {
    this.finishContentEditSession('redo');
    this.finishStyleEditSession('redo');
    if (!this.canRedo || !this.workingTemplate) {
      return;
    }
    const currentFp = this.getHistoryDesignFingerprint(this.workingTemplate);

    let next = this.redoStack.pop();
    while (next) {
      const targetFp = this.getHistoryDesignFingerprint(next.template);
      if (targetFp !== currentFp) { break; }
      next = this.redoStack.pop();
    }
    if (!next) {
      return;
    }

    this.isRestoringHistory = true;
    this.undoStack.push(this.createHistoryEntry());
    this.lastHistoryFingerprint = this.getHistoryDesignFingerprint(next.template);
    this.suppressHistoryUntil = Date.now() + 150;

    this.auditHistory({
      type: 'redo',
      actionId: this.nextHistoryActionId('redo'),
      reason: 'redo',
      beforeFingerprint: this.shortFp(currentFp),
      afterFingerprint: this.shortFp(this.lastHistoryFingerprint),
      diff: this.getDesignDiffSummary(this.workingTemplate, next.template),
      undoLength: this.undoStack.length,
      redoLength: this.redoStack.length,
      blockIds: this.getBlockIdsFromDiff(this.getDesignDiffSummary(this.workingTemplate, next.template), [...this.selectedBlockIds]),
    });

    this.applyHistoryEntry(next);
    this.isRestoringHistory = false;
  }

  private getNormalizedTemplateForSave(template: QrPosterTemplate): QrPosterTemplate {
    const normalized = this.normalizeRuntimeTemplate(this.cloneTemplate(template));
    normalized.blocks = (normalized.blocks || [])
      .map((block) => {
        const content = { ...(block.content || {}) };
        if (block.type === 'image' && content.imageAssetKey) {
          delete content.imageUrl;
        }
        return this.clampBlock({
          ...block,
          content,
          style: block.style || {},
        });
      })
      .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    return this.getLocalTemplateForPlan(normalized);
  }

  private normalizeRuntimeTemplate(template: QrPosterTemplate): QrPosterTemplate {
    const normalized = this.cloneTemplate(template);
    normalized.blocks = (normalized.blocks || []).filter((block) => {
      if (normalized.selectedTemplateId !== 'scanafy-classic') {
        return true;
      }
      return block.id !== 'cta-arrow';
    });
    const qrFrame = normalized.selectedTemplateId === 'scanafy-classic'
      ? normalized.blocks.find((block) => block.id === 'qr-frame' && block.type === 'rectangle')
      : null;
    if (qrFrame?.style?.borderWidth === 3 && normalized.source !== 'custom') {
      qrFrame.style = { ...(qrFrame.style || {}), borderWidth: 0.8 };
    }
    return normalized;
  }

  private clampBlock(block: QrPosterBlock): QrPosterBlock {
    const next = this.cloneBlock(block);
    const minSize = this.getMinBlockSize(next.type);
    next.width = this.clampMm(next.width ?? minSize.width, minSize.width, this.pageWidthMm);
    next.height = this.clampMm(next.height ?? minSize.height, minSize.height, this.pageHeightMm);
    next.x = Number.isFinite(next.x) ? next.x : 0;
    next.y = Number.isFinite(next.y) ? next.y : 0;
    next.content = next.content || {};
    next.style = this.normalizeBlockStyle(next.style || {});
    next.rotation = this.normalizeRotation(next.rotation || 0);
    if (next.type === 'line') {
      next.height = this.clampMm(next.height || 30, 0.1, this.pageHeightMm);
      next.width = this.clampMm(next.width || 30, 0.1, this.pageWidthMm);
    }

    const rotatedBounds = this.getRotatedBlockBounds(next);
    if (rotatedBounds.left < 0) {
      next.x += -rotatedBounds.left;
    } else if (rotatedBounds.right > this.pageWidthMm) {
      next.x -= rotatedBounds.right - this.pageWidthMm;
    }

    if (rotatedBounds.top < 0) {
      next.y += -rotatedBounds.top;
    } else if (rotatedBounds.bottom > this.pageHeightMm) {
      next.y -= rotatedBounds.bottom - this.pageHeightMm;
    }
    return next;
  }

  private normalizeBlockStyle(style: QrPosterBlock['style'] | null | undefined): NonNullable<QrPosterBlock['style']> {
    const next = { ...(style || {}) } as NonNullable<QrPosterBlock['style']>;
    next.textFit = normalizeQrPosterTextFit(next.textFit, 'wrap');
    return next;
  }

  private setSelectedBlockIds(ids: string[]): void {
    const nextIds = Array.from(new Set(ids.filter(Boolean)));
    if (this.selectedBlockId !== (nextIds[0] || null)) {
      this.finishContentEditSession('selected-block-changed');
      this.finishStyleEditSession('selected-block-changed');
    }
    this.selectedBlockIds = nextIds;
    this.selectedBlockId = nextIds[0] || null;
    if (this.editingBlockId && !nextIds.includes(this.editingBlockId)) {
      this.cancelInlineEdit();
    }
  }

  private getHistoryDesignFingerprint(template: QrPosterTemplate): string {
    return JSON.stringify(this.createCanonicalHistoryTemplate(template));
  }

  private createCanonicalHistoryTemplate(template: QrPosterTemplate): QrPosterTemplate {
    const clone = this.cloneTemplate(template);
    clone.blocks = (clone.blocks || [])
      .map((block) => {
        const content = block.type === 'image'
          ? this.canonicalizeImageContent(block.content)
          : { ...(block.content || {}) };
        return this.clampBlock({
          ...block,
          content,
          style: block.style || {},
        });
      })
      .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0) || a.id.localeCompare(b.id));
    return clone;
  }

  private resetHistoryBaseline(reason: string): void {
    this.undoStack = [];
    this.redoStack = [];
    if (this.workingTemplate) {
      this.lastHistoryFingerprint = this.getHistoryDesignFingerprint(this.workingTemplate);
    } else {
      this.lastHistoryFingerprint = '';
    }
    this.clearInteractionCheckpointState(reason);

    this.auditHistory({
      type: 'baseline-reset',
      actionId: this.nextHistoryActionId(reason),
      reason,
      beforeFingerprint: this.shortFp(this.lastHistoryFingerprint),
      undoLength: this.undoStack.length,
      redoLength: this.redoStack.length,
    });
  }

  private saveInteractionCheckpointTemplate(): void {
    if (this.workingTemplate) {
      this.interactionCheckpointTemplate = this.createCanonicalHistoryTemplate(this.workingTemplate);
      this.interactionBaselineFingerprint = this.getHistoryDesignFingerprint(this.interactionCheckpointTemplate);
      if (this.debugQrPosterHistory) {
        console.debug('[QR Builder][Interaction Baseline Captured]', {
          reason: this.interactionCheckpointReason,
          baselineFingerprint: this.interactionBaselineFingerprint.substring(0, 80),
        });
      }
    }
  }

  private clearInteractionCheckpointState(reason: string): void {
    this.interactionCheckpointTemplate = null;
    this.interactionBaselineFingerprint = '';
    this.interactionCheckpointReason = null;
    this.interactionCheckpointBlockIds = [];
  }

  private tryCommitInteractionCheckpoint(): void {
    if (!this.interactionCheckpointTemplate || !this.workingTemplate) {
      return;
    }
    const reason = this.interactionCheckpointReason || 'unknown';
    const finalFingerprint = this.getHistoryDesignFingerprint(this.workingTemplate);
    const fingerprintChanged = finalFingerprint !== this.interactionBaselineFingerprint;
    const diff = fingerprintChanged ? this.getDesignDiffSummary(this.interactionCheckpointTemplate, this.workingTemplate) : undefined;
    const hasVisualDiff = !!diff && (
      diff.addedBlockIds.length > 0 ||
      diff.removedBlockIds.length > 0 ||
      diff.changedBlocks.length > 0 ||
      diff.changedTemplateFields.length > 0
    );
    const changed = fingerprintChanged && hasVisualDiff;

    this.auditHistory({
      type: 'interaction-end',
      actionId: this.currentHistoryActionId,
      reason,
      blockIds: this.interactionCheckpointBlockIds.length ? [...this.interactionCheckpointBlockIds] : [],
      beforeFingerprint: this.shortFp(this.interactionBaselineFingerprint),
      afterFingerprint: this.shortFp(finalFingerprint),
      changed,
      diff: changed ? diff : undefined,
      undoLength: this.undoStack.length,
      redoLength: this.redoStack.length,
    });

    if (!changed) {
      if (fingerprintChanged && !hasVisualDiff) {
        this.auditHistory({
          type: 'history-skip',
          actionId: this.currentHistoryActionId,
          reason: `interaction-${reason}`,
          source: 'tryCommitInteractionCheckpoint',
          skipped: true,
          skipReason: 'empty-diff',
          blockIds: this.interactionCheckpointBlockIds.length ? [...this.interactionCheckpointBlockIds] : [],
          extra: { interactionReason: reason },
        });
      }
      this.clearInteractionCheckpointState(reason);
      return;
    }
    const entry: HistoryEntry = {
      template: this.interactionCheckpointTemplate,
      selectedBlockIds: [...this.selectedBlockIds],
    };
    this.pushHistoryEntryDirect(entry, `interaction-${reason}`);
    this.clearInteractionCheckpointState(reason);
    if (this.workingTemplate) {
      this.lastHistoryFingerprint = this.getHistoryDesignFingerprint(this.workingTemplate);
    }
  }

  private getActiveInteractionReason(): string {
    if (this.dragState) { return 'drag'; }
    if (this.resizeState) { return 'resize'; }
    if (this.rotateState) { return 'rotate'; }
    return 'unknown';
  }

  private pushHistoryEntryDirect(entry: HistoryEntry, reason = 'direct-push'): boolean {
    if (this.isInitializingBuilderHistory || this.isRestoringHistory || Date.now() < this.suppressHistoryUntil) {
      return false;
    }
    if (!reason || reason === 'missing-reason') {
      this.auditHistory({
        type: 'warning',
        reason: 'missing-history-reason',
        source: 'pushHistoryEntryDirect',
        skipped: true,
        skipReason: 'missing-reason-not-allowed',
        undoLength: this.undoStack.length,
        redoLength: this.redoStack.length,
        extra: this.getHistoryAuditContext(reason),
      });
      return false;
    }
    const fp = this.getHistoryDesignFingerprint(entry.template);
    const top = this.undoStack[this.undoStack.length - 1];
    const topFp = top ? this.getHistoryDesignFingerprint(top.template) : '';
    const skipped = fp === topFp;

    this.auditHistory({
      type: skipped ? 'history-skip' : 'history-push',
      actionId: this.nextHistoryActionId(reason),
      reason,
      source: 'pushHistoryEntryDirect',
      candidateFingerprint: this.shortFp(fp),
      topUndoFingerprint: this.shortFp(topFp),
      lastHistoryFingerprint: this.shortFp(this.lastHistoryFingerprint),
      undoLength: this.undoStack.length,
      redoLength: this.redoStack.length,
      skipped,
      skipReason: skipped ? 'matches-top-undo-stack' : undefined,
      pushed: !skipped,
    });

    if (skipped) {
      return false;
    }
    this.undoStack.push(entry);
    if (this.undoStack.length > this.historyLimit) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    this.lastHistoryFingerprint = fp;
    return true;
  }

  /* ---------- History Audit Trace ---------- */

  private get auditEnabled(): boolean {
    return this.debugQrPosterHistoryAudit || !!(window as any).__qrPosterHistoryAuditEnabled;
  }

  private auditHistory(event: QrPosterHistoryAuditEvent): void {
    if (!this.auditEnabled) { return; }
    event.seq = ++this.qrPosterHistoryAuditSeq;
    event.ts = new Date().toISOString();
    const actionId = event.actionId || this.currentHistoryActionId;
    if (actionId) { event.actionId = actionId; }
    this.qrPosterHistoryAuditTrace.push(event);
    if (this.qrPosterHistoryAuditTrace.length > this.qrPosterHistoryAuditMaxEvents) {
      this.qrPosterHistoryAuditTrace.shift();
    }
  }

  private nextHistoryActionId(reason: string): string {
    const id = `${Date.now()}-${++this.qrPosterHistoryAuditSeq}-${reason}`;
    this.currentHistoryActionId = id;
    return id;
  }

  private getDesignDiffSummary(before: QrPosterTemplate | null, after: QrPosterTemplate | null): QrPosterDesignDiffSummary | undefined {
    if (!before || !after) { return undefined; }
    const beforeBlockMap = new Map((before.blocks || []).map((b) => [b.id, b]));
    const afterBlockMap = new Map((after.blocks || []).map((b) => [b.id, b]));
    const addedBlockIds: string[] = [];
    const removedBlockIds: string[] = [];
    const changedBlocks: QrPosterDesignDiffSummary['changedBlocks'] = [];
    const beforeIds = new Set(beforeBlockMap.keys());
    const afterIds = new Set(afterBlockMap.keys());
    for (const id of afterIds) {
      if (!beforeIds.has(id)) { addedBlockIds.push(id); }
    }
    for (const id of beforeIds) {
      if (!afterIds.has(id)) { removedBlockIds.push(id); }
    }
    for (const id of beforeIds) {
      const b = beforeBlockMap.get(id);
      const a = afterBlockMap.get(id);
      if (!b || !a) { continue; }
      const changedFields: string[] = [];
      const geoFields: Array<keyof QrPosterBlock> = ['x', 'y', 'width', 'height', 'rotation', 'zIndex'];
      for (const f of geoFields) {
        const bv = this.roundAudit(b[f] as number);
        const av = this.roundAudit(a[f] as number);
        if (bv !== av) { changedFields.push(f); }
      }
      if (b.type !== a.type) { changedFields.push('type'); }
      if (JSON.stringify(this.canonicalizeImageContent(b.content)) !== JSON.stringify(this.canonicalizeImageContent(a.content))) { changedFields.push('content'); }
      if (JSON.stringify(b.style) !== JSON.stringify(a.style)) { changedFields.push('style'); }
      if (b.locked !== a.locked) { changedFields.push('locked'); }
      if (changedFields.length) {
        changedBlocks.push({ id, type: a.type, changedFields });
      }
    }
    const changedTemplateFields: string[] = [];
    if (before.branding?.showScanafyFooter !== after.branding?.showScanafyFooter) { changedTemplateFields.push('branding.showScanafyFooter'); }

    const beforeSortedIds = (before.blocks || []).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0) || a.id.localeCompare(b.id)).map((b) => b.id);
    const afterSortedIds = (after.blocks || []).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0) || a.id.localeCompare(b.id)).map((b) => b.id);
    const relativeOrderSame = JSON.stringify(beforeSortedIds) === JSON.stringify(afterSortedIds);

    if (relativeOrderSame) {
      for (let i = changedBlocks.length - 1; i >= 0; i--) {
        const cb = changedBlocks[i];
        const nonZFields = cb.changedFields.filter((f) => f !== 'zIndex');
        if (nonZFields.length === 0) {
          changedBlocks.splice(i, 1);
        } else {
          cb.changedFields = nonZFields;
        }
      }
    }

    return {
      blockCountBefore: (before.blocks || []).length,
      blockCountAfter: (after.blocks || []).length,
      addedBlockIds,
      removedBlockIds,
      changedBlocks,
      changedTemplateFields,
    };
  }

  private roundAudit(n: number | undefined | null): number {
    if (n == null) { return 0; }
    return Math.round(n * 100) / 100;
  }

  private canonicalizeImageContent(content: Record<string, unknown> | null | undefined): Record<string, unknown> {
    if (!content) { return {}; }
    const next = { ...content };
    delete next['objectUrl'];
    delete next['previewUrl'];
    delete next['dimensions'];
    delete next['naturalWidth'];
    delete next['naturalHeight'];
    delete next['file'];
    delete next['blob'];
    delete next['blobUrl'];
    delete next['lastModified'];
    delete next['size'];
    delete next['mimeType'];
    delete next['base64'];
    if (next['imageAssetKey'] || next['assetId'] || next['uploadedImageId']) {
      delete next['imageUrl'];
      delete next['dataUrl'];
    }
    return next;
  }

  private shortFp(fp: string): string {
    return fp ? fp.substring(0, 60) : '';
  }

  private getBlockIdsFromDiff(diff: QrPosterDesignDiffSummary | undefined, fallback: string[]): string[] {
    if (!diff) { return fallback; }
    const ids = new Set<string>();
    for (const cb of diff.changedBlocks) { ids.add(cb.id); }
    for (const id of diff.addedBlockIds) { ids.add(id); }
    for (const id of diff.removedBlockIds) { ids.add(id); }
    return ids.size > 0 ? Array.from(ids) : fallback;
  }

  private getHistoryAuditContext(source?: string): Record<string, unknown> {
    return {
      source,
      selectedBlockIds: [...this.selectedBlockIds],
      selectedBlockId: this.selectedBlockId,
      activeInlineEditBlockId: this.editingBlockId || undefined,
      hasDragState: !!this.dragState,
      hasResizeState: !!this.resizeState,
      hasRotateState: !!this.rotateState,
      currentHistoryActionId: this.currentHistoryActionId,
      stack: this.auditEnabled ? new Error().stack?.split('\n').slice(2, 8) : undefined,
    };
  }

  /* ---------- Pre-Mutation History Checkpoint ---------- */

  private recordPreMutationHistoryCheckpoint(reason = 'missing-reason', source = reason): boolean {
    if (!this.workingTemplate || this.isInitializingBuilderHistory || this.isRestoringHistory || Date.now() < this.suppressHistoryUntil) {
      return false;
    }
    if (!reason || reason === 'missing-reason') {
      this.auditHistory({
        type: 'warning',
        reason: 'missing-history-reason',
        source: `pre-mutation:${source}`,
        skipped: true,
        skipReason: 'missing-reason-not-allowed',
        undoLength: this.undoStack.length,
        redoLength: this.redoStack.length,
        extra: this.getHistoryAuditContext(source),
      });
      return false;
    }
    const snapshot = this.createCanonicalHistoryTemplate(this.workingTemplate);
    const fingerprint = this.getHistoryDesignFingerprint(snapshot);

    if (this.lastPreMutationPush
      && this.lastPreMutationPush.reason === reason
      && Date.now() - this.lastPreMutationPush.time < 300) {
      if (fingerprint === this.lastPreMutationPush.fingerprint) {
        return false;
      }
    }
    const top = this.undoStack[this.undoStack.length - 1];
    const topFp = top ? this.buildTemplateSignature(top.template) : '';

    if (fingerprint === topFp) {
      this.auditHistory({
        type: 'history-skip',
        actionId: this.nextHistoryActionId(reason),
        reason,
        source: `pre-mutation:${source}`,
        candidateFingerprint: this.shortFp(fingerprint),
        topUndoFingerprint: this.shortFp(topFp),
        undoLength: this.undoStack.length,
        redoLength: this.redoStack.length,
        skipped: true,
        skipReason: 'matches-topUndoFingerprint',
      });
      return false;
    }

    const entry: HistoryEntry = {
      template: snapshot,
      selectedBlockIds: [...this.selectedBlockIds],
    };
    this.undoStack.push(entry);
    if (this.undoStack.length > this.historyLimit) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    this.lastPreMutationPush = { fingerprint, reason, time: Date.now() };

    const candidateVsPreviousDiff = top ? this.getDesignDiffSummary(top.template, snapshot) : undefined;
    this.auditHistory({
      type: 'history-push',
      actionId: this.nextHistoryActionId(reason),
      reason,
      source: `pre-mutation:${source}`,
      candidateFingerprint: this.shortFp(fingerprint),
      undoLength: this.undoStack.length,
      redoLength: 0,
      pushed: true,
      extra: {
        redoCleared: true,
        diffMeaning: 'pre-mutation-checkpoint-no-action-diff',
        expectedMutation: {
          blockId: this.selectedBlockId,
          reason,
          source,
        },
        candidateVsPreviousCheckpointDiff: candidateVsPreviousDiff,
      },
    });

    return true;
  }

  private refreshLastHistoryFingerprint(): void {
    if (!this.workingTemplate || this.isRestoringHistory || this.isInitializingBuilderHistory) {
      return;
    }
    this.lastHistoryFingerprint = this.getHistoryDesignFingerprint(this.workingTemplate);
  }

  /* ------------------------------------------- */

  private createHistoryEntry(): HistoryEntry {
    return {
      template: this.createCanonicalHistoryTemplate(this.workingTemplate || this.getSystemTemplate('scanafy-classic')?.qrPosterTemplate || QR_POSTER_SYSTEM_TEMPLATES_FALLBACK[0].qrPosterTemplate),
      selectedBlockIds: [...this.selectedBlockIds],
    };
  }

  private recordHistoryCheckpoint(reason = 'missing-reason'): void {
    if (!this.workingTemplate || this.isRestoringHistory || this.isInitializingBuilderHistory || Date.now() < this.suppressHistoryUntil) {
      return;
    }
    const fingerprint = this.getHistoryDesignFingerprint(this.workingTemplate);
    const top = this.undoStack[this.undoStack.length - 1];
    const topFp = top ? this.getHistoryDesignFingerprint(top.template) : '';
    const skippedDuplicate = fingerprint === this.lastHistoryFingerprint;
    const skippedTopMatch = !skippedDuplicate && fingerprint === topFp;

    this.auditHistory({
      type: 'history-attempt',
      actionId: this.nextHistoryActionId(reason),
      reason,
      source: reason,
      candidateFingerprint: this.shortFp(fingerprint),
      lastHistoryFingerprint: this.shortFp(this.lastHistoryFingerprint),
      topUndoFingerprint: this.shortFp(topFp),
      undoLength: this.undoStack.length,
      redoLength: this.redoStack.length,
      skipped: skippedDuplicate || skippedTopMatch,
      skipReason: skippedDuplicate ? 'matches-lastHistoryFingerprint' : skippedTopMatch ? 'matches-top-undo-stack' : undefined,
      extra: { isInitializingBuilderHistory: this.isInitializingBuilderHistory, isRestoringHistory: this.isRestoringHistory },
    });

    if (skippedDuplicate || skippedTopMatch) {
      return;
    }
    const nextEntry = this.createHistoryEntry();
    const beforeTemplate = this.undoStack.length > 0 ? this.undoStack[this.undoStack.length - 1].template : null;
    this.undoStack.push(nextEntry);
    if (this.undoStack.length > this.historyLimit) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    this.lastHistoryFingerprint = fingerprint;

    this.auditHistory({
      type: 'history-push',
      actionId: this.currentHistoryActionId,
      reason,
      source: reason,
      candidateFingerprint: this.shortFp(fingerprint),
      undoLength: this.undoStack.length,
      redoLength: 0,
      pushed: true,
      diff: this.getDesignDiffSummary(beforeTemplate, nextEntry.template),
      extra: { redoCleared: true },
    });

    if (this.debugQrPosterHistory) {
      console.debug('[QR Builder][History Push]', {
        fingerprint: fingerprint.substring(0, 40),
        undoLength: this.undoStack.length,
        redoLength: 0,
      });
    }
  }

  private applyHistoryEntry(entry: HistoryEntry): void {
    this.applyWorkingTemplate(entry.template);
    const validIds = entry.selectedBlockIds.filter((id) => this.workingTemplate?.blocks.some((block) => block.id === id));
    this.setSelectedBlockIds(validIds.length ? validIds : (this.workingTemplate?.blocks[0] ? [this.workingTemplate.blocks[0].id] : []));
    this.guideLines = [];
    this.marqueeSelectionRect = null;
    this.dragState = null;
    this.resizeState = null;
    this.rotateState = null;

    if (this.workingTemplate) {
      const restoredFp = this.getHistoryDesignFingerprint(this.workingTemplate);
      const entryFp = this.getHistoryDesignFingerprint(entry.template);
      if (restoredFp !== entryFp) {
        const diff = this.getDesignDiffSummary(entry.template, this.workingTemplate);
        const imageDiag: Record<string, unknown> = {};
        const entryImages = entry.template.blocks.filter((b) => b.type === 'image');
        const restoredImages = this.workingTemplate.blocks.filter((b) => b.type === 'image');
        for (let i = 0; i < Math.max(entryImages.length, restoredImages.length); i++) {
          const e = entryImages[i];
          const r = restoredImages[i];
          if (e && r) {
            const ek = Object.keys(e.content || {});
            const rk = Object.keys(r.content || {});
            imageDiag[`image-${i}`] = {
              entryKeys: ek,
              restoredKeys: rk,
              onlyInEntry: ek.filter((k) => !rk.includes(k)),
              onlyInRestored: rk.filter((k) => !ek.includes(k)),
            };
          }
        }
        this.auditHistory({
          type: 'warning',
          reason: 'restored-fingerprint-drift',
          source: 'applyHistoryEntry',
          candidateFingerprint: this.shortFp(restoredFp),
          topUndoFingerprint: this.shortFp(entryFp),
          extra: { diff, imageContentDiag: imageDiag },
        });
      }
    }
  }

  private isSameHistoryEntry(a: HistoryEntry, b: HistoryEntry): boolean {
    return JSON.stringify(a.template) === JSON.stringify(b.template);
  }

  private getBoundsForBlocks(blocks: QrPosterBlock[]): { x: number; y: number; width: number; height: number } {
    if (!blocks.length) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    const left = Math.min(...blocks.map((block) => block.x));
    const top = Math.min(...blocks.map((block) => block.y));
    const right = Math.max(...blocks.map((block) => block.x + block.width));
    const bottom = Math.max(...blocks.map((block) => block.y + block.height));
    return { x: left, y: top, width: right - left, height: bottom - top };
  }

  private getBoundsForBlocksConsideringRotation(blocks: QrPosterBlock[]): { left: number; top: number; right: number; bottom: number; width: number; height: number } {
    if (!blocks.length) {
      return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
    }
    const rotatedBounds = blocks.map((block) => this.getRotatedBlockBounds(block));
    const left = Math.min(...rotatedBounds.map((entry) => entry.left));
    const top = Math.min(...rotatedBounds.map((entry) => entry.top));
    const right = Math.max(...rotatedBounds.map((entry) => entry.right));
    const bottom = Math.max(...rotatedBounds.map((entry) => entry.bottom));
    return { left, top, right, bottom, width: right - left, height: bottom - top };
  }

  private getRotatedBlockBounds(block: QrPosterBlock): { left: number; top: number; right: number; bottom: number; width: number; height: number } {
    const rotation = (this.normalizeRotation(block.rotation || 0) * Math.PI) / 180;
    if (!rotation) {
      return {
        left: block.x,
        top: block.y,
        right: block.x + block.width,
        bottom: block.y + block.height,
        width: block.width,
        height: block.height,
      };
    }
    const centerX = block.x + (block.width / 2);
    const centerY = block.y + (block.height / 2);
    const corners = [
      { x: block.x, y: block.y },
      { x: block.x + block.width, y: block.y },
      { x: block.x + block.width, y: block.y + block.height },
      { x: block.x, y: block.y + block.height },
    ].map((corner) => {
      const translatedX = corner.x - centerX;
      const translatedY = corner.y - centerY;
      return {
        x: centerX + (translatedX * Math.cos(rotation)) - (translatedY * Math.sin(rotation)),
        y: centerY + (translatedX * Math.sin(rotation)) + (translatedY * Math.cos(rotation)),
      };
    });
    const left = Math.min(...corners.map((corner) => corner.x));
    const top = Math.min(...corners.map((corner) => corner.y));
    const right = Math.max(...corners.map((corner) => corner.x));
    const bottom = Math.max(...corners.map((corner) => corner.y));
    return {
      left,
      top,
      right,
      bottom,
      width: right - left,
      height: bottom - top,
    };
  }

  private applyDragMovement(event: PointerEvent): void {
    if (!this.workingTemplate || !this.dragState) {
      return;
    }
    const deltaXmm = (event.clientX - this.dragState.startClientX) / this.canvasScalePxPerMm;
    const deltaYmm = (event.clientY - this.dragState.startClientY) / this.canvasScalePxPerMm;
    if (Math.abs(deltaXmm) > 0.1 || Math.abs(deltaYmm) > 0.1) {
      if (!this.dragState.hasMoved) {
        this.dragState.hasMoved = true;
        this.dragState.checkpointPushed = true;
      }
    }
    const rawBounds = {
      x: this.dragState.startBounds.x + deltaXmm,
      y: this.dragState.startBounds.y + deltaYmm,
      width: this.dragState.startBounds.width,
      height: this.dragState.startBounds.height,
    };
    const clampedDelta = this.clampRotatedGroupDelta(deltaXmm, deltaYmm, this.dragState.startRotatedBounds);
    const snapped = this.getSnappedBounds({
      x: this.dragState.startBounds.x + clampedDelta.deltaX,
      y: this.dragState.startBounds.y + clampedDelta.deltaY,
      width: rawBounds.width,
      height: rawBounds.height,
    }, this.dragState.blockIds);
    const snappedDelta = this.clampRotatedGroupDelta(snapped.bounds.x - this.dragState.startBounds.x, snapped.bounds.y - this.dragState.startBounds.y, this.dragState.startRotatedBounds);
    const groupDeltaX = snappedDelta.deltaX;
    const groupDeltaY = snappedDelta.deltaY;

    this.workingTemplate.blocks = this.workingTemplate.blocks.map((block) => {
      if (!this.dragState?.blockIds.includes(block.id)) {
        return block;
      }
      const start = this.dragState.startPositions[block.id];
      return this.clampBlock({
        ...block,
        x: start.x + groupDeltaX,
        y: start.y + groupDeltaY,
      });
    });
    this.guideLines = snapped.guides;
  }

  private clampRotatedGroupDelta(
    deltaX: number,
    deltaY: number,
    bounds: { left: number; top: number; right: number; bottom: number; width: number; height: number }
  ): { deltaX: number; deltaY: number } {
    return {
      deltaX: this.clampMm(deltaX, -bounds.left, this.pageWidthMm - bounds.right),
      deltaY: this.clampMm(deltaY, -bounds.top, this.pageHeightMm - bounds.bottom),
    };
  }

  private applyRotationMovement(event: PointerEvent): void {
    if (!this.workingTemplate || !this.rotateState) {
      return;
    }
    this.rotateState.checkpointPushed = true;
    const block = this.workingTemplate.blocks.find((item) => item.id === this.rotateState?.blockId);
    if (!block) {
      return;
    }
    const currentAngle = this.getPointerAngleDegrees(
      event.clientX,
      event.clientY,
      this.rotateState.centerClientX,
      this.rotateState.centerClientY,
    );
    const delta = currentAngle - this.rotateState.startPointerAngle;
    const snapped = event.shiftKey
      ? Math.round((this.rotateState.startRotation + delta) / 15) * 15
      : this.rotateState.startRotation + delta;
    block.rotation = this.normalizeRotation(snapped);
  }

  private clampGroupBounds(bounds: { x: number; y: number; width: number; height: number }): { x: number; y: number; width: number; height: number } {
    return {
      ...bounds,
      x: this.clampMm(bounds.x, 0, Math.max(0, this.pageWidthMm - bounds.width)),
      y: this.clampMm(bounds.y, 0, Math.max(0, this.pageHeightMm - bounds.height)),
    };
  }

  private getSnappedBounds(
    bounds: { x: number; y: number; width: number; height: number },
    movingBlockIds: string[],
  ): { bounds: { x: number; y: number }; guides: GuideLine[] } {
    const thresholdMm = 1.8;
    const verticalTargets = [0, this.pageWidthMm / 2, this.pageWidthMm];
    const horizontalTargets = [0, this.pageHeightMm / 2, this.pageHeightMm];
    const otherBlocks = (this.workingTemplate?.blocks || []).filter((block) => !movingBlockIds.includes(block.id));

    otherBlocks.forEach((block) => {
      verticalTargets.push(block.x, block.x + (block.width / 2), block.x + block.width);
      horizontalTargets.push(block.y, block.y + (block.height / 2), block.y + block.height);
    });

    const xSnap = this.findAxisSnap(bounds.x, bounds.width, verticalTargets, thresholdMm);
    const ySnap = this.findAxisSnap(bounds.y, bounds.height, horizontalTargets, thresholdMm);
    const guides: GuideLine[] = [];

    if (xSnap) {
      guides.push({ orientation: 'vertical', positionMm: xSnap.target });
    }
    if (ySnap) {
      guides.push({ orientation: 'horizontal', positionMm: ySnap.target });
    }

    return {
      bounds: {
        x: xSnap?.position ?? bounds.x,
        y: ySnap?.position ?? bounds.y,
      },
      guides,
    };
  }

  private findAxisSnap(
    start: number,
    size: number,
    targets: number[],
    thresholdMm: number,
  ): { position: number; target: number } | null {
    const edges = [
      { kind: 'start', value: start },
      { kind: 'center', value: start + (size / 2) },
      { kind: 'end', value: start + size },
    ];
    let bestMatch: { distance: number; position: number; target: number } | null = null;

    for (const edge of edges) {
      for (const target of targets) {
        const distance = target - edge.value;
        if (Math.abs(distance) > thresholdMm) {
          continue;
        }
        const snappedPosition = edge.kind === 'start'
          ? target
          : edge.kind === 'center'
            ? target - (size / 2)
            : target - size;
        if (!bestMatch || Math.abs(distance) < bestMatch.distance) {
          bestMatch = {
            distance: Math.abs(distance),
            position: snappedPosition,
            target,
          };
        }
      }
    }

    return bestMatch ? { position: bestMatch.position, target: bestMatch.target } : null;
  }

  private getPagePointFromClient(clientX: number, clientY: number): { x: number; y: number } | null {
    const rect = this.posterPageRef?.nativeElement.getBoundingClientRect();
    if (!rect) {
      return null;
    }
    return {
      x: this.clampMm(clientX - rect.left, 0, rect.width),
      y: this.clampMm(clientY - rect.top, 0, rect.height),
    };
  }

  private buildMarqueeRect(startX: number, startY: number, endX: number, endY: number): { leftPx: number; topPx: number; widthPx: number; heightPx: number } {
    return {
      leftPx: Math.min(startX, endX),
      topPx: Math.min(startY, endY),
      widthPx: Math.abs(endX - startX),
      heightPx: Math.abs(endY - startY),
    };
  }

  private finishMarqueeSelection(): void {
    if (!this.marqueeState || !this.marqueeSelectionRect || !this.workingTemplate) {
      this.marqueeState = null;
      this.marqueeSelectionRect = null;
      return;
    }
    const selectedIds = this.workingTemplate.blocks
      .filter((block) => this.doesMarqueeIntersectBlock(this.marqueeSelectionRect!, block))
      .map((block) => block.id);
    this.setSelectedBlockIds(selectedIds);
    this.marqueeState = null;
    this.marqueeSelectionRect = null;
    this.guideLines = [];
  }

  private doesMarqueeIntersectBlock(
    marquee: { leftPx: number; topPx: number; widthPx: number; heightPx: number },
    block: QrPosterBlock,
  ): boolean {
    const blockLeft = block.x * this.canvasScalePxPerMm;
    const blockTop = block.y * this.canvasScalePxPerMm;
    const blockRight = blockLeft + (block.width * this.canvasScalePxPerMm);
    const blockBottom = blockTop + (block.height * this.canvasScalePxPerMm);
    const marqueeRight = marquee.leftPx + marquee.widthPx;
    const marqueeBottom = marquee.topPx + marquee.heightPx;
    return blockLeft < marqueeRight
      && blockRight > marquee.leftPx
      && blockTop < marqueeBottom
      && blockBottom > marquee.topPx;
  }

  private getMinBlockSize(type: QrPosterBlock['type']): { width: number; height: number } {
    switch (type) {
      case 'qrCode':
        return { width: 25, height: 25 };
      case 'icon':
        return { width: 4, height: 4 };
      case 'companyLogo':
        return { width: 10, height: 8 };
      case 'image':
        return { width: 10, height: 4 };
      case 'line':
        return { width: 0.1, height: 0.1 };
      case 'rectangle':
        return { width: 0.1, height: 0.1 };
      case 'text':
      case 'variableText':
      case 'iconText':
      case 'iconMultilineText':
        return { width: 8, height: 4 };
      default:
        return { width: 8, height: 4 };
    }
  }

  private getResizeCursor(handle: ResizeHandle): string {
    switch (handle) {
      case 'n':
      case 's':
        return 'ns-resize';
      case 'e':
      case 'w':
        return 'ew-resize';
      case 'nw':
      case 'se':
        return 'nwse-resize';
      default:
        return 'nesw-resize';
    }
  }

  private getResizedBlock(block: QrPosterBlock, event: PointerEvent): QrPosterBlock {
    if (!this.resizeState) {
      return block;
    }
    const minSize = this.getMinBlockSize(block.type);
    const deltaX = (event.clientX - this.resizeState.startClientX) / this.canvasScalePxPerMm;
    const deltaY = (event.clientY - this.resizeState.startClientY) / this.canvasScalePxPerMm;
    const angleRad = (this.resizeState.startRotation * Math.PI) / 180;
    const cosR = Math.cos(angleRad);
    const sinR = Math.sin(angleRad);
    const localDx = deltaX * cosR + deltaY * sinR;
    const localDy = -deltaX * sinR + deltaY * cosR;
    let x = this.resizeState.startX;
    let y = this.resizeState.startY;
    let width = this.resizeState.startWidth;
    let height = this.resizeState.startHeight;

    switch (this.resizeState.handle) {
      case 'n':
        y = this.resizeState.startY + localDy;
        height = this.resizeState.startHeight - localDy;
        break;
      case 's':
        height = this.resizeState.startHeight + localDy;
        break;
      case 'w':
        x = this.resizeState.startX + localDx;
        width = this.resizeState.startWidth - localDx;
        break;
      case 'e':
        width = this.resizeState.startWidth + localDx;
        break;
      case 'nw':
        x = this.resizeState.startX + localDx;
        y = this.resizeState.startY + localDy;
        width = this.resizeState.startWidth - localDx;
        height = this.resizeState.startHeight - localDy;
        break;
      case 'ne':
        y = this.resizeState.startY + localDy;
        width = this.resizeState.startWidth + localDx;
        height = this.resizeState.startHeight - localDy;
        break;
      case 'sw':
        x = this.resizeState.startX + localDx;
        width = this.resizeState.startWidth - localDx;
        height = this.resizeState.startHeight + localDy;
        break;
      case 'se':
        width = this.resizeState.startWidth + localDx;
        height = this.resizeState.startHeight + localDy;
        break;
    }

    if (width < minSize.width) {
      if (this.resizeState.handle === 'nw' || this.resizeState.handle === 'sw' || this.resizeState.handle === 'w') {
        x -= minSize.width - width;
      }
      width = minSize.width;
    }
    if (height < minSize.height) {
      if (this.resizeState.handle === 'nw' || this.resizeState.handle === 'ne' || this.resizeState.handle === 'n') {
        y -= minSize.height - height;
      }
      height = minSize.height;
    }

    x = this.clampMm(x, 0, this.pageWidthMm - width);
    y = this.clampMm(y, 0, this.pageHeightMm - height);
    width = this.clampMm(width, minSize.width, this.pageWidthMm - x);
    height = this.clampMm(height, minSize.height, this.pageHeightMm - y);

    return this.clampBlock({
      ...block,
      x,
      y,
      width,
      height,
    });
  }

  private getOrderedBlocks(): QrPosterBlock[] {
    return [...(this.workingTemplate?.blocks || [])].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
  }

  private resequenceBlockLayers(blocks: QrPosterBlock[]): void {
    blocks.forEach((block, index) => {
      block.zIndex = Math.min((index + 1) * 5, 900);
    });
  }

  private getEditableBlockText(block: QrPosterBlock): string {
    return block.type === 'variableText'
      ? block.content?.variable || block.content?.text || ''
      : block.content?.text || block.content?.variable || '';
  }

  private writeEditableBlockText(block: QrPosterBlock, value: string): void {
    block.content = block.content || {};
    if (block.type === 'variableText') {
      block.content = { ...block.content, variable: value };
      return;
    }
    block.content = { ...block.content, text: value };
  }

  private getAccentColor(): string {
    return this.company?.themeColor || this.themeColor || '#2b2b2d';
  }

  private normalizeRotation(value: number): number {
    const normalized = Number.isFinite(value) ? value : 0;
    const wrapped = ((normalized % 360) + 360) % 360;
    return Number((wrapped > 180 ? wrapped - 360 : wrapped).toFixed(2));
  }

  private getBlockCenterClientCoordinates(block: QrPosterBlock): { x: number; y: number } | null {
    const rect = this.posterPageRef?.nativeElement.getBoundingClientRect();
    if (!rect) {
      return null;
    }
    return {
      x: rect.left + ((block.x + (block.width / 2)) * this.canvasScalePxPerMm),
      y: rect.top + ((block.y + (block.height / 2)) * this.canvasScalePxPerMm),
    };
  }

  private getPointerAngleDegrees(clientX: number, clientY: number, centerX: number, centerY: number): number {
    return Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);
  }

  private resolveSystemTemplateSource(id: string, templateFromApi: QrPosterTemplate | null | undefined): QrPosterTemplate {
    const localTemplate = QR_POSTER_SYSTEM_TEMPLATES_FALLBACK.find((entry) => entry.id === id)?.qrPosterTemplate || null;
    return this.cloneTemplate(templateFromApi || localTemplate || QR_POSTER_SYSTEM_TEMPLATES_FALLBACK[0].qrPosterTemplate);
  }

  private async updatePreviewQrDataUrl(): Promise<void> {
    this.previewQrDataUrl = await this.buildCustomQrDataUrl(this.previewQrValue || DOMAIN);
  }

  private async buildCustomQrDataUrl(value: string): Promise<string> {
    return QRCode.toDataURL(value || DOMAIN, { errorCorrectionLevel: 'H', margin: 0 });
  }

  private formatQrPosterFilenameTimestamp(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}_${hours}-${minutes}`;
  }

  private refreshAdvancedUiCache(): void {
    this.variableGroupsCache = this.variableResolver.getVariableGroups(this.appMode, this.previewContext);
    this.refreshVariableTooltipCache();
    this.refreshQrOverlapWarningState();
    this.hasUnsavedAdvancedChangesFlag = !!this.workingTemplate
      && this.buildTemplateSignature(this.workingTemplate) !== this.buildTemplateSignature(this.loadedTemplateBaseline);
    this.refreshLastHistoryFingerprint();
    const activeTemplateName = this.activeTemplateId
      ? this.userTemplates.find((template) => template.id === this.activeTemplateId)?.name
      : null;
    this.activeTemplateStatusLabelText = activeTemplateName
      ? this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.ACTIVE_TEMPLATE', {
        name: activeTemplateName,
      })
      : this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.ACTIVE_NONE');
    this.advancedStatusLabelText = this.hasUnsavedAdvancedChangesFlag
      ? this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.STATUS_UNSAVED')
      : this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.STATUS_SAVED');
  }

  private refreshVariableTooltipCache(): void {
    const nextTitles: Record<string, string> = {};
    for (const group of this.variableGroupsCache) {
      for (const variable of group.variables) {
        const resolved = this.variableResolver.resolveVariable(variable.token, this.previewContext).trim();
        nextTitles[variable.token] = resolved
          ? this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.VARIABLE_CURRENT_VALUE', { value: resolved })
          : this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.VARIABLE_NO_VALUE');
      }
    }
    this.variableTooltipTitles = nextTitles;
  }

  private buildTemplateSignature(template: QrPosterTemplate | null): string {
    if (!template) {
      return '';
    }
    return JSON.stringify(this.getNormalizedTemplateForSave(template));
  }

  private refreshQrOverlapWarningState(): void {
    if (!this.workingTemplate?.blocks?.length) {
      this.qrOverlapWarningBlockIds = [];
      return;
    }
    const warningIds = new Set<string>();
    const qrBlocks = this.workingTemplate.blocks.filter((block) => block.type === 'qrCode' && !block.hidden);
    const candidates = this.workingTemplate.blocks.filter((block) => block.type !== 'qrCode' && !block.hidden);

    for (const qrBlock of qrBlocks) {
      const qrBounds = this.getRotatedBlockBounds(qrBlock);
      for (const block of candidates) {
        if (this.shouldIgnoreQrOverlapCandidate(block, qrBlock, qrBounds)) {
          continue;
        }
        const blockBounds = this.getRotatedBlockBounds(block);
        if (this.doBoundsOverlap(qrBounds, blockBounds, 0.5)) {
          warningIds.add(qrBlock.id);
          warningIds.add(block.id);
        }
      }
    }

    this.qrOverlapWarningBlockIds = Array.from(warningIds);
  }

  private shouldIgnoreQrOverlapCandidate(
    block: QrPosterBlock,
    qrBlock: QrPosterBlock,
    qrBounds: { left: number; top: number; right: number; bottom: number; width: number; height: number }
  ): boolean {
    if (block.hidden || block.id === qrBlock.id) {
      return true;
    }
    if (block.type === 'footer') {
      return true;
    }
    const blockId = (block.id || '').toLowerCase();
    if (block.type === 'rectangle' && blockId.includes('qr-frame') && (block.zIndex || 0) <= (qrBlock.zIndex || 0)) {
      return true;
    }
    if (block.type === 'rectangle' && (block.zIndex || 0) <= (qrBlock.zIndex || 0)) {
      const bounds = this.getRotatedBlockBounds(block);
      if (bounds.left <= qrBounds.left && bounds.right >= qrBounds.right && bounds.top <= qrBounds.top && bounds.bottom >= qrBounds.bottom) {
        return true;
      }
    }
    return false;
  }

  private doBoundsOverlap(
    a: { left: number; top: number; right: number; bottom: number },
    b: { left: number; top: number; right: number; bottom: number },
    toleranceMm = 0,
  ): boolean {
    return a.left < (b.right - toleranceMm)
      && a.right > (b.left + toleranceMm)
      && a.top < (b.bottom - toleranceMm)
      && a.bottom > (b.top + toleranceMm);
  }

  private confirmQrOverlapIfNeeded(): Promise<boolean> {
    if (!this.hasQrOverlapWarning) {
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      this.modal.confirm({
        nzTitle: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.QR_OVERLAP_CONFIRM_TITLE'),
        nzContent: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.QR_OVERLAP_CONFIRM_MESSAGE'),
        nzOkText: this.translate.instant('QR_POSTER_TEMPLATE.BUILDER.QR_OVERLAP_CONFIRM_CONTINUE'),
        nzCancelText: this.translate.instant('COMMON.CANCEL'),
        nzOnOk: () => resolve(true),
        nzOnCancel: () => resolve(false),
      });
    });
  }
}
